import axios from 'axios';
import { solvePow } from './pow';
import type { BookingBranding } from '../components/Booking/bookingBranding';

// Dynamically determine API URL based on current hostname
// This allows the frontend to work when accessed from other devices on the network
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    
    // In production, we use the Nginx proxy at /api
    if (typeof window !== 'undefined') {
        if (window.location.hostname === 'aime.clearbyte.au') {
            return '/api';
        }
        return `${window.location.protocol}//${window.location.hostname}:8001`;
    }
    return 'http://localhost:8001';
};

export const API_BASE_URL = getApiBaseUrl();

export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 120000, // 120 second timeout (2 minutes) - allows for batch operations like quick wins generation (~5s per article × 20 articles)
});

// ==================== Anti-Scraping: Proof-of-Work Challenge ====================
// The backend may respond to any request with HTTP 429 and a body of
// { detail, pow: { token, difficulty, algorithm } }. When that happens we
// solve the challenge client-side (see pow.ts), verify the solution via
// POST /security/pow/verify, then retry the original request exactly once.
// A 429 WITHOUT a pow field is a plain rate limit and passes through untouched.
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error?.config;
        const pow = error?.response?.status === 429 ? error?.response?.data?.pow : undefined;

        // Not a PoW challenge, no config to retry, or this request was already
        // retried once (never loop on repeated challenges) → pass through.
        if (!pow || typeof pow.token !== 'string' || typeof pow.difficulty !== 'number' ||
            !originalRequest || originalRequest._powRetried) {
            return Promise.reject(error);
        }

        originalRequest._powRetried = true;

        let nonce: string;
        try {
            nonce = await solvePow(pow.token, pow.difficulty);
        } catch {
            // Unsolvable within the time cap — surface the original 429
            return Promise.reject(error);
        }

        try {
            // Raw axios call (NOT apiClient) so verification never recurses
            // through this interceptor; same baseURL + Authorization as the
            // original request.
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            const auth = originalRequest.headers?.Authorization;
            if (auth) {
                headers.Authorization = String(auth);
            }
            const verifyResponse = await axios.post(
                '/security/pow/verify',
                { token: pow.token, nonce },
                { baseURL: originalRequest.baseURL || API_BASE_URL, headers, timeout: 30000 }
            );
            if (verifyResponse.data?.ok !== true) {
                return Promise.reject(error);
            }
        } catch {
            return Promise.reject(error);
        }

        try {
            // Retry the original request once through apiClient (re-runs the
            // auth request interceptor so the token header is fresh).
            return await apiClient(originalRequest);
        } catch (retryError: any) {
            if (retryError?.response?.status === 429 && retryError?.response?.data?.pow) {
                // Retry got challenged again — do NOT loop; reject with the original error
                return Promise.reject(error);
            }
            return Promise.reject(retryError);
        }
    }
);

// --- Data Types ---
export interface Project {
    project_id: string;
    name: string;
    slug: string | null;
    description: string | null;
    client_info: any | null;
    masterplan_document: string | null;
    is_archived: boolean;
    created_at: string;
    updated_at: string;
    ga_property_id?: string | null;
    ga_connected_email?: string | null;
    ga_connected_at?: string | null;
}

export interface Task {
    task_id: string;
    project_id: string;
    name: string;
    description: string | null;
    status: string;
    assigned_agent: string | null;
    input_data: any | null;
    output_data: any | null;
    synthesized_result: string | null;
    /** Read-only. Populated by GET /api/tasks/{id} only: synthesized_result with
     *  the outer generator wrapper removed. Absent (null) on list endpoints and
     *  on the PUT response. Never sent on PUT. */
    synthesized_result_clean?: string | null;
    calendar_row_statuses?: Record<string, string> | null;
    approval_status?: string | null;
    external_post_url?: string | null;
}

// --- API Functions ---

// == Projects ==
export const getProjects = async (includeArchived: boolean = true): Promise<Project[]> => {
    const response = await apiClient.get('/projects/', {
        params: { include_archived: includeArchived }
    });
    return response.data;
};

export const getProject = async (projectId: string): Promise<Project> => {
    const response = await apiClient.get(`/projects/${projectId}`);
    return response.data;
};

export const createProject = async (projectData: { name: string; slug?: string; description?: string; client_info?: any; masterplan_document?: string }) => {
    const response = await apiClient.post('/projects/', projectData);
    return response.data;
};

export const updateProject = async (projectId: string, projectData: { name?: string; slug?: string; description?: string; client_info?: Record<string, any> }): Promise<Project> => {
    const response = await apiClient.put(`/projects/${projectId}`, projectData);
    return response.data;
};

export const checkProjectSlug = async (slug: string, projectId?: string): Promise<{ available: boolean; slug: string }> => {
    const params = projectId ? `?project_id=${projectId}` : '';
    const response = await apiClient.get(`/projects/check-slug/${encodeURIComponent(slug)}${params}`);
    return response.data;
};

export const deleteProject = async (projectId: string, confirmName: string) => {
    const response = await apiClient.delete(`/projects/${projectId}`, {
        params: { confirm_name: confirmName }
    });
    return response.data;
};

export const archiveProject = async (projectId: string, archive: boolean) => {
    const response = await apiClient.patch(`/projects/${projectId}/archive?archive=${archive}`);
    return response.data;
};

// == Project Members ==
export interface ProjectMember {
    user_id: string;
    email: string;
    full_name: string;
    role: string;
    is_owner?: boolean;
}

export const getProjectMembers = async (projectId: string): Promise<ProjectMember[]> => {
    const response = await apiClient.get(`/projects/${projectId}/members`);
    return response.data;
};

export const addProjectMember = async (projectId: string, email: string): Promise<ProjectMember> => {
    const response = await apiClient.post(`/projects/${projectId}/members`, { email });
    return response.data;
};

export const removeProjectMember = async (projectId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}/members/${userId}`);
};

// == Tasks ==
export const getTasksForProject = async (projectId: string): Promise<Task[]> => {
    const response = await apiClient.get(`/projects/${projectId}/tasks/`);
    return response.data;
};

export const getTask = async (taskId: string): Promise<Task> => {
    const response = await apiClient.get(`/tasks/${taskId}`);
    return response.data;
};

export const updateTask = async (taskId: string, updates: Partial<Task>): Promise<Task> => {
    const response = await apiClient.put(`/tasks/${taskId}`, updates);
    return response.data;
};

export const deleteTask = async (taskId: string): Promise<void> => {
    await apiClient.delete(`/tasks/${taskId}`);
};

// == Manager ==
export const processPrompt = async (projectId: string, prompt: string, groundingMode: string = 'off'): Promise<Task> => {
    const response = await apiClient.post(`/projects/${projectId}/process_prompt/`, { prompt, grounding_mode: groundingMode });
    return response.data;
};

// --- Chat API ---
export interface Message {
    message_id: string;
    conversation_id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    created_at: string;
    metadata?: any;
}

export interface Conversation {
    conversation_id: string;
    project_id?: string;
    title?: string;
    created_at: string;
    updated_at: string;
    messages: Message[];
    metadata?: any;
}

export interface ChatRequest {
    message: string;
    conversation_id?: string;
    project_id?: string;
    attachments?: Array<{ filename: string; content: string }>;
    grounding_mode?: string;
}

export interface ChatResponse {
    message_id: string;
    conversation_id: string;
    content: string;
    tasks_created?: string[];
    metadata?: any;
}

export const sendChatMessage = async (request: ChatRequest): Promise<ChatResponse> => {
    const response = await apiClient.post('/chat/', request);
    return response.data;
};

export const getConversations = async (projectId?: string): Promise<Conversation[]> => {
    const url = projectId ? `/conversations/?project_id=${projectId}` : '/conversations/';
    const response = await apiClient.get(url);
    return response.data;
};

export const getConversation = async (conversationId: string): Promise<Conversation> => {
    const response = await apiClient.get(`/conversations/${conversationId}`);
    return response.data;
};

export const createConversation = async (projectId?: string, title?: string): Promise<Conversation> => {
    const response = await apiClient.post('/conversations/', { project_id: projectId, title });
    return response.data;
};

export const updateConversation = async (conversationId: string, title: string): Promise<Conversation> => {
    const response = await apiClient.put(`/conversations/${conversationId}`, null, {
        params: { title }
    });
    return response.data;
};

export const deleteConversation = async (conversationId: string): Promise<void> => {
    await apiClient.delete(`/conversations/${conversationId}`);
};

export const executeConversationTasks = async (conversationId: string): Promise<any> => {
    const response = await apiClient.post(`/chat/${conversationId}/execute_tasks/`);
    return response.data;
};

// --- Export API ---
export const getArticlesCount = async (projectId?: string, status: string = 'completed'): Promise<{ count: number }> => {
    if (projectId) {
        const response = await apiClient.get(`/projects/${projectId}/articles_count`, {
            params: { status }
        });
        return response.data;
    } else {
        const response = await apiClient.get('/articles_count', {
            params: { status }
        });
        return response.data;
    }
};

// --- Quick Wins API ---
export const createQuickWinSocialMedia = async (taskId: string) => {
    const response = await apiClient.post(`/tasks/${taskId}/quick_wins/social_media`, {}, {
        timeout: 120000 // 2 minutes for synthesis + generation
    });
    return response.data;
};

export const createQuickWinMetaData = async (taskId: string, targetKeyword?: string) => {
    const response = await apiClient.post(`/tasks/${taskId}/quick_wins/meta_data`, null, {
        params: targetKeyword ? { target_keyword: targetKeyword } : {},
        timeout: 120000 // 2 minutes for synthesis + generation
    });
    return response.data;
};

export const createQuickWinAdCopy = async (taskId: string, platform: string = 'all') => {
    const response = await apiClient.post(`/tasks/${taskId}/quick_wins/ad_copy`, null, {
        params: { platform },
        timeout: 120000 // 2 minutes for synthesis + generation
    });
    return response.data;
};

export const createQuickWinKeywordResearch = async (taskId: string) => {
    const response = await apiClient.post(`/tasks/${taskId}/quick_wins/keyword_research`, {}, {
        timeout: 120000 // 2 minutes for synthesis + generation
    });
    return response.data;
};

export const createQuickWinEmailNewsletter = async (projectId: string, taskIds: string[], theme?: string) => {
    const response = await apiClient.post(
        `/projects/${projectId}/quick_wins/email_newsletter`,
        { task_ids: taskIds, newsletter_theme: theme }
    );
    return response.data;
};

export const createBatchNewsletters = async (
    projectId: string,
    theme?: string,
    maxNewsletters?: number,
    articlesPerNewsletter?: number,
    excludeUsedInDays?: number
) => {
    const response = await apiClient.post(
        `/projects/${projectId}/quick_wins/email_newsletter/batch`,
        {
            newsletter_theme: theme,
            max_newsletters: maxNewsletters,
            articles_per_newsletter: articlesPerNewsletter || 4,
            exclude_used_in_days: excludeUsedInDays
        }
    );
    return response.data;
};

export const createQuickWinContentCalendar = async (
    projectId: string,
    taskIds: string[],
    frequency: string = 'weekly',
    startDate?: string,
    autoSelect?: boolean,
    maxArticles?: number,
    articlesFromDate?: string,
    excludePreviouslyUsed?: boolean
) => {
    const response = await apiClient.post(
        `/projects/${projectId}/quick_wins/content_calendar`,
        {
            task_ids: taskIds.length > 0 ? taskIds : null,
            start_date: startDate,
            frequency,
            auto_select: autoSelect,
            max_articles: maxArticles,
            articles_from_date: articlesFromDate,
            exclude_previously_used: excludePreviouslyUsed
        }
    );
    return response.data;
};

export const exportProjectArticles = async (
    projectId: string,
    status: string = 'completed',
    format: string = 'zip',
    outputFormat: string = 'markdown',
    includeMetadata: boolean = false
): Promise<Blob> => {
    const response = await apiClient.get(`/projects/${projectId}/export_articles`, {
        params: { status, format, output_format: outputFormat, include_metadata: includeMetadata },
        responseType: 'blob'
    });
    return response.data;
};

export const exportAllArticles = async (projectId?: string, status: string = 'completed', format: string = 'zip'): Promise<Blob> => {
    const params: any = { status, format };
    if (projectId) {
        params.project_id = projectId;
    }
    const response = await apiClient.get('/export_articles', {
        params,
        responseType: 'blob'
    });
    return response.data;
};

export interface MasterplanStreamsResponse {
    streams: string[];
    has_masterplan: boolean;
    total_streams: number;
}

export const getMasterplanStreams = async (projectId: string): Promise<MasterplanStreamsResponse> => {
    const response = await apiClient.get(`/projects/${projectId}/masterplan_streams/`);
    return response.data;
};

export interface MasterplanArticle {
    title: string;
    summary: string;
    prompt: string;
}

export const getMasterplanArticles = async (projectId: string, stream: string): Promise<MasterplanArticle[]> => {
    const response = await apiClient.get(`/landing-pages/${projectId}/masterplans/${stream}/articles`);
    return response.data;
};

// == Masterplan ==
export interface Masterplan {
    masterplan_id: string;
    project_id: string;
    industry: string | null;
    business_type: string | null;
    target_audience: string[] | null;
    primary_location: string | null;
    jurisdiction: string | null;
    country: string | null;
    num_streams: number | null;
    streams: any[] | null;
    competitor_sources: string[] | null;
    existing_research: string | null;
    masterplan_document: string | null;
    generation_status: string;
    created_at: string;
    updated_at: string;
}

export interface MasterplanCreateData {
    project_id: string;
    industry: string;
    business_type: string;
    primary_location: string;
    jurisdiction: string;
    country: string;
    num_streams: number;
    target_audience?: string[];
    competitor_sources?: string[];
    existing_research?: string;
}

export const createMasterplan = async (projectId: string, data: MasterplanCreateData): Promise<Masterplan> => {
    const response = await apiClient.post(`/projects/${projectId}/masterplan/create`, data);
    return response.data;
};

export const getMasterplan = async (masterplanId: string): Promise<Masterplan> => {
    const response = await apiClient.get(`/masterplans/${masterplanId}`);
    return response.data;
};

export const getProjectMasterplans = async (projectId: string): Promise<Masterplan[]> => {
    const response = await apiClient.get(`/projects/${projectId}/masterplans`);
    return response.data;
};

export const updateMasterplan = async (masterplanId: string, data: Partial<MasterplanCreateData>): Promise<Masterplan> => {
    const response = await apiClient.put(`/masterplans/${masterplanId}`, data);
    return response.data;
};

export const deleteMasterplan = async (masterplanId: string): Promise<void> => {
    await apiClient.delete(`/masterplans/${masterplanId}`);
};

export interface StreamGenerationResult {
    masterplan_id: string;
    status: string;
    streams_generated: number;
    streams: any[];
}

export const generateMasterplanStreams = async (masterplanId: string, streamNames: string[]): Promise<StreamGenerationResult> => {
    const response = await apiClient.post(`/masterplans/${masterplanId}/generate_streams`, streamNames);
    return response.data;
};

export const exportQuickWins = async (
    projectId: string,
    quickWinType: string = 'all',
    status: string = 'completed',
    format: string = 'zip',
    outputFormat: string = 'markdown',
    includeMetadata: boolean = false
): Promise<Blob> => {
    const response = await apiClient.get(`/projects/${projectId}/export_quick_wins`, {
        params: { quick_win_type: quickWinType, status, format, output_format: outputFormat, include_metadata: includeMetadata },
        responseType: 'blob'
    });
    return response.data;
};

// == Image Selection ==
export interface ImageSelectionResponse {
    message: string;
    images: {
        hero: any;
        alternates: any[];
    };
    reselected: boolean;
}

export const selectImagesForTask = async (taskId: string, forceReselect: boolean = false, excludeIds: number[] = []): Promise<ImageSelectionResponse> => {
    const response = await apiClient.post(`/tasks/${taskId}/select_images`, null, {
        params: {
            force_reselect: forceReselect,
            ...(excludeIds.length > 0 ? { exclude_ids: excludeIds.join(',') } : {})
        },
        timeout: 900000  // 15 minutes - Gemini CLI analysis can take time (15s load + processing per image)
    });
    return response.data;
};

export interface HeroImageResponse {
    message: string;
    hero: any;
    cascade_applied: boolean;
    upward_applied: boolean;
    child_social_post_count: number;
}

export const updateHeroImage = async (
    taskId: string,
    url: string,
    photographer?: string,
    source?: string,
    opts?: { cascade?: boolean },
): Promise<HeroImageResponse> => {
    const response = await apiClient.put(`/tasks/${taskId}/hero-image`, {
        url,
        photographer: photographer || '',
        source: source || 'custom',
        cascade: opts?.cascade ?? false,
    });
    return response.data;
};

export const getChildSocialPostCount = async (taskId: string): Promise<number> => {
    const response = await apiClient.get(`/tasks/${taskId}/child-social-post-count`);
    return response.data.child_social_post_count ?? 0;
};

export const selectImagesForMultipleTasks = async (taskIds: string[]): Promise<{ results: any[], errors: any[] }> => {
    const results = [];
    const errors = [];

    for (const taskId of taskIds) {
        try {
            const result = await selectImagesForTask(taskId, false);
            results.push({ taskId, ...result });
        } catch (error: any) {
            errors.push({ taskId, error: error.message || 'Failed to select images' });
        }
    }

    return { results, errors };
};

// == Content Catalogue ==
type QuickWinItem = { task_id: string; title: string; source_article?: string; source_task_id?: string; approval_status?: string | null; synthesized_result?: string | null };

export interface ContentTreeResponse {
    overview: {
        articles: number;
        social_media: number;
        meta_data: number;
        ad_copy: number;
        newsletters: number;
        calendars: number;
        keyword_research: number;
        multichannel_carousel: number;
        multichannel_video_script: number;
        multichannel_pull_quotes: number;
        edm_templates: number;
        edm_sequences: number;
    };
    streams: Array<{
        name: string;
        article_count: number;
        articles: Array<{
            task_id: string;
            title: string;
            status: string;
            has_image: boolean;
            quick_wins: string[];
            approval_status?: string | null;
        }>;
    }>;
    quick_wins: {
        social_media: QuickWinItem[];
        meta_data: QuickWinItem[];
        ad_copy: QuickWinItem[];
        email_newsletter: QuickWinItem[];
        content_calendar: QuickWinItem[];
        keyword_research: QuickWinItem[];
        multichannel_carousel: QuickWinItem[];
        multichannel_video_script: QuickWinItem[];
        multichannel_pull_quotes: QuickWinItem[];
    };
    edm?: {
        templates: Array<{ template_id: string; name: string; category: string; status: string; usage_count: number }>;
        sequences: Array<{ sequence_id: string; name: string; status: string; step_count: number }>;
    };
}

export const getContentTreeForProject = async (projectId: string): Promise<ContentTreeResponse> => {
    const response = await apiClient.get(`/projects/${projectId}/content_tree`);
    return response.data;
};

export const reassignTaskStream = async (taskId: string, stream: string): Promise<{ task_id: string; stream: string }> => {
    const response = await apiClient.patch(`/tasks/${taskId}/stream`, { stream });
    return response.data;
};

export const createStream = async (projectId: string, name: string): Promise<{ name: string; custom_streams: string[] }> => {
    const response = await apiClient.post(`/projects/${projectId}/streams`, { name });
    return response.data;
};

export interface StreamRenameResult {
    old_name: string;
    new_name: string;
    display_name: string;
    tasks_updated: number;
    masterplan_document_updated: boolean;
    masterplan_rows_updated: number;
    roadmap_heading_updated: boolean;
    streams: string[];
    untracked_mentions: number;
    warnings: string[];
    would_collide?: boolean;
}

export const previewStreamRename = async (projectId: string, oldName: string, newName: string): Promise<StreamRenameResult> => {
    const response = await apiClient.post(`/projects/${projectId}/streams/rename/preview`, { old_name: oldName, new_name: newName });
    return response.data;
};

export const renameStream = async (projectId: string, oldName: string, newName: string): Promise<StreamRenameResult> => {
    const response = await apiClient.patch(`/projects/${projectId}/streams/rename`, { old_name: oldName, new_name: newName });
    return response.data;
};

export const updateTaskApproval = async (taskId: string, approvalStatus: string | null): Promise<{ task_id: string; approval_status: string | null }> => {
    const response = await apiClient.patch(`/tasks/${taskId}/approval`, { approval_status: approvalStatus });
    return response.data;
};

// == Landing Pages ==
export interface LandingPage {
    landing_page_id: string;
    project_id: string;
    title: string;
    slug: string;
    description?: string;
    html_content?: string;
    widgets_config?: any;
    theme_config?: any;
    meta_title?: string;
    meta_description?: string;
    meta_keywords?: string[];
    og_title?: string;
    og_description?: string;
    og_image?: string;
    canonical_url?: string;
    analytics_code?: string;
    cookie_consent_enabled?: boolean;
    noindex?: boolean;
    generation_prompt?: string;
    llm_model?: string;
    status: string;
    campaign_id?: string;  // campaign-sequence-enrollment: linked campaign, drives automatic form_submit enrollment
    published_at?: string;
    created_at: string;
    updated_at: string;
    // H5.3 deploy fields
    deployed_url?: string;
    deploy_status?: string;
    cloudflare_project_slug?: string;
}

export interface LandingPageGenerate {
    project_id: string;
    stream?: string;
    title: string;
    slug: string;
    campaign_goal?: string;
    value_proposition?: string;
    target_segment_override?: string;
    style?: string;
    sections?: string[];
    include_quiz?: boolean;
    quiz_purpose?: string;
    include_lead_form?: boolean;
    include_testimonials?: boolean;
    testimonials_content?: string;
    include_faq?: boolean;
    faq_questions?: string;
    services_content?: string;
    existing_quiz_id?: string;
    primary_color?: string;
    brand_guide_id?: string;
    source_website_url?: string;  // URL to analyze for styling reference
    llm_model?: string;
    template_type?: string;  // Landing page template slug
}

export interface QuizConfig {
    quiz_id: string;
    project_id: string;
    landing_page_id?: string;
    name: string;
    description?: string;
    quiz_type: string;
    questions: any[];
    scoring_logic?: any;
    result_messages?: any;
    show_progress?: boolean;
    allow_back?: boolean;
    require_all_answers?: boolean;
    theme?: any;
    lead_magnet_enabled?: boolean;
    lead_magnet_url?: string;
    lead_magnet_label?: string;
    created_at: string;
    updated_at: string;
}

export interface LeadSubmission {
    submission_id: string;
    project_id: string;
    landing_page_id?: string;
    quiz_id?: string;
    name?: string;
    email: string;
    phone?: string;
    company?: string;
    custom_fields?: any;
    quiz_responses?: any[];
    quiz_score?: number;
    quiz_result_level?: string;
    source_widget?: string;
    source_url?: string;
    user_agent?: string;
    ip_address?: string;
    status: string;
    notes?: string;
    created_at: string;
    updated_at: string;
}

export const getLandingPages = async (projectId?: string, status?: string, includeArchived?: boolean): Promise<LandingPage[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (status) params.status = status;
    if (includeArchived) params.include_archived = true;

    const response = await apiClient.get('/landing-pages/', { params });
    return response.data;
};

export const getLandingPage = async (landingPageId: string): Promise<LandingPage> => {
    const response = await apiClient.get(`/landing-pages/${landingPageId}`);
    return response.data;
};

export const getLandingPageComponents = async (landingPageId: string): Promise<any[]> => {
    const response = await apiClient.get(`/landing-pages/${landingPageId}/components`);
    return response.data?.components || [];
};

// Landing page templates
export interface LandingPageTemplate {
    slug: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    component_types: string[];
    locked_layout: boolean;
    max_components: number | null;
}

export interface LandingPageTemplateCategory {
    key: string;
    label: string;
    description: string;
}

export const getLandingPageTemplates = async (): Promise<{
    templates: LandingPageTemplate[];
    categories: LandingPageTemplateCategory[];
}> => {
    const response = await apiClient.get('/landing-pages/templates');
    return response.data;
};

export const generateLandingPage = async (data: LandingPageGenerate): Promise<LandingPage> => {
    const response = await apiClient.post('/landing-pages/generate', data, {
        timeout: 300000 // 5 minutes for LLM generation
    });
    return response.data;
};

export const updateLandingPage = async (landingPageId: string, data: Partial<LandingPage>): Promise<LandingPage> => {
    const response = await apiClient.put(`/landing-pages/${landingPageId}`, data);
    return response.data;
};

// landing_pages-gated equivalents of getCampaigns()/getNurtureWorkflows() —
// see landing_page_routes.py's dual-gate note (code-review iteration 1
// IMPR-6). Used by LandingPageCampaignModal so it never has to call the
// campaigns- or lead_capture-gated endpoints from a landing_pages-gated
// surface.
export const getLandingPageCampaigns = async (projectId: string): Promise<Campaign[]> => {
    const response = await apiClient.get('/landing-pages/campaigns', { params: { project_id: projectId } });
    return response.data;
};

export const getLandingPageCampaignWorkflowCount = async (campaignId: string, projectId: string): Promise<number> => {
    const response = await apiClient.get(`/landing-pages/campaigns/${campaignId}/workflow-count`, { params: { project_id: projectId } });
    return response.data.count;
};

export const publishLandingPage = async (landingPageId: string): Promise<any> => {
    const response = await apiClient.post(`/landing-pages/${landingPageId}/publish`, {});
    return response.data;
};

export const deleteLandingPage = async (landingPageId: string): Promise<void> => {
    await apiClient.delete(`/landing-pages/${landingPageId}`);
};

export const archiveLandingPage = async (landingPageId: string): Promise<LandingPage> => {
    const response = await apiClient.patch(`/landing-pages/${landingPageId}/archive`);
    return response.data;
};

export const unarchiveLandingPage = async (landingPageId: string): Promise<LandingPage> => {
    const response = await apiClient.patch(`/landing-pages/${landingPageId}/unarchive`);
    return response.data;
};

export const duplicateLandingPage = async (landingPageId: string): Promise<LandingPage> => {
    const response = await apiClient.post(`/landing-pages/${landingPageId}/duplicate`);
    return response.data;
};

export const regenerateLandingPageSection = async (
    landingPageId: string,
    body: { component_id: string; component_type: string; instruction?: string; current_props?: any }
): Promise<{ component_id: string; type: string; props: Record<string, any> }> => {
    const response = await apiClient.post(`/landing-pages/${landingPageId}/regenerate-section`, body);
    return response.data;
};

// --- SuiteCRM Integration ---
export interface SuiteCRMStatus {
    connected: boolean;
    username?: string;
    url?: string;
    connected_at?: string;
    message?: string;
}

export const getSuiteCRMStatus = async (): Promise<SuiteCRMStatus> => {
    const response = await apiClient.get('/users/me/suitecrm/status');
    return response.data;
};

export const connectSuiteCRM = async (credentials: { url: string; username: string; password: string }): Promise<SuiteCRMStatus> => {
    const response = await apiClient.post('/users/me/suitecrm/connect', credentials);
    return response.data;
};

export const disconnectSuiteCRM = async (): Promise<any> => {
    const response = await apiClient.delete('/users/me/suitecrm/disconnect');
    return response.data;
};

export const testSuiteCRMConnection = async (): Promise<SuiteCRMStatus> => {
    const response = await apiClient.get('/users/me/suitecrm/test');
    return response.data;
};

// --- Per-User Outbound Email (SMTP) Config ---
export interface EmailConfig {
    configured: boolean;
    smtp_server?: string;
    smtp_port?: number;
    smtp_user?: string;
    smtp_security?: string;
    from_name?: string;
    from_address?: string;
    reply_to?: string;
    verified?: boolean;
    is_active?: boolean;
    crm_synced?: boolean;
    crm_synced_at?: string;
}

export const getEmailConfig = (): Promise<EmailConfig> =>
    apiClient.get('/users/me/suitecrm/email-config').then(r => r.data);

export const saveEmailConfig = (data: {
    smtp_server: string;
    smtp_port: number;
    smtp_user: string;
    smtp_password: string;
    smtp_security: string;
    from_name?: string;
    from_address?: string;
    reply_to?: string;
}): Promise<any> =>
    apiClient.post('/users/me/suitecrm/email-config', data).then(r => r.data);

export const testEmailConfig = (): Promise<any> =>
    apiClient.post('/users/me/suitecrm/email-config/test').then(r => r.data);

export const deleteEmailConfig = (): Promise<any> =>
    apiClient.delete('/users/me/suitecrm/email-config').then(r => r.data);

// ── Enterprise Company Management ────────────────────────────────────

export interface Company {
    company_id: string;
    name: string;
    slug: string;
    domain?: string;
    plan_tier: string;
    is_active: boolean;
    admin_user_id?: string;
    member_count?: number;
    crm_status?: string;
    crm_instance?: CRMInstanceDetail;
    created_at: string;
    updated_at?: string;
}

export interface CRMInstanceDetail {
    instance_id: string;
    label: string;
    crm_url: string;
    status: string;
    ssl_verify: boolean;
    last_health_check?: string;
    health_message?: string;
    // Remote FormIntake provisioning companion (Phase 5). Secret is never returned — only a boolean.
    provisioning_endpoint_url?: string | null;
    provisioning_enabled?: boolean;
    provisioning_secret_set?: boolean;
    provisioning_installed_at?: string | null;
}

export const getCompanies = (): Promise<Company[]> =>
    apiClient.get('/companies').then(r => r.data);

export const createCompany = (data: { name: string; domain?: string; plan_tier?: string }): Promise<Company> =>
    apiClient.post('/companies', data).then(r => r.data);

export const getCompany = (id: string): Promise<Company> =>
    apiClient.get(`/companies/${id}`).then(r => r.data);

export const updateCompany = (id: string, data: Record<string, any>): Promise<any> =>
    apiClient.put(`/companies/${id}`, data).then(r => r.data);

export const getCompanyMembers = (id: string): Promise<any[]> =>
    apiClient.get(`/companies/${id}/members`).then(r => r.data);

export const addCompanyMember = (id: string, email: string, userType: string = 'company_member'): Promise<any> =>
    apiClient.post(`/companies/${id}/members`, { email, user_type: userType }).then(r => r.data);

export const removeCompanyMember = (id: string, userId: string): Promise<void> =>
    apiClient.delete(`/companies/${id}/members/${userId}`).then(r => r.data);

export const configureCRMInstance = (companyId: string, data: {
    crm_url: string; api_client_id?: string; api_client_secret?: string;
    admin_username?: string; admin_password?: string; ssl_verify?: boolean; label?: string;
    provisioning_endpoint_url?: string; provisioning_secret?: string; provisioning_enabled?: boolean;
}): Promise<any> =>
    apiClient.post(`/companies/${companyId}/crm-instance`, data).then(r => r.data);

export const updateCRMInstance = (companyId: string, data: Record<string, any>): Promise<any> =>
    apiClient.put(`/companies/${companyId}/crm-instance`, data).then(r => r.data);

export const testCRMInstance = (companyId: string): Promise<{ success: boolean; message: string }> =>
    apiClient.post(`/companies/${companyId}/crm-instance/test`).then(r => r.data);

export const getCRMInstanceHealth = (companyId: string): Promise<CRMInstanceDetail> =>
    apiClient.get(`/companies/${companyId}/crm-instance/health`).then(r => r.data);

export const deleteCRMInstance = (companyId: string): Promise<void> =>
    apiClient.delete(`/companies/${companyId}/crm-instance`).then(r => r.data);

export interface GenerateFieldContentRequest {
    field_type: 'value_proposition' | 'target_segment' | 'quiz_purpose' | 'testimonials' | 'faq_questions' | 'services';
    project_id: string;
    user_prompt?: string;
    context?: Record<string, any>;
}

export const generateFieldContent = async (data: GenerateFieldContentRequest): Promise<{ content: string }> => {
    const response = await apiClient.post('/landing-pages/generate-field', data, {
        timeout: 60000 // 1 minute for field generation
    });
    return response.data;
};

// Website Page Routes API (independent from Landing Pages)
export const generateWebsiteFieldContent = async (data: GenerateFieldContentRequest): Promise<{ content: string }> => {
    const response = await apiClient.post('/website-pages/generate-field', data, {
        timeout: 60000 // 1 minute for field generation
    });
    return response.data;
};

export interface GenerateValuePropositionRequest {
    project_id: string;
    user_prompt?: string;
    context: {
        article_content: string;
        stream?: string;
    };
}

export const generateValueProposition = async (data: GenerateValuePropositionRequest): Promise<{ content: string }> => {
    const response = await apiClient.post('/landing-pages/generate-value-proposition', data, {
        timeout: 60000 // 1 minute for generation
    });
    return response.data;
};

export const generateWebsiteValueProposition = async (data: GenerateValuePropositionRequest): Promise<{ content: string }> => {
    const response = await apiClient.post('/website-pages/generate-value-proposition', data, {
        timeout: 60000 // 1 minute for generation
    });
    return response.data;
};

export interface QuizGenerateRequest {
    project_id: string;
    quiz_name: string;
    quiz_purpose: string;
    quiz_type?: string;
    context?: Record<string, any>;
}

export const getQuizConfigs = async (projectId?: string): Promise<QuizConfig[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;

    const response = await apiClient.get('/quizzes/', { params });
    return response.data;
};

export const getQuizConfig = async (quizId: string): Promise<QuizConfig> => {
    const response = await apiClient.get(`/quizzes/${quizId}`);
    return response.data;
};

export const createQuizConfig = async (data: Partial<QuizConfig>): Promise<QuizConfig> => {
    const response = await apiClient.post('/quizzes/', data);
    return response.data;
};

export const updateQuizConfig = async (quizId: string, data: Partial<QuizConfig>): Promise<QuizConfig> => {
    const response = await apiClient.put(`/quizzes/${quizId}`, data);
    return response.data;
};

export const deleteQuizConfig = async (quizId: string): Promise<void> => {
    await apiClient.delete(`/quizzes/${quizId}`);
};

export const generateQuizConfig = async (data: QuizGenerateRequest): Promise<QuizConfig> => {
    const response = await apiClient.post('/quizzes/generate', data, {
        timeout: 60000 // 1 minute for generation
    });
    return response.data;
};

// ==================== Script API ====================

export interface Script {
    script_id: string;
    project_id: string;
    task_id?: string;
    title: string;
    video_format: string;
    script_content?: string;
    status: string;
    approval_status: string;
    created_at: string;
    updated_at: string;
}

export const getScripts = async (projectId?: string): Promise<Script[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    const response = await apiClient.get('/scripts/', { params });
    return response.data;
};

export const generateScript = async (data: {
    project_id: string;
    task_id: string;
    video_format: string;
    num_characters?: number;
    character_names?: string[];
    additional_details?: string;
}): Promise<Script> => {
    const response = await apiClient.post('/scripts/generate', data, {
        timeout: 180000 // 3 minutes for generation
    });
    return response.data;
};

export const getScript = async (scriptId: string): Promise<Script> => {
    const response = await apiClient.get(`/scripts/${scriptId}`);
    return response.data;
};

export const updateScript = async (scriptId: string, scriptContent: string): Promise<Script> => {
    const response = await apiClient.patch(`/scripts/${scriptId}`, { script_content: scriptContent });
    return response.data;
};

export const updateScriptApproval = async (scriptId: string, approvalStatus: 'pending' | 'approved'): Promise<Script> => {
    const response = await apiClient.patch(`/scripts/${scriptId}/approval`, { approval_status: approvalStatus });
    return response.data;
};

export interface BatchCandidate {
    task_id: string;
    title: string;
    date: string;
    has_script: boolean;
}

export const getBatchCandidates = async (calendarTaskId: string, videoFormat: string): Promise<BatchCandidate[]> => {
    const response = await apiClient.get(`/scripts/batch-candidates/${calendarTaskId}`, {
        params: { video_format: videoFormat },
    });
    return response.data;
};

export const deleteScript = async (scriptId: string): Promise<void> => {
    await apiClient.delete(`/scripts/${scriptId}`);
};

export const exportScript = async (scriptId: string): Promise<Blob> => {
    const response = await apiClient.get(`/scripts/${scriptId}/export`, { responseType: 'blob' });
    return response.data;
};

export const exportScripts = async (scriptIds: string[]): Promise<Blob> => {
    const response = await apiClient.post(
        '/scripts/export/batch',
        { script_ids: scriptIds },
        { responseType: 'blob' }
    );
    return response.data;
};

export const getLeadSubmissions = async (
    projectId?: string,
    landingPageId?: string,
    status?: string
): Promise<LeadSubmission[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (landingPageId) params.landing_page_id = landingPageId;
    if (status) params.status = status;

    const response = await apiClient.get('/landing-pages/leads/', { params });
    return response.data;
};

export const updateLeadSubmission = async (
    submissionId: string,
    data: { status?: string; notes?: string }
): Promise<LeadSubmission> => {
    const response = await apiClient.put(`/landing-pages/leads/${submissionId}`, data);
    return response.data;
};

// ==================== Brand Guide API ====================

export interface CustomFont {
    name: string;
    role: 'primary' | 'secondary';
    filename: string;
    url: string;
    format: string;
}

export interface BrandGuide {
    brand_guide_id: string;
    project_id: string;
    brand_guide_name: string;
    is_default: boolean;
    brand_name?: string;
    tagline?: string;
    mission_statement?: string;
    brand_story?: string;
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
    neutral_colors?: string[];
    color_palette?: any;
    primary_font?: string;
    primary_font_url?: string;
    secondary_font?: string;
    secondary_font_url?: string;
    font_pairings?: any;
    logo_url?: string;
    logo_background_preference?: string; // Preference for logo background (light, dark, auto)
    logo_dark_bg_url?: string; // Logo for dark backgrounds
    logo_monochrome_black_url?: string; // Black monochrome version
    logo_monochrome_white_url?: string; // White monochrome version
    logo_mark_url?: string; // Icon/symbol-only logo variant (no wordmark)
    logo_mark_background_preference?: string; // Preference for logo mark background (light, dark, auto)
    favicon_url?: string; // Brand icon / favicon
    logo_guidelines?: string;
    image_style?: string;
    imagery_guidelines?: string;
    brand_voice?: string;
    tone_attributes?: string[];
    writing_guidelines?: string;
    messaging_pillars?: string[];
    design_style?: string;
    visual_principles?: string[];
    ui_preferences?: any;
    example_headlines?: string[];
    example_ctas?: string[];
    do_not_examples?: string[];
    mockup_settings?: {
        mobile?: any;
        tshirt?: any;
        mug?: any;
        pen?: any;
    };
    source_type?: string;
    source_url?: string;
    source_files?: string[];
    generation_prompt?: string;
    llm_model?: string;
    custom_fonts?: CustomFont[];
    status: string;
    created_at: string;
    updated_at: string;
}

export interface BrandGuideCreate {
    project_id: string;
    brand_guide_name: string;
    is_default?: boolean;
    brand_name?: string;
    tagline?: string;
    mission_statement?: string;
    brand_story?: string;
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
    neutral_colors?: string[];
    color_palette?: any;
    primary_font?: string;
    primary_font_url?: string;
    secondary_font?: string;
    secondary_font_url?: string;
    font_pairings?: any;
    logo_url?: string;
    logo_background_preference?: string; // Preference for logo background (light, dark, auto)
    logo_dark_bg_url?: string; // Logo for dark backgrounds
    logo_monochrome_black_url?: string; // Black monochrome version
    logo_monochrome_white_url?: string; // White monochrome version
    logo_mark_url?: string; // Icon/symbol-only logo variant (no wordmark)
    logo_mark_background_preference?: string; // Preference for logo mark background (light, dark, auto)
    favicon_url?: string; // Brand icon / favicon
    logo_guidelines?: string;
    image_style?: string;
    imagery_guidelines?: string;
    brand_voice?: string;
    tone_attributes?: string[];
    writing_guidelines?: string;
    messaging_pillars?: string[];
    design_style?: string;
    visual_principles?: string[];
    ui_preferences?: any;
    example_headlines?: string[];
    example_ctas?: string[];
    do_not_examples?: string[];
    source_type?: string;
    source_url?: string;
    source_files?: string[];
    generation_prompt?: string;
    llm_model?: string;
    status?: string;
}

export interface BrandGuideGenerate {
    project_id: string;
    brand_guide_name: string;
    source_type: 'website' | 'content_analysis' | 'manual_prompt';
    source_url?: string;
    custom_prompt?: string;
    llm_model?: string;
}

export const getBrandGuides = async (projectId?: string, status?: string): Promise<BrandGuide[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (status) params.status = status;

    const response = await apiClient.get('/brand-guides/', { params });
    return response.data;
};

export const getBrandGuide = async (brandGuideId: string): Promise<BrandGuide> => {
    const response = await apiClient.get(`/brand-guides/${brandGuideId}`);
    return response.data;
};

export const getBrandGuidesByProject = async (projectId: string): Promise<BrandGuide[]> => {
    const response = await apiClient.get(`/brand-guides/project/${projectId}`);
    return response.data;
};

export const getDefaultBrandGuide = async (projectId: string): Promise<BrandGuide> => {
    const response = await apiClient.get(`/brand-guides/project/${projectId}/default`);
    return response.data;
};

export const createBrandGuide = async (data: BrandGuideCreate): Promise<BrandGuide> => {
    const response = await apiClient.post('/brand-guides/', data);
    return response.data;
};

export const updateBrandGuide = async (brandGuideId: string, data: Partial<BrandGuideCreate>): Promise<BrandGuide> => {
    const response = await apiClient.put(`/brand-guides/${brandGuideId}`, data);
    return response.data;
};

export const generateBrandGuide = async (data: BrandGuideGenerate): Promise<BrandGuide> => {
    const response = await apiClient.post('/brand-guides/generate', data, {
        timeout: 180000 // 3 minutes for brand guide generation
    });
    return response.data;
};

export const uploadBrandGuidePDF = async (
    projectId: string,
    brandGuideName: string,
    file: File,
    llmModel: string = 'gemini'
): Promise<BrandGuide> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
        `/brand-guides/upload?project_id=${projectId}&brand_guide_name=${encodeURIComponent(brandGuideName)}&llm_model=${llmModel}`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 180000 // 3 minutes for PDF processing
        }
    );
    return response.data;
};

export const uploadBrandAsset = async (file: File): Promise<{ url: string; background_preference: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/brand-guides/upload-asset', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        }
    });
    return response.data;
};

export const setDefaultBrandGuide = async (brandGuideId: string): Promise<{ message: string; brand_guide_id: string; brand_guide_name: string; is_default: number }> => {
    const response = await apiClient.post(`/brand-guides/${brandGuideId}/set-default`);
    return response.data;
};

export const activateBrandGuide = async (brandGuideId: string): Promise<{ message: string; brand_guide_id: string; status: string }> => {
    const response = await apiClient.post(`/brand-guides/${brandGuideId}/activate`);
    return response.data;
};

export const deleteBrandGuide = async (brandGuideId: string): Promise<void> => {
    await apiClient.delete(`/brand-guides/${brandGuideId}`);
};

export const uploadBrandFont = async (
    brandGuideId: string,
    role: 'primary' | 'secondary',
    file: File
): Promise<{ url: string; filename: string; format: string; role: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('role', role);
    const response = await apiClient.post(`/brand-guides/${brandGuideId}/fonts`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const deleteBrandFont = async (brandGuideId: string, filename: string): Promise<void> => {
    await apiClient.delete(`/brand-guides/${brandGuideId}/fonts/${encodeURIComponent(filename)}`);
};

// Logo Studio: generate preview + confirm
export interface LogoGeneratePayload {
    brand_name?: string;
    design_style?: string;     // modern | classic | bold | playful
    complexity?: string;       // minimal | wordmark | combination
    shape?: string;            // geometric | organic
    layout?: string;           // icon_left | icon_above | icon_only | wordmark_only
    icon_hint?: string;
}

export interface LogoPreviewResponse {
    preview_id: string;
    variants: {
        primary: string;
        dark_bg: string;
        mono_black: string;
        mono_white: string;
        favicon?: string | null;
    };
    brand_name: string;
    primary_color: string;
}

export interface LogoConfirmResponse {
    committed: boolean;
    logo_url: string | null;
    logo_dark_bg_url: string | null;
    logo_monochrome_black_url: string | null;
    logo_monochrome_white_url: string | null;
    favicon_url: string | null;
}

export const generateBrandLogoPreview = async (
    brandGuideId: string,
    data: LogoGeneratePayload
): Promise<LogoPreviewResponse> => {
    const response = await apiClient.post(`/brand-guides/${brandGuideId}/generate-logo`, data);
    return response.data;
};

export const confirmBrandLogo = async (
    brandGuideId: string,
    previewId: string
): Promise<LogoConfirmResponse> => {
    const response = await apiClient.post(`/brand-guides/${brandGuideId}/confirm-logo`, { preview_id: previewId });
    return response.data;
};

export const previewWebsiteExtraction = async (url: string): Promise<any> => {
    const response = await apiClient.post(`/brand-guides/preview-extraction?url=${encodeURIComponent(url)}`);
    return response.data;
};

// Brand Guide Templates
export interface BrandGuideTemplate {
    template_id: string;
    template_name: string;
    template_description: string;
    category: string;
    preview_image?: string;
}

export interface BrandGuideTemplateDetail extends BrandGuideTemplate {
    brand_data: Partial<BrandGuideCreate>;
}

export const getBrandGuideTemplates = async (): Promise<{ templates: BrandGuideTemplate[] }> => {
    const response = await apiClient.get('/brand-guides/templates/');
    return response.data;
};

export const getBrandGuideTemplate = async (templateId: string): Promise<BrandGuideTemplateDetail> => {
    const response = await apiClient.get(`/brand-guides/templates/${templateId}`);
    return response.data;
};

// ==================== Authentication API ====================

export interface AuthTokens {
    access_token: string;
    refresh_token: string;
    token_type: string;
}

export interface LoginRequest {
    username: string;
    password: string;
}

export interface RegisterRequest {
    username: string;
    email: string;
    password: string;
    full_name?: string;
}

export const login = async (username: string, password: string): Promise<AuthTokens> => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await apiClient.post('/auth/login', formData, {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    });
    return response.data;
};

export const register = async (data: RegisterRequest): Promise<User> => {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
};

export const refreshAuthToken = async (refreshToken: string): Promise<AuthTokens> => {
    const response = await apiClient.post('/auth/refresh', null, {
        params: { refresh_token: refreshToken }
    });
    return response.data;
};

export const getAuthProfile = async (): Promise<User> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
};

export const exportMyData = async (): Promise<Blob> => {
    const response = await apiClient.get('/users/me/data-export', { responseType: 'blob' });
    return response.data as Blob;
};

export const deleteMyAccount = async (confirmation: string): Promise<{ deleted: Record<string, number> }> => {
    const response = await apiClient.delete('/users/me/data', { data: { confirmation } });
    return response.data;
};

export const updateAuthProfile = async (data: Partial<User>): Promise<User> => {
    const response = await apiClient.put('/auth/me', data);
    return response.data;
};

export const addToWaitlist = (email: string) => apiClient.post('/auth/waitlist', { email });

export const forgotPassword = (email: string) =>
    apiClient.post('/auth/forgot-password', { email }).then(r => r.data);

export const resetPassword = (token: string, new_password: string) =>
    apiClient.post('/auth/reset-password', { token, new_password }).then(r => r.data);

export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
    });
};

// ==================== User Admin API ====================

export interface User {
    user_id: string;
    username: string;
    email: string;
    full_name?: string;
    role: string;
    is_active: boolean;
    avatar_url?: string;
    bio?: string;
    timezone?: string;
    preferences?: Record<string, any>;
    email_notifications?: boolean;
    last_login_at?: string;
    created_at: string;
    updated_at: string;
}

export const getUsers = async (): Promise<User[]> => {
    const response = await apiClient.get('/users/');
    return response.data;
};

export const getCurrentUser = async (): Promise<User> => {
    const response = await apiClient.get('/users/me');
    return response.data;
};

export const createUser = async (data: any): Promise<User> => {
    const response = await apiClient.post('/users/', data);
    return response.data;
};

export const updateUser = async (userId: string, data: any): Promise<User> => {
    const response = await apiClient.put(`/users/${userId}`, data);
    return response.data;
};

export const deleteUser = async (userId: string): Promise<void> => {
    await apiClient.delete(`/users/${userId}`);
};

export const getAdminWaitlist = async (): Promise<any[]> => {
    const response = await apiClient.get('/admin/waitlist');
    return response.data;
};

export interface UserPermission {
    id: string;
    user_id: string;
    allowed_projects: string[] | null;
    allowed_modules: string[] | null;
    feature_flags: string[];
    created_at: string;
    updated_at: string;
    updated_by: string | null;
}

export const getUserPermissions = async (userId: string): Promise<UserPermission> => {
    const response = await apiClient.get(`/users/${userId}/permissions`);
    return response.data;
};

export const updateUserPermissions = async (
    userId: string,
    data: { allowed_projects: string[] | null; allowed_modules: string[] | null; feature_flags: string[] }
): Promise<UserPermission> => {
    const response = await apiClient.put(`/users/${userId}/permissions`, data);
    return response.data;
};

// ==================== System Settings API ====================

export interface SystemSetting {
    key: string;
    value: string;
    description?: string;
}

export const getSetting = async (key: string): Promise<SystemSetting> => {
    const response = await apiClient.get(`/settings/${key}`);
    return response.data;
};

export const getPublicSetting = async (key: string): Promise<{ key: string; value: string }> => {
    const response = await apiClient.get(`/settings/public/${key}`);
    return response.data;
};

export const updateSetting = async (key: string, value: string): Promise<SystemSetting> => {
    const response = await apiClient.put(`/settings/${key}`, { value });
    return response.data;
};

// ==================== Teams API ====================

export interface Team {
    team_id: string;
    name: string;
    description?: string;
    owner_id: string;
    created_at: string;
    updated_at: string;
    member_count?: number;
    project_count?: number;
}

export interface TeamMember {
    id: string;
    team_id: string;
    user_id: string;
    role: string;
    status: string;
    created_at: string;
    username?: string;
    email?: string;
    full_name?: string;
    avatar_url?: string;
}

export interface TeamProject {
    id: string;
    team_id: string;
    project_id: string;
    shared_by: string;
    permission_level: string;
    shared_at: string;
    project_name?: string;
    project_description?: string;
}

export interface TeamInvitation {
    invitation_id: string;
    team_id: string;
    email: string;
    role: string;
    status: string;
    expires_at: string;
    created_at: string;
    team_name?: string;
    invited_by_name?: string;
}

export const getTeams = async (): Promise<Team[]> => {
    const response = await apiClient.get('/teams/');
    return response.data;
};

export const createTeam = async (data: { name: string; description?: string }): Promise<Team> => {
    const response = await apiClient.post('/teams/', data);
    return response.data;
};

export const getTeam = async (teamId: string): Promise<Team> => {
    const response = await apiClient.get(`/teams/${teamId}`);
    return response.data;
};

export const updateTeam = async (teamId: string, data: { name?: string; description?: string }): Promise<Team> => {
    const response = await apiClient.put(`/teams/${teamId}`, data);
    return response.data;
};

export const deleteTeam = async (teamId: string): Promise<void> => {
    await apiClient.delete(`/teams/${teamId}`);
};

export const getTeamMembers = async (teamId: string): Promise<TeamMember[]> => {
    const response = await apiClient.get(`/teams/${teamId}/members`);
    return response.data;
};

export const addTeamMember = async (teamId: string, data: { user_id?: string; email?: string; role: string }): Promise<TeamMember> => {
    const response = await apiClient.post(`/teams/${teamId}/members`, data);
    return response.data;
};

export const updateTeamMember = async (teamId: string, userId: string, data: { role: string }): Promise<TeamMember> => {
    const response = await apiClient.put(`/teams/${teamId}/members/${userId}`, data);
    return response.data;
};

export const removeTeamMember = async (teamId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/teams/${teamId}/members/${userId}`);
};

export const getTeamProjects = async (teamId: string): Promise<TeamProject[]> => {
    const response = await apiClient.get(`/teams/${teamId}/projects`);
    return response.data;
};

export const shareProject = async (teamId: string, data: { project_id: string; permission_level: string }): Promise<TeamProject> => {
    const response = await apiClient.post(`/teams/${teamId}/projects`, data);
    return response.data;
};

export const unshareProject = async (teamId: string, projectId: string): Promise<void> => {
    await apiClient.delete(`/teams/${teamId}/projects/${projectId}`);
};

export const getPendingInvitations = async (): Promise<TeamInvitation[]> => {
    const response = await apiClient.get('/teams/invitations/pending');
    return response.data;
};

export const acceptInvitation = async (token: string): Promise<TeamMember> => {
    const response = await apiClient.post(`/teams/invitations/${token}/accept`);
    return response.data;
};

export const cancelInvitation = async (invitationId: string): Promise<void> => {
    await apiClient.delete(`/teams/invitations/${invitationId}`);
};

export const createBrandGuideFromTemplate = async (
    projectId: string,
    brandGuideName: string,
    templateId: string,
    customizations?: Partial<BrandGuideCreate>
): Promise<BrandGuide> => {
    const response = await apiClient.post(
        `/brand-guides/from-template?project_id=${projectId}&brand_guide_name=${encodeURIComponent(brandGuideName)}&template_id=${templateId}`,
        customizations || {}
    );
    return response.data;
};

// ==================== Website API ====================

export interface Website {
    website_id: string;
    project_id: string;
    website_name: string;
    brand_name: string;
    page_type: string;
    status: string;
    html_content?: string;
    meta_title?: string;
    meta_description?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    canonical_url?: string;
    analytics_code?: string;
    cookie_consent_enabled?: boolean;
    published_at?: string;
    created_at: string;
    updated_at?: string;
}

export interface WebsiteGenerate {
    project_id: string;
    website_name: string;
    brand_name: string;
    phone?: string;
    email?: string;
    address?: string;
    website_url?: string;
    page_type: 'home' | 'service' | 'contact' | 'longform';
    page_title?: string;
    hero_badge?: string;
    hero_title?: string;
    hero_subtitle?: string;
    services?: Array<{icon: string; title: string; description: string}>;
    features?: Array<{icon: string; title: string; description: string}>;
    testimonials?: Array<{quote: string; author: string; role: string}>;
    faqs?: Array<{question: string; answer: string}>;
    stats?: Array<{number: string; label: string}>;
    style?: string;
    primary_color?: string;
    llm_model?: string;
}

export const getWebsites = async (projectId?: string, status?: string): Promise<Website[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (status) params.status = status;
    
    const response = await apiClient.get('/websites/', { params });
    return response.data;
};

export const getWebsite = async (websiteId: string): Promise<Website> => {
    const response = await apiClient.get(`/websites/${websiteId}`);
    return response.data;
};

export const generateWebsite = async (data: WebsiteGenerate): Promise<Website> => {
    const response = await apiClient.post('/websites/generate', data, {
        timeout: 180000 // 3 minutes for website generation
    });
    return response.data;
};

export const deleteWebsite = async (websiteId: string): Promise<void> => {
    await apiClient.delete(`/websites/${websiteId}`);
};

export const publishWebsite = async (websiteId: string, settings?: {
    og_title?: string;
    og_description?: string;
    og_image?: string;
    canonical_url?: string;
    analytics_code?: string;
    cookie_consent_enabled?: boolean;
}): Promise<Website> => {
    const response = await apiClient.post(`/websites/${websiteId}/publish`, settings || {});
    return response.data;
};

export interface GenerateWebsiteContentRequest {
    project_id: string;
    field_type: 'company_overview' | 'service_description' | 'hero_content' | 'services_list' | 'features_list' | 'testimonials_list' | 'faq_questions_list';
    service_name?: string;
    context?: Record<string, any>;
}

// ==================== Site Builder Migration API ====================

export interface MigrateV2Result {
    site_project_id: string;
    already_migrated: boolean;
    page_count?: number;
    message: string;
}

export const migrateV2ToSiteBuilder = async (
    websiteName: string,
    projectId: string,
): Promise<MigrateV2Result> => {
    const encoded = encodeURIComponent(websiteName);
    const response = await apiClient.post(
        `/site-builder/migrate-from-v2/${encoded}?project_id=${encodeURIComponent(projectId)}`,
    );
    return response.data;
};

export const generateWebsiteContent = async (data: GenerateWebsiteContentRequest): Promise<{ content: string }> => {
    const response = await apiClient.post('/websites/generate-content', data, {
        timeout: 60000 // 1 minute for field generation
    });
    return response.data;
};

export interface ServicesFromStreamsResponse {
    services: string;
    stream_count: number;
    source: string;
}

export const generateServicesFromStreams = async (
    projectId: string,
    brandGuideId?: string
): Promise<ServicesFromStreamsResponse> => {
    const response = await apiClient.post('/websites/generate-services-from-streams', {
        project_id: projectId,
        brand_guide_id: brandGuideId || null
    }, {
        timeout: 120000 // 2 minutes for service synthesis
    });
    return response.data;
};

export interface FeaturesFromExpertiseResponse {
    features: string;
    feature_count: number;
    source: string;
}

export const generateFeaturesFromExpertise = async (
    projectId: string,
    brandGuideId?: string
): Promise<FeaturesFromExpertiseResponse> => {
    const response = await apiClient.post('/websites/generate-features-from-expertise', {
        project_id: projectId,
        brand_guide_id: brandGuideId || null
    }, {
        timeout: 120000 // 2 minutes for feature synthesis
    });
    return response.data;
};

export interface FAQsFromContentResponse {
    faqs: string;
    faq_count: number;
    source: string;
}

export const generateFAQsFromContent = async (
    projectId: string,
    brandGuideId?: string
): Promise<FAQsFromContentResponse> => {
    const response = await apiClient.post('/websites/generate-faqs-from-content', {
        project_id: projectId,
        brand_guide_id: brandGuideId || null
    }, {
        timeout: 120000 // 2 minutes for FAQ synthesis
    });
    return response.data;
};

// ==================== Template Library API ====================

export interface TemplateCategory {
    id: string;
    name: string;
    description: string;
    best_for: string[];
    key_features: string[];
    color_scheme: {
        primary: string;
        secondary: string;
        accent: string;
    };
}

export interface Template {
    id: string;
    name: string;
    category: string;
    type: 'homepage' | 'landing';
    description: string;
    preview_image: string;
    sections: string[];
}

export interface TemplateCategoriesResponse {
    categories: TemplateCategory[];
}

export interface TemplatesResponse {
    templates: Template[];
}

export const getTemplateCategories = async (): Promise<TemplateCategoriesResponse> => {
    const response = await apiClient.get('/website-pages/templates/categories');
    return response.data;
};

export const getTemplates = async (category?: string): Promise<TemplatesResponse> => {
    const url = category 
        ? `/website-pages/templates/?category=${encodeURIComponent(category)}`
        : '/website-pages/templates/';
    const response = await apiClient.get(url);
    return response.data;
};

export const getTemplate = async (templateId: string): Promise<Template> => {
    const response = await apiClient.get(`/website-pages/templates/${templateId}`);
    return response.data;
};

// ============================================================================
// SCHEDULING MODULE
// ============================================================================

// --- Types ---

export type PlatformType = 'wordpress' | 'linkedin' | 'twitter' | 'facebook' | 'instagram' | 'edm' | 'aime_site';
export type PublicationStatus = 'scheduled' | 'publishing' | 'published' | 'failed' | 'cancelled';
export type ContentType = 'article' | 'social_post' | 'edm';
export type CredentialStatus = 'active' | 'expired' | 'error';

export interface ProjectCredential {
    credential_id: string;
    project_id: string;
    platform: PlatformType;
    status: CredentialStatus;
    last_verified_at: string | null;
    error_message: string | null;
    created_at: string;
    updated_at: string;
    created_by: string;
}

export interface ScheduledPublication {
    publication_id: string;
    project_id: string;
    task_id: string | null;
    platform: PlatformType;
    content_type: ContentType;
    scheduled_for: string;
    status: PublicationStatus;
    content_data: Record<string, any>;
    published_at: string | null;
    external_post_id: string | null;
    external_post_url: string | null;
    error_message: string | null;
    retry_count: number;
    platform_metadata: Record<string, any> | null;
    created_at: string;
    updated_at: string;
    created_by: string;
    // Present on POST /publications responses only: advisory when many
    // publications land on the same day for one project (SEO pacing).
    pacing_warning?: string | null;
}

export interface PublicationLog {
    log_id: string;
    publication_id: string;
    status: PublicationStatus;
    message: string | null;
    metadata: Record<string, any> | null;
    created_at: string;
    created_by: string | null;
}

export interface CalendarEvent {
    id: string;
    title: string;
    start: string;
    backgroundColor: string;
    borderColor: string;
    extendedProps: {
        platform: PlatformType;
        status: PublicationStatus;
        content_type: ContentType;
        publication_id: string;
        external_post_url?: string;
    };
}

export interface CredentialTestResult {
    success: boolean;
    message: string;
    platform: PlatformType;
    metadata?: Record<string, any>;
}

export interface PublishNowResponse {
    success: boolean;
    message: string;
    publication_id: string;
    external_post_url?: string;
    external_post_id?: string;
    error?: string;
}

// --- API Functions ---

// Platform Credentials

export const createProjectCredential = async (
    projectId: string,
    platform: PlatformType,
    credentials: Record<string, any>
): Promise<ProjectCredential> => {
    const response = await apiClient.post(`/scheduling/projects/${projectId}/credentials`, {
        project_id: projectId,
        platform,
        credentials
    });
    return response.data;
};

export const listProjectCredentials = async (projectId: string): Promise<ProjectCredential[]> => {
    const response = await apiClient.get(`/scheduling/projects/${projectId}/credentials`);
    return response.data;
};

export const updateCredential = async (
    credentialId: string,
    credentials?: Record<string, any>,
    status?: CredentialStatus
): Promise<ProjectCredential> => {
    const response = await apiClient.put(`/scheduling/credentials/${credentialId}`, {
        credentials,
        status
    });
    return response.data;
};

export const deleteCredential = async (credentialId: string): Promise<void> => {
    await apiClient.delete(`/scheduling/credentials/${credentialId}`);
};

export const testCredential = async (credentialId: string): Promise<CredentialTestResult> => {
    const response = await apiClient.post(`/scheduling/credentials/${credentialId}/test`);
    return response.data;
};

// Publications

export const schedulePublication = async (data: {
    project_id: string;
    platform: PlatformType;
    platform_target_id?: string;
    content_type: ContentType;
    scheduled_for: string;
    content_data: Record<string, any>;
    task_id?: string;
}): Promise<ScheduledPublication> => {
    const response = await apiClient.post('/scheduling/publications', data);
    return response.data;
};

export interface SiteProjectSummary {
    site_project_id: string;
    project_id: string;
    name: string;
    description?: string;
    status: string;
    created_at?: string;
    updated_at?: string;
    page_count: number;
    // 'component' (default) | 'imported_static'. Imported sites can only be a blog publish
    // target once has_blog_index is true — see SchedulePublicationModal.
    source_type?: string;
    // Imported sites only: true once the site has a kind="blog_index" generated page. Always
    // false for component sites and for imported sites with no blog page yet — mirrors the
    // backend gate in _assert_imported_site_has_blog (site_builder_routes.py).
    has_blog_index?: boolean;
    // Imported sites only: valid stored favicon path (/assets/...), else null.
    favicon_url?: string | null;
}

export const listSiteProjects = (projectId: string): Promise<SiteProjectSummary[]> =>
    apiClient.get('/site-builder/sites', { params: { project_id: projectId } }).then(r => r.data);

export const listProjectPublications = async (
    projectId: string,
    filters?: {
        platform?: PlatformType;
        status?: PublicationStatus;
        start_date?: string;
        end_date?: string;
    }
): Promise<ScheduledPublication[]> => {
    const params = new URLSearchParams();
    if (filters?.platform) params.append('platform', filters.platform);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const url = `/scheduling/projects/${projectId}/publications${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
};

export const getPublication = async (publicationId: string): Promise<ScheduledPublication> => {
    const response = await apiClient.get(`/scheduling/publications/${publicationId}`);
    return response.data;
};

export const updatePublication = async (
    publicationId: string,
    data: {
        scheduled_for?: string;
        content_data?: Record<string, any>;
        status?: PublicationStatus;
    }
): Promise<ScheduledPublication> => {
    const response = await apiClient.put(`/scheduling/publications/${publicationId}`, data);
    return response.data;
};

export const publishNow = async (publicationId: string): Promise<PublishNowResponse> => {
    const response = await apiClient.post(`/scheduling/publications/${publicationId}/publish-now`);
    return response.data;
};

export const cancelPublication = async (publicationId: string): Promise<void> => {
    await apiClient.delete(`/scheduling/publications/${publicationId}`);
};

// Calendar & Logs

export const getCalendarEvents = async (
    projectId: string,
    startDate?: string,
    endDate?: string
): Promise<{ events: CalendarEvent[]; total_count: number }> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = `/scheduling/projects/${projectId}/calendar${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
};

export const getPublicationLogs = async (publicationId: string): Promise<PublicationLog[]> => {
    const response = await apiClient.get(`/scheduling/publications/${publicationId}/logs`);
    return response.data;
};

// Content Calendar Sync

export const syncContentCalendar = async (calendarTaskId: string): Promise<{
    success: boolean;
    created: number;
    skipped: number;
    errors: string[];
    publications: string[];
}> => {
    const response = await apiClient.post(`/scheduling/sync-content-calendar/${calendarTaskId}`);
    return response.data;
};

export const getContentCalendarSyncStatus = async (calendarTaskId: string): Promise<{
    total_articles: number;
    approved_articles: number;
    scheduled_publications: number;
    pending_articles: string[];
}> => {
    const response = await apiClient.get(`/scheduling/content-calendar/${calendarTaskId}/sync-status`);
    return response.data;
};

export interface ResyncPreviewItem {
    title: string;
    publication_id?: string;
    old_date?: string;
    new_date?: string;
    scheduled_for?: string;
    error?: string;
}

export interface ResyncPreview {
    updated: ResyncPreviewItem[];
    cancelled: ResyncPreviewItem[];
    added: ResyncPreviewItem[];
    unchanged: ResyncPreviewItem[];
}

export const previewCalendarResync = async (calendarTaskId: string): Promise<ResyncPreview> => {
    const response = await apiClient.get(`/scheduling/content-calendar/${calendarTaskId}/resync-preview`);
    return response.data;
};

export const applyCalendarResync = async (calendarTaskId: string): Promise<{
    success: boolean;
    updated: number;
    cancelled: number;
    added: number;
    unchanged: number;
    errors: string[];
}> => {
    const response = await apiClient.post(`/scheduling/content-calendar/${calendarTaskId}/resync`);
    return response.data;
};

export const refreshFromCalendar = async (projectId: string): Promise<{
    success: boolean;
    calendar_task_id: string | null;
    rebuilt: number;
    updated: number;
    cancelled: number;
    added: number;
    unchanged: number;
    errors: string[];
}> => {
    const response = await apiClient.post(`/scheduling/projects/${projectId}/refresh-from-calendar`);
    return response.data;
};


// ============================================================================
// H2.1 Campaign Orchestration
// ============================================================================

export interface CampaignAsset {
    asset_id: string;
    campaign_id: string;
    asset_type: string;
    asset_ref_id: string | null;
    title: string | null;
    content: string | null;
    status: string;
    platform: string | null;
    variant_group: string | null;
    variant_index: number | null;
    variant_label: string | null;
    image_url: string | null;
    image_concept: string | null;
    generation_status: string;       // pending|generating|ready|failed
    external_post_url: string | null;
    overlay_state?: OverlayState | null;
    created_at: string;
    updated_at: string;
}

export interface EmailSequenceStep {
    step_id: string;
    sequence_id: string;
    step_order: number;
    step_type: string;
    delay_days: number;
    send_time_of_day: string | null;  // "HH:MM" override for this step
    subject_line: string | null;
    preview_text: string | null;
    body_html: string | null;
    body_plain: string | null;
    condition_config: any | null;
    sent_count: number;
    opened_count: number;
    clicked_count: number;
    approval_status: 'approved' | 'pending';
    created_at: string;
    updated_at: string;
}

export interface EmailSequence {
    sequence_id: string;
    campaign_id: string | null;
    project_id: string;
    owner_id: string;
    name: string;
    description: string | null;
    trigger_type: string;
    trigger_config: any | null;
    status: string;
    crm_campaign_id: string | null;
    crm_synced_at: string | null;
    // Scheduling (Items 2 + 3)
    send_time_of_day: string | null;  // "HH:MM" in owner's timezone
    start_date: string | null;
    // Evergreen (Item 5)
    evergreen: boolean;
    evergreen_source: string | null;
    evergreen_source_id: string | null;
    created_at: string;
    updated_at: string;
    steps: EmailSequenceStep[];
}

export interface SendingRampPolicy {
    policy_id: string;
    user_id: string;
    initial_batch_size: number;
    batch_increment: number;
    batch_interval_minutes: number;
    max_batch_size: number;
    max_per_domain_per_minute: number;
    current_batch_level: number;
    current_limit: number;
    last_batch_at: string | null;
    cooldown_days: number;
}

export interface ProjectEmailConfig {
    id: string;
    project_id: string;
    owner_id: string;
    smtp_server: string;
    smtp_port: number;
    smtp_user: string;
    smtp_security: string;
    from_name: string | null;
    from_address: string | null;
    reply_to: string | null;
    verified: boolean;
    is_active: boolean;
}

export interface Campaign {
    campaign_id: string;
    project_id: string;
    crm_campaign_id?: string;
    crm_synced_at?: string;
    owner_id: string;
    name: string;
    description: string | null;
    product_service: string | null;
    campaign_goal: string;
    target_audience: string | null;
    offer?: string | null;
    audience_segments?: string[] | null;
    start_date: string | null;
    end_date: string | null;
    budget: number | null;
    status: string;
    generation_status: string;
    campaign_plan: string | null;
    auto_scale_enabled: boolean;
    auto_scale_config: any | null;
    created_at: string;
    updated_at: string;
    assets: CampaignAsset[];
    email_sequences: EmailSequence[];
}

export interface CampaignTemplate {
    template_id: string;
    name: string;
    description: string;
    goal: string;
    steps: Array<{
        step_order: number;
        delay_days: number;
        step_type: string;
        subject_line: string;
        preview_text: string;
    }>;
}

// -- Campaign CRUD --

export const getCampaigns = async (projectId?: string): Promise<Campaign[]> => {
    const params = projectId ? { project_id: projectId } : {};
    const response = await apiClient.get('/campaigns/', { params });
    return response.data;
};

export const getCampaign = async (campaignId: string): Promise<Campaign> => {
    const response = await apiClient.get(`/campaigns/${campaignId}`);
    return response.data;
};

export const createCampaign = async (data: {
    project_id: string;
    name: string;
    description?: string;
    product_service?: string;
    campaign_goal: string;
    target_audience?: string;
    offer?: string;
    audience_segments?: string[];
    start_date?: string;
    end_date?: string;
    budget?: number;
}): Promise<Campaign> => {
    const response = await apiClient.post('/campaigns/', data);
    return response.data;
};

export const updateCampaign = async (campaignId: string, data: Partial<Campaign>): Promise<Campaign> => {
    const response = await apiClient.patch(`/campaigns/${campaignId}`, data);
    return response.data;
};

export const deleteCampaign = async (campaignId: string): Promise<void> => {
    await apiClient.delete(`/campaigns/${campaignId}`);
};

export interface CampaignGenerationProgress {
    campaign_id: string;
    generation_status: string;  // queued | generating | completed | completed_with_gaps | failed | cancelled | draft
    progress: {
        phase: string;          // queued | first_pass | variants | completed | completed_with_gaps | failed | cancelled
        payload: {
            message?: string;
            step?: string;      // bundle | core_saved | social | ad_copy | lp_upgrade
            [k: string]: any;
        };
        ts: string;
        is_terminal: boolean;
    } | null;
}

export const getCampaignGenerationProgress = async (campaignId: string): Promise<CampaignGenerationProgress> => {
    const response = await apiClient.get(`/campaigns/${campaignId}/generation-progress`);
    return response.data;
};

export const generateCampaign = async (
    campaignId: string,
    assetTypes?: string[],
    brandGuideId?: string,
    variantCount?: number,
    lpTemplateType?: string,
    resume?: boolean,
    adAutoImages?: boolean,
    adImageType?: string,
): Promise<Campaign> => {
    // Build request body — OMIT undefined fields entirely (don't send default
    // placeholders). On resume the backend preserves prior request fields when
    // the caller didn't provide them; sending `variant_count: 1` as a default
    // would be treated as an explicit override and reset a prior 5-variant
    // selection to 1.
    const body: Record<string, any> = { campaign_id: campaignId, resume: !!resume };
    if (assetTypes !== undefined) body.asset_types = assetTypes;
    if (brandGuideId !== undefined) body.brand_guide_id = brandGuideId;
    if (variantCount !== undefined) body.variant_count = variantCount;
    if (lpTemplateType !== undefined) body.lp_template_type = lpTemplateType;
    // adAutoImages is a bool — sent whenever it is not undefined, INCLUDING
    // when it is false, since false is a meaningful explicit choice (the
    // backend's exclude_unset resume-preservation idiom depends on this key
    // being present in the request to distinguish "explicitly off" from
    // "not supplied, keep whatever the prior request had").
    if (adAutoImages !== undefined) body.ad_auto_images = adAutoImages;
    if (adImageType !== undefined) body.ad_image_type = adImageType;
    const response = await apiClient.post(
        `/campaigns/${campaignId}/generate`,
        body,
        // POST /generate now returns 202 quickly (work runs in aime-scheduler);
        // 60s ceiling is plenty.
        { timeout: 60000 },
    );
    return response.data;
};

// -- Campaign Assets --

export const updateAsset = async (
    campaignId: string,
    assetId: string,
    data: { status?: string; content?: string; title?: string },
): Promise<CampaignAsset> => {
    const response = await apiClient.patch(`/campaigns/${campaignId}/assets/${assetId}`, data);
    return response.data;
};

export const deleteAsset = async (campaignId: string, assetId: string): Promise<void> => {
    await apiClient.delete(`/campaigns/${campaignId}/assets/${assetId}`);
};

export const regenerateSingleAsset = async (
    campaignId: string,
    assetId: string,
    lpTemplateType?: string,
): Promise<CampaignAsset> => {
    const response = await apiClient.post(`/campaigns/${campaignId}/assets/${assetId}/regenerate`, {
        lp_template_type: lpTemplateType || undefined,
    });
    return response.data;
};

export const publishCampaignAssetNow = (
    campaignId: string,
    assetId: string,
    body: { social_account_id?: string; wordpress_site_id?: string; link_override?: string }
): Promise<{ success: boolean; publication_id: string; post_url?: string; post_id?: string; error_message?: string }> =>
    apiClient.post(`/campaigns/${campaignId}/assets/${assetId}/publish-now`, body).then(r => r.data);

export interface PublishSocialPostNowRequest {
    task_id: string;
    platform: 'linkedin' | 'twitter' | 'instagram' | 'facebook';
    social_account_id: string;
    content: string; // post text — named `content` not `body` to avoid Fetch API Response.body collision
    hero_image_url?: string;
    link_override?: string;
}

export interface PublishSocialPostNowResponse {
    success: boolean;
    publication_id: string;
    post_url?: string;
    post_id?: string;
    error_message?: string;
}

export const publishSocialPostNow = (
    payload: PublishSocialPostNowRequest,
): Promise<PublishSocialPostNowResponse> =>
    apiClient.post('/social/posts/publish-now', payload).then(r => r.data);

// -- Campaign Asset Image Management --

export const fetchCampaignAssetImage = async (
    campaignId: string,
    assetId: string,
    query?: string,
    excludedIds?: string[],
): Promise<{ image_url: string }> => {
    const response = await apiClient.post(
        `/campaigns/${campaignId}/assets/${assetId}/fetch-image`,
        { query: query || undefined, excluded_image_ids: excludedIds || undefined },
    );
    return response.data;
};

export const uploadCampaignAssetImage = async (
    campaignId: string,
    assetId: string,
    file: File,
): Promise<{ image_url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
        `/campaigns/${campaignId}/assets/${assetId}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
};

export const setCampaignAssetImageUrl = async (
    campaignId: string,
    assetId: string,
    url: string,
    imageConcept?: string,
): Promise<{ image_url: string; image_concept?: string | null }> => {
    const response = await apiClient.post(
        `/campaigns/${campaignId}/assets/${assetId}/set-image-url`,
        { url, image_concept: imageConcept },
    );
    return response.data;
};

// -- Email Sequences --

export const getSequences = async (campaignId: string): Promise<EmailSequence[]> => {
    const response = await apiClient.get(`/campaigns/${campaignId}/sequences`);
    return response.data;
};

export const getSequence = async (sequenceId: string): Promise<EmailSequence> => {
    const response = await apiClient.get(`/campaigns/sequences/${sequenceId}`);
    return response.data;
};

export const createSequence = async (
    campaignId: string,
    data: {
        project_id: string;
        name: string;
        description?: string;
        trigger_type?: string;
        trigger_config?: any;
    },
): Promise<EmailSequence> => {
    const response = await apiClient.post(`/campaigns/${campaignId}/sequences`, data);
    return response.data;
};

export const updateSequence = async (
    sequenceId: string,
    data: Partial<EmailSequence>,
): Promise<EmailSequence> => {
    const response = await apiClient.patch(`/campaigns/sequences/${sequenceId}`, data);
    return response.data;
};

export const deleteSequence = async (sequenceId: string): Promise<void> => {
    await apiClient.delete(`/campaigns/sequences/${sequenceId}`);
};

export const generateSequence = async (sequenceId: string): Promise<EmailSequence> => {
    const response = await apiClient.post(`/campaigns/sequences/${sequenceId}/generate`);
    return response.data;
};

// -- Sequence Steps --

export const createStep = async (
    sequenceId: string,
    data: {
        sequence_id: string;
        step_order?: number;
        step_type?: string;
        delay_days?: number;
        subject_line?: string;
        preview_text?: string;
        body_html?: string;
        body_plain?: string;
    },
): Promise<EmailSequenceStep> => {
    const response = await apiClient.post(`/campaigns/sequences/${sequenceId}/steps`, data);
    return response.data;
};

export const updateStep = async (
    sequenceId: string,
    stepId: string,
    data: Partial<EmailSequenceStep>,
): Promise<EmailSequenceStep> => {
    const response = await apiClient.patch(`/campaigns/sequences/${sequenceId}/steps/${stepId}`, data);
    return response.data;
};

export const deleteStep = async (sequenceId: string, stepId: string): Promise<void> => {
    await apiClient.delete(`/campaigns/sequences/${sequenceId}/steps/${stepId}`);
};

export const reorderSteps = async (
    sequenceId: string,
    items: Array<{ step_id: string; step_order: number }>,
): Promise<EmailSequence> => {
    const response = await apiClient.post(`/campaigns/sequences/${sequenceId}/reorder`, items);
    return response.data;
};

export const duplicateSequence = async (sequenceId: string): Promise<{ sequence_id: string; name: string }> => {
    const response = await apiClient.post(`/campaigns/sequences/${sequenceId}/duplicate`);
    return response.data;
};

export const getCampaignTemplates = async (): Promise<CampaignTemplate[]> => {
    const response = await apiClient.get('/campaigns/templates');
    return response.data;
};

// ---------------------------------------------------------------------------
// H2.4 SEO Tooling
// ---------------------------------------------------------------------------

export interface SEOKeyword {
    keyword: string;
    search_intent: string;
    competition?: string;
    notes: string;
}

export interface SEOLongTailKeyword {
    keyword: string;
    notes: string;
}

export interface SEOKeywordResult {
    primary_keywords: SEOKeyword[];
    secondary_keywords: SEOKeyword[];
    long_tail_keywords: SEOLongTailKeyword[];
    questions_people_ask: string[];
}

export interface SEOHeading {
    level: string;
    text: string;
}

export interface SEOContentBrief {
    title: string;
    meta_description: string;
    target_word_count: number;
    heading_structure: SEOHeading[];
    secondary_keywords: string[];
    questions_to_answer: string[];
    content_tips: string[];
    competitor_angles: string[];
}

export interface SEOMetricScore {
    score: number;
    notes: string;
    value?: number;
}

export interface SEOCheckResult {
    overall_score: number;
    keyword_density: SEOMetricScore & { value: number };
    heading_structure: SEOMetricScore;
    meta_title: SEOMetricScore;
    meta_description: SEOMetricScore;
    readability: SEOMetricScore;
    ai_citability?: SEOMetricScore & { checks?: Record<string, boolean> };
    recommendations: string[];
}

export interface SEOMetaTags {
    meta_title: string;
    meta_description: string;
    og_title: string;
    og_description: string;
    twitter_title: string;
    twitter_description: string;
}

export interface SEOSchema {
    schema_type: string;
    json_ld: string;
}

export const seoKeywords = (data: {
    topic: string;
    industry: string;
    location?: string;
}): Promise<SEOKeywordResult> =>
    apiClient.post('/seo/keywords', data).then(r => r.data);

export const seoContentBrief = (data: {
    keyword: string;
    industry: string;
    brand_guide_id?: string;
    project_id?: string;
}): Promise<SEOContentBrief> =>
    apiClient.post('/seo/content-brief', data).then(r => r.data);

export const seoCheck = (data: {
    content: string;
    target_keyword: string;
    meta_title?: string;
    meta_description?: string;
}): Promise<SEOCheckResult> =>
    apiClient.post('/seo/check', data).then(r => r.data);

export const seoMetaTags = (data: {
    content: string;
    title: string;
    target_keyword?: string;
}): Promise<SEOMetaTags> =>
    apiClient.post('/seo/meta-tags', data).then(r => r.data);

export const seoSchema = (data: {
    schema_type: string;
    content: string;
    business_name?: string;
    title?: string;
    author?: string;
    url?: string;
}): Promise<SEOSchema> =>
    apiClient.post('/seo/schema', data).then(r => r.data);

// ---------------------------------------------------------------------------
// JSON-LD Schema auto-injection API (Phase 1)
// ---------------------------------------------------------------------------

export interface SchemaPreviewResponse {
    blocks: object[];
    html: string;
    dropped: { type: string; reason: string }[];
    counts: { valid: number; dropped: number };
}

export interface SchemaCoverageMissingItem {
    page_type: string;
    page_id: string;
    page_title: string;
    page_url: string;
    reason: string;
}

export interface SchemaCoverageResponse {
    total_published: number;
    with_valid_schema: number;
    coverage_pct: number;
    missing: SchemaCoverageMissingItem[];
}

/** Returns the JSON-LD blocks for a task (used by ContentPreview schema disclosure). */
export const getSchemaPreview = (taskId: string): Promise<SchemaPreviewResponse> =>
    apiClient.get(`/seo/schema/preview/${taskId}`).then(r => r.data);

/** Returns the Organization block for a brand guide (used by BrandPortal). */
export const getBrandSchemaPreview = (brandGuideId: string): Promise<SchemaPreviewResponse> =>
    apiClient.get(`/seo/schema/preview/brand/${brandGuideId}`).then(r => r.data);

/** Returns WebPage + Organization for a landing page. */
export const getLandingSchemaPreview = (landingPageId: string): Promise<SchemaPreviewResponse> =>
    apiClient.get(`/seo/schema/preview/landing/${landingPageId}`).then(r => r.data);

/** Returns WebPage + Organization + BreadcrumbList for a site builder page. */
export const getSiteSchemaPreview = (siteId: string, pageSlug: string): Promise<SchemaPreviewResponse> =>
    apiClient.get(`/seo/schema/preview/site/${siteId}/${pageSlug}`).then(r => r.data);

/** Validates HTML content and returns blocks + per-block validation status. No quota. */
export const validateSchema = (
    html: string,
    contentType: string,
    context: Record<string, any> = {}
): Promise<SchemaPreviewResponse> =>
    apiClient.post('/seo/schema/validate', { html, content_type: contentType, context }).then(r => r.data);

/** Returns schema coverage stats for a project. */
export const getSchemaCoverage = (
    projectId: string,
    windowDays: number = 90
): Promise<SchemaCoverageResponse> =>
    apiClient.get(`/seo/schema/coverage/${projectId}`, { params: { window_days: windowDays } }).then(r => r.data);

export interface SeoSearchRecord {
    id: number;
    search_type: string;
    query_params: Record<string, any>;
    result_data: Record<string, any>;
    created_at: string;
}

export interface SeoPageIndexabilityRecord {
    id: number;
    page_type: string;
    page_id: string;
    page_title: string;
    page_url: string;
    canonical_ok: boolean;
    canonical_url: string;
    robots_ok: boolean;
    sitemap_ok: boolean;
    guaranteed_at: string;
}

export const seoGetIndexability = (project_id: string): Promise<SeoPageIndexabilityRecord[]> =>
    apiClient.get('/seo/indexability', { params: { project_id } }).then(r => r.data);

export const seoSaveSearch = (data: {
    project_id: string;
    search_type: string;
    query_params: Record<string, any>;
    result_data: Record<string, any>;
}): Promise<{ id: number }> =>
    apiClient.post('/seo/history', data).then(r => r.data);

export const seoListSearches = (project_id: string): Promise<SeoSearchRecord[]> =>
    apiClient.get('/seo/history', { params: { project_id } }).then(r => r.data);

// ==================== SEO Audit API (SPEC_05) ====================

export interface SeoAuditFinding {
    check: string;
    severity: 'error' | 'warning' | 'info';
    message: string;
    category: 'crawlability' | 'onpage' | 'geo';
    value?: any;
}

export interface SeoAuditPageReport {
    url: string;
    status_code: number;
    ttfb_ms: number;
    page_weight_kb: number;
    findings: SeoAuditFinding[];
}

export interface SeoAuditReportJson {
    root_url: string;
    robots: {
        present: boolean;
        valid: boolean;
        blocks_user_agents: string[];
        sitemap_url: string | null;
        raw_text?: string | null;
    };
    sitemap: {
        present: boolean;
        valid: boolean;
        url_count: number;
        sampled_urls: string[];
    };
    llms_txt: { present: boolean };
    pages: SeoAuditPageReport[];
    site_findings: SeoAuditFinding[];
    score: {
        total: number;
        crawlability: number;
        onpage: number;
        geo: number;
        max: { crawlability: number; onpage: number; geo: number };
    };
}

export interface SeoAuditRecord {
    audit_id: string;
    project_id?: string | null;
    email?: string | null;
    root_url: string;
    max_pages: number;
    status: 'running' | 'complete' | 'failed';
    score_total: number | null;
    score_crawlability: number | null;
    score_onpage: number | null;
    score_geo: number | null;
    report_json: SeoAuditReportJson | null;
    error_message: string | null;
    source: 'in_app' | 'public_tool';
    created_at: string;
    completed_at: string | null;
}

export interface SeoAuditHistoryItem {
    audit_id: string;
    root_url: string;
    score_total: number | null;
    score_crawlability: number | null;
    score_onpage: number | null;
    score_geo: number | null;
    status: 'running' | 'complete' | 'failed';
    created_at: string;
}

export const runSeoAudit = (data: { url: string; project_id?: string }): Promise<{ audit_id: string; status: string; max_pages_used: number }> =>
    apiClient.post('/seo/audit/run', data).then(r => r.data);

export const getSeoAudit = (audit_id: string): Promise<SeoAuditRecord> =>
    apiClient.get(`/seo/audit/${audit_id}`).then(r => r.data);

export const listSeoAuditHistory = (project_id: string): Promise<SeoAuditHistoryItem[]> =>
    apiClient.get(`/seo/audit/history/${project_id}`).then(r => r.data);

// ── Migration Check (T17 Site-Rebuild Migration Check) ──────────────────────

export interface MigrationMissingPage {
    url: string;
    raw_url: string;
    clicks: number;
    impressions: number;
    suggested_redirect_to: string | null;
    redirect_confidence: number;
    match_kind: 'missing' | 'changed';
}

export interface MigrationCheckResult {
    has_gsc_history: boolean;
    window_days: number;
    historical_page_count: number;
    live_url_count: number;
    live_source: 'audit' | 'sitemap' | 'candidate_urls';
    missing_pages: MigrationMissingPage[];
    changed_pages: MigrationMissingPage[];
    total_lost_clicks: number;
    total_lost_impressions: number;
}

export interface MigrationPreviewSummary {
    has_risk: boolean;
    missing_count: number;
    changed_count: number;
    lost_clicks: number;
    lost_impressions: number;
    top_missing: { raw_url: string; clicks: number; impressions: number }[];
    window_days: number;
}

export const getMigrationCheck = (
    project_id: string,
    opts?: { window_days?: number; min_clicks?: number; min_impressions?: number; live_source?: 'audit' | 'sitemap' },
): Promise<MigrationCheckResult> =>
    apiClient.get(`/seo/migration-check/${project_id}`, { params: opts || {} }).then(r => r.data);

export const previewPublishMigrationCheck = (data: {
    project_id: string;
    candidate_urls: string[];
}): Promise<MigrationPreviewSummary> =>
    apiClient.post('/seo/migration-check/preview', data).then(r => r.data);

// ==================== Multi-Channel Content API ====================

export interface MultiChannelFormatResult {
    formatted: Record<string, { content: string; character_count: number }>;
}

export interface TwitterThread {
    tweets: { number: number; text: string; char_count: number }[];
    total_tweets: number;
}

export interface LinkedInCarousel {
    title: string;
    subtitle: string;
    slides: {
        slide_number: number;
        headline: string;
        body: string;
        visual_suggestion: string;
    }[];
    cover_text: string;
    closing_cta: string;
}

export interface RepurposeResult {
    social_posts: Record<string, string>;
    email_version: {
        subject_line: string;
        preheader: string;
        body_html: string;
    };
    pull_quotes: string[];
    carousel_outline: LinkedInCarousel;
    video_script: {
        hook: string;
        talking_points: string[];
        cta: string;
    };
}

export interface AdCopyPlatformGoogle {
    headlines: string[];
    descriptions: string[];
    display_path?: string;
    headlines_over_limit?: boolean;
    descriptions_over_limit?: boolean;
}
export interface AdCopyPlatformMeta {
    primary_text: string;
    headline: string;
    description: string;
    cta_options?: string[];
    primary_text_over_limit?: boolean;
    headline_over_limit?: boolean;
    description_over_limit?: boolean;
}
export interface AdCopyPlatformLinkedIn {
    intro_text: string;
    headline: string;
    description: string;
    cta?: string;
    intro_text_over_limit?: boolean;
    headline_over_limit?: boolean;
    description_over_limit?: boolean;
}
export interface AdCopyPlatformTwitter {
    tweet_text: string;
    card_headline?: string;
}
export interface AdCopyPlatformTikTok {
    ad_text: string;
    ad_text_over_limit?: boolean;
}

export interface AdCopyResult {
    // New generator keys
    google?: AdCopyPlatformGoogle;
    meta?: AdCopyPlatformMeta;
    linkedin?: AdCopyPlatformLinkedIn;
    twitter?: AdCopyPlatformTwitter;
    tiktok?: AdCopyPlatformTikTok;
    _angle?: string;
    _framework?: string;
    _error?: string;
    _audience_segment?: string | null;
    _offer?: string | null;
    _offer_inferred?: boolean;
    _audience_inferred?: boolean;
    _audience_segments_all?: string[] | null;
    // Legacy multichannel keys (backward compat)
    google_search?: AdCopyPlatformGoogle;
    meta_facebook?: AdCopyPlatformMeta;
    linkedin_sponsored?: AdCopyPlatformLinkedIn;
}

export interface AdCopyRequest {
    product_service: string;
    target_audience: string;
    goal: string;
    platforms?: string[];
    brand_guide_id?: string;
    project_id?: string;  // resolves business location + project emoji policy when no brand guide is selected
    angle?: string;
    framework?: string;
    offer?: string;
    audience_segments?: string[];
    variant_count?: number;
}

export interface ThreadRequest {
    content: string;
    topic?: string;
    brand_guide_id?: string;
    project_id?: string;  // resolves business location + project emoji policy when no brand guide is selected
}

export const formatForPlatforms = (data: any): Promise<MultiChannelFormatResult> =>
    apiClient.post('/multichannel/format', data, { timeout: 120000 }).then(r => r.data);

export const buildTwitterThread = (data: ThreadRequest): Promise<TwitterThread> =>
    apiClient.post('/multichannel/thread', data, { timeout: 120000 }).then(r => r.data);

export const generateLinkedInCarousel = (data: any): Promise<LinkedInCarousel> =>
    apiClient.post('/multichannel/linkedin-carousel', data, { timeout: 120000 }).then(r => r.data);

export const repurposeContent = (data: any): Promise<RepurposeResult> =>
    apiClient.post('/multichannel/repurpose', data, { timeout: 180000 }).then(r => r.data);

export const generateAdCopy = (data: any): Promise<AdCopyResult> =>
    apiClient.post('/multichannel/ad-copy', data, { timeout: 120000 }).then(r => r.data);

// -- Project-connected repurposing pipeline --

export interface ProjectArticle {
    task_id: string;
    name: string;
    description: string;
    has_hero_image: boolean;
    masterplan_stream: string;
}

export interface RepurposeArticleResponse {
    task_ids: Record<string, string>;
    hero_image: any;
    repurpose_result: RepurposeResult & { ad_copy?: AdCopyResult };
    email_drip: {
        sequence_name: string;
        emails: {
            step_order: number;
            delay_days: number;
            subject_line: string;
            preview_text: string;
            body_html: string;
            body_plain: string;
        }[];
    } | null;
    email_sequence_id: string | null;
    summary: Record<string, boolean>;
}

export const getProjectArticles = (projectId: string): Promise<ProjectArticle[]> =>
    apiClient.get(`/multichannel/project-articles/${projectId}`).then(r => r.data);

export const repurposeArticle = (data: {
    task_id: string;
    project_id: string;
    brand_guide_id?: string;
    generate_email_drip?: boolean;
    auto_fetch_image?: boolean;
    generate_ad_copy?: boolean;
    ad_copy_platforms?: string[];
    ad_copy_angle?: string;
}): Promise<RepurposeArticleResponse> =>
    apiClient.post('/multichannel/repurpose-article', data, { timeout: 300000 }).then(r => r.data);

export const saveMultichannelOutput = (data: {
    project_id: string;
    source_task_id?: string;
    content_type: string;
    content_data: any;
    article_title?: string;
}): Promise<{ saved: boolean; type: string; task_id?: string; sequence_id?: string }> =>
    apiClient.post('/multichannel/save-output', data, { timeout: 30000 }).then(r => r.data);

export interface SavedTaskRef {
    task_id: string;
    status: string;
    external_post_url: string | null;
    saved_at?: string | null;   // ISO-8601 UTC, added 2026-05-19
}

export const getArticleMultichannelContent = (taskId: string): Promise<{
    multichannel: RepurposeArticleResponse | null;
    saved_tasks?: Record<string, SavedTaskRef>;
}> =>
    apiClient.get(`/multichannel/article-content/${taskId}`).then(r => r.data);

export const regenerateSection = (data: {
    task_id: string;
    project_id: string;
    section: string;
    instructions?: string;
    brand_guide_id?: string;
}): Promise<{ section_data: any; multichannel: any }> =>
    apiClient.post('/multichannel/regenerate-section', data, { timeout: 120000 }).then(r => r.data);

// ---------------------------------------------------------------------------
// H2.8 SuiteCRM Publishing Integrations
// ---------------------------------------------------------------------------

export const syncLeadToCRM = (data: {
    first_name?: string;
    last_name: string;
    email?: string;
    phone?: string;
    company?: string;
    lead_source?: string;
    description?: string;
    project_id?: string;
}): Promise<{ crm_lead_id: string; status: string; message: string }> =>
    apiClient.post('/crm/leads/sync', data).then(r => r.data);

export const bulkSyncLeadsToCRM = (data: {
    leads: any[];
    project_id?: string;
}): Promise<{ synced: number; failed: number; errors: string[] }> =>
    apiClient.post('/crm/leads/bulk-sync', data).then(r => r.data);

export const syncCampaignToCRM = (campaignId: string): Promise<{
    crm_campaign_id: string;
    status: 'synced' | 'error';
}> =>
    apiClient.post(`/crm/campaigns/sync/${campaignId}`).then(r => r.data);

export const getCampaignCRMStatus = (campaignId: string): Promise<{
    campaign_id: string;
    synced: boolean;
    crm_campaign_id?: string;
    last_synced?: string;
}> =>
    apiClient.get(`/crm/campaigns/${campaignId}/status`).then(r => r.data);

export const executeEmailSequence = (sequenceId: string): Promise<{
    sequence_id: string;
    steps_synced: number;
    crm_template_ids: string[];
    status: string;
    errors?: string[];
}> =>
    apiClient.post(`/crm/email-sequences/${sequenceId}/execute`).then(r => r.data);

export const getSequenceSyncStatus = (sequenceId: string): Promise<{
    sequence_id: string;
    synced: boolean;
    crm_synced_at?: string;
    steps_count: number;
}> =>
    apiClient.get(`/crm/email-sequences/${sequenceId}/sync-status`).then(r => r.data);

// ============================================================
// Analytics & Reporting (H2.5)
// ============================================================

export const getAnalyticsOverview = (projectId?: string): Promise<any> =>
    apiClient.get('/analytics/overview', { params: projectId ? { project_id: projectId } : {} }).then(r => r.data);

export const getCampaignAnalytics = (campaignId: string): Promise<any> =>
    apiClient.get(`/analytics/campaign/${campaignId}`).then(r => r.data);

export const getEmailMetrics = (): Promise<any> =>
    apiClient.get('/analytics/email-metrics').then(r => r.data);

export const getContentPerformance = (projectId?: string): Promise<any> =>
    apiClient.get('/analytics/content-performance', { params: projectId ? { project_id: projectId } : {} }).then(r => r.data);

export const generateMonthlySummary = (data: { project_id: string; month?: string }): Promise<any> =>
    apiClient.post('/analytics/monthly-summary', data).then(r => r.data);

export const getTrafficPlaceholder = (): Promise<any> =>
    apiClient.get('/analytics/traffic-placeholder').then(r => r.data);

// ============================================================
// H2.9 Lead Capture & CRM
// ============================================================

export const getLeadForms = (projectId: string): Promise<any[]> =>
    apiClient.get(`/lead-capture/forms?project_id=${projectId}`).then(r => r.data);

export const getAllLeadForms = (): Promise<any[]> =>
    apiClient.get('/lead-capture/forms').then(r => r.data);

export const getLeadForm = (formId: string): Promise<any> =>
    apiClient.get(`/lead-capture/forms/${formId}`).then(r => r.data);

export const createLeadForm = (data: any): Promise<any> =>
    apiClient.post('/lead-capture/forms', data).then(r => r.data);

export const updateLeadForm = (formId: string, data: any): Promise<any> =>
    apiClient.patch(`/lead-capture/forms/${formId}`, data).then(r => r.data);

export const deleteLeadForm = (formId: string): Promise<void> =>
    apiClient.delete(`/lead-capture/forms/${formId}`).then(() => {});

export const generateLeadForm = (data: any): Promise<any> =>
    apiClient.post('/lead-capture/forms/generate', data).then(r => r.data);

export const getFormSubmissions = (formId: string, skip = 0, limit = 25): Promise<any> =>
    apiClient.get(`/lead-capture/forms/${formId}/submissions?skip=${skip}&limit=${limit}`).then(r => r.data);

export const deleteFormSubmission = (
    submissionId: string,
    cascadeCrm = false,
): Promise<{ crm_deleted: boolean | null; crm_error: string | null }> =>
    apiClient.delete(`/lead-capture/submissions/${submissionId}`, {
        params: { cascade_crm: cascadeCrm },
    }).then(r => r.data);

export const getLeadCaptureSequences = (projectId: string): Promise<any[]> =>
    apiClient.get(`/lead-capture/sequences?project_id=${projectId}`).then(r => r.data);

export const getABTests = (projectId: string): Promise<any[]> =>
    apiClient.get(`/lead-capture/ab-tests?project_id=${projectId}`).then(r => r.data);

export const createABTest = (data: any): Promise<any> =>
    apiClient.post('/lead-capture/ab-tests', data).then(r => r.data);

export const updateABTest = (testId: string, data: any): Promise<any> =>
    apiClient.patch(`/lead-capture/ab-tests/${testId}`, data).then(r => r.data);

export const deleteABTest = (testId: string): Promise<void> =>
    apiClient.delete(`/lead-capture/ab-tests/${testId}`).then(() => {});

export const getNurtureWorkflows = (projectId: string): Promise<any[]> =>
    apiClient.get(`/lead-capture/workflows?project_id=${projectId}`).then(r => r.data);

export const createNurtureWorkflow = (data: any): Promise<any> =>
    apiClient.post('/lead-capture/workflows', data).then(r => r.data);

export const updateNurtureWorkflow = (workflowId: string, data: any): Promise<any> =>
    apiClient.patch(`/lead-capture/workflows/${workflowId}`, data).then(r => r.data);

export const deleteNurtureWorkflow = (workflowId: string): Promise<void> =>
    apiClient.delete(`/lead-capture/workflows/${workflowId}`).then(() => {});

export const enrollLeadInWorkflow = (workflowId: string, leadData: any): Promise<any> =>
    apiClient.post(`/lead-capture/workflows/${workflowId}/enroll`, { lead_data: leadData }).then(r => r.data);

export interface NurtureWorkflowEnrollmentEvent {
    event_id: string;
    trigger: string;
    source: string;
    source_ref?: string | null;
    recipient_email: string;
    outcome: 'enrolled' | 'duplicate' | 'suppressed' | 'no_sequence' | 'error';
    detail?: string | null;
    created_at?: string | null;
}

export const getWorkflowEnrollmentLog = (workflowId: string): Promise<{ events: NurtureWorkflowEnrollmentEvent[] }> =>
    apiClient.get(`/lead-capture/workflows/${workflowId}/enrollment-log`).then(r => r.data);

// ==================== Text Overlay Compositing ====================

// OverlayState is defined here (in services/api.ts, not in the component) to
// avoid a service-imports-component dependency inversion. Components import
// this type from api.ts; api.ts never imports from components.
export interface OverlayState {
    text: string;
    size: 'small' | 'medium' | 'large';
    text_color: string;        // '#RRGGBB'
    glow_enabled: boolean;
    glow_color: string;        // '#RRGGBB'
    shadow_enabled: boolean;
    shadow_color: string;      // '#RRGGBB'
    position: number;          // 0–8 (3×3 grid)
    // Free-text on the wire: one of the 6 bundled keys, or a "brand:<Family>" value
    // for a Google/uploaded brand font (Tier 2). Use BUNDLED_OVERLAY_FONTS for the
    // closed built-in set. default 'inter'.
    font_family: string;
    italic: boolean;                                    // default false
    fallback_to_prompt: boolean;
    /** URL of the original pre-text base image. Stamped by the FRONTEND
     *  on first composite (Improvement 2). On re-edit, this value (not the
     *  current `image_url`) is what gets passed to `compositeText()` so
     *  successive edits never bake the previous overlay into the new image.
     *  Optional because legacy overlay_state values in DB won't have it —
     *  the frontend treats `undefined` as "fall back to the current image_url". */
    base_image_url?: string;                            // optional
    // ── Logo overlay (Phase 2) ──────────────────────────────────────────────
    logo_url?: string | null;
    logo_size?: 'small' | 'medium' | 'large' | 'xlarge';
    logo_position?: number;     // 0-8
    logo_opacity?: number;      // 0.0-1.0
    logo_source?: 'brand_guide' | 'upload';
    logo_shadow_enabled: boolean;
    logo_shadow_color: string;   // '#RRGGBB'
    logo_glow_enabled: boolean;
    logo_glow_color: string;     // '#RRGGBB'
    logo_enabled?: boolean;
}

export interface CompositeTextResponse {
    url: string;
    width: number;
    height: number;
    overlay_state: OverlayState;
    fallback_used: boolean;
    logo_fetch_failed?: boolean;
    thumbnail_url?: string | null;
}

export const compositeText = (payload: {
    base_image_url: string;
    fallback_visual_prompt?: string;
    project_id?: string;
} & OverlayState): Promise<CompositeTextResponse> =>
    // 120 s to accommodate the fallback Gemini image-generation path (spec Known Risks #12:
    // prompt-based generation can take up to 60 s; 60 s was too tight).
    apiClient.post('/images/composite-text', payload, { timeout: 120000 }).then(r => r.data);

// The 6 fonts bundled on disk (/overlay-fonts/) — the closed built-in overlay set.
export const BUNDLED_OVERLAY_FONTS = ['inter', 'playfair', 'montserrat', 'bebas', 'poppins', 'caveat'] as const;
export type BundledOverlayFont = typeof BUNDLED_OVERLAY_FONTS[number];

export interface OverlayFontResolution {
    family: string;
    resolvable: boolean;
    is_open_source: boolean | null;
    category: string | null;
    canvas_font_url: string | null;
    canvas_format: string | null;
    bundled_key: string | null;
    fallback_reason: string | null;
}

// Resolve a brand font family to a canvas-loadable web-font URL (Tier 2 overlays).
// Also warms the server-side cache so a subsequent composite is fast.
export const resolveOverlayFont = (
    family: string,
    italic: boolean,
    projectId?: string,
): Promise<OverlayFontResolution> =>
    apiClient.get('/images/overlay-fonts/resolve', {
        params: { family, italic, ...(projectId ? { project_id: projectId } : {}) },
    }).then(r => r.data);

// ==================== Images API (H2.3) ====================

export interface GeneratedImageMeta {
    image_id: string;
    prompt: string;
    asset_type?: string;
    campaign_id?: string;
    project_id?: string;
    width: number;
    height: number;
    created_at: string;
    has_variants: boolean;
}

export interface GeneratedImageFull extends GeneratedImageMeta {
    original_image: string;
    platform_variants: Record<string, string>;
    revised_prompt?: string;
}

export const generateImage = (data: any): Promise<any> =>
    apiClient.post('/images/generate', data, { timeout: 120000 }).then(r => r.data);

export const resizeImage = (imageId: string, platforms: string[]): Promise<any> =>
    apiClient.post(`/images/${imageId}/resize`, { platforms }).then(r => r.data);

export interface ApplyNoiseResponse {
    url: string;
    width: number;
    height: number;
    seed: number;
    thumbnail_url?: string | null;
}

// strip_metadata removes C2PA Content Credentials (the AI-provenance marker
// Gemini embeds in generated images) -- default false, and the frontend must
// always surface it as its own explicit, clearly-labeled control. Never fold
// it into the noise toggle.
export const applyNoiseFilter = (payload: {
    base_image_url?: string;
    base_image_b64?: string;
    sigma?: number;
    seed?: number;
    strip_metadata?: boolean;
}): Promise<ApplyNoiseResponse> =>
    apiClient.post('/images/apply-noise', payload).then(r => r.data);

export const getImages = (params?: { project_id?: string; campaign_id?: string }): Promise<GeneratedImageMeta[]> =>
    apiClient.get('/images', { params }).then(r => r.data);

export const getImageFull = (imageId: string): Promise<GeneratedImageFull> =>
    apiClient.get(`/images/${imageId}`).then(r => r.data);

export const deleteImage = (imageId: string): Promise<void> =>
    apiClient.delete(`/images/${imageId}`).then(() => {});

export const generateImagePrompt = (data: any): Promise<{ prompt: string; style_suggestions: string[] }> =>
    apiClient.post('/images/generate-prompt', data, { timeout: 60000 }).then(r => r.data);

// ── Sprint 2.2: Model-selector image generation ────────────────────────────

export interface AiGeneratedImage {
    id: string;
    url: string;
    width: number;
    height: number;
    provider: string;
    model_id: string;
    revised_prompt?: string;
    platform: string;
    prompt?: string;
    created_at?: string;
    noise_applied?: boolean;
}

export interface ImageQuota {
    used_today: number;
    daily_limit: number | null;
    tier: string;
    unlimited: boolean;
}

export interface AiImageGenerateRequest {
    prompt: string;
    platform?: string;
    quality?: string;
    style_hint?: string;
    filename_hint?: string;
}

export const generateImageAi = (data: AiImageGenerateRequest): Promise<AiGeneratedImage> =>
    apiClient.post('/images/generate-ai', data, { timeout: 120000 }).then(r => r.data);

// Hero image generation for articles and social posts.
// platform: 'default' keeps square-ish dimensions suitable for hero use across
// all four social platforms and article headers (no platform-specific crop).
// filename_hint (the article title) names the saved file so downloads are recognisable.
export const generateAiHeroImage = (prompt: string, filenameHint?: string): Promise<AiGeneratedImage> =>
    apiClient.post('/images/generate-ai', { prompt, platform: 'default', filename_hint: filenameHint }, { timeout: 120000 }).then(r => r.data);

export const getGeneratedImagesAi = (params?: { limit?: number; offset?: number }): Promise<AiGeneratedImage[]> =>
    apiClient.get('/images/generated', { params }).then(r => r.data);

export const getImageQuota = (): Promise<ImageQuota> =>
    apiClient.get('/images/quota').then(r => r.data);

// ==================== Review & Reputation Management API (H2.7) ====================

export const getReviewTemplates = (projectId: string): Promise<any[]> =>
    apiClient.get(`/reviews/templates?project_id=${projectId}`).then(r => r.data);

export const createReviewTemplate = (data: any): Promise<any> =>
    apiClient.post('/reviews/templates', data).then(r => r.data);

export const updateReviewTemplate = (templateId: string, data: any): Promise<any> =>
    apiClient.patch(`/reviews/templates/${templateId}`, data).then(r => r.data);

export const deleteReviewTemplate = (templateId: string): Promise<void> =>
    apiClient.delete(`/reviews/templates/${templateId}`).then(() => {});

export const generateReviewTemplate = (data: any): Promise<{ subject: string; body: string }> =>
    apiClient.post('/reviews/templates/generate', data, { timeout: 60000 }).then(r => r.data);

export const generateReviewResponse = (data: any): Promise<{ response_text: string; tone: string; key_points: string[] }> =>
    apiClient.post('/reviews/responses/generate', data, { timeout: 60000 }).then(r => r.data);

export const bulkGenerateReviewResponses = (data: any): Promise<{ responses: any[] }> =>
    apiClient.post('/reviews/responses/bulk-generate', data, { timeout: 120000 }).then(r => r.data);

export const getSavedReviewResponses = (projectId: string): Promise<any[]> =>
    apiClient.get(`/reviews/saved-responses?project_id=${projectId}`).then(r => r.data);

export const saveReviewResponse = (data: any): Promise<any> =>
    apiClient.post('/reviews/saved-responses', data).then(r => r.data);

export const deleteSavedReviewResponse = (responseId: string): Promise<void> =>
    apiClient.delete(`/reviews/saved-responses/${responseId}`).then(() => {});

// ==================== Paid Media & Advertising API (H2.6) ====================

export const buildUtmUrl = (data: any): Promise<{ tagged_url: string; params: any; utm_link_id?: string }> =>
    apiClient.post('/multichannel/utm', data).then(r => r.data);

export interface UtmLinkRow {
    utm_link_id: string;
    tagged_url: string;
    base_url: string;
    source: string;
    medium: string;
    campaign: string;
    content: string | null;
    term: string | null;
    click_count: number;
    last_click_at: string | null;
    disabled_at: string | null;
    created_at: string;
    updated_at: string;
}

export const listUtmLinks = (projectId: string, active: boolean = true): Promise<UtmLinkRow[]> =>
    apiClient.get('/multichannel/utm/links', { params: { project_id: projectId, active } }).then(r => r.data);

export const deleteUtmLink = (utmLinkId: string): Promise<{ disabled: boolean }> =>
    apiClient.delete(`/multichannel/utm/links/${utmLinkId}`).then(r => r.data);

export const restoreUtmLink = (utmLinkId: string): Promise<{ restored: boolean }> =>
    apiClient.post(`/multichannel/utm/links/${utmLinkId}/restore`).then(r => r.data);

export const checkMessageMatch = (data: any): Promise<{ match_score: number; alignment_issues: string[]; recommendations: string[]; verdict: string }> =>
    apiClient.post('/multichannel/message-match', data, { timeout: 60000 }).then(r => r.data);

// ==================== Collaboration — User Roles API (H2.10) ====================

export const getAllUsersWithRoles = (): Promise<any[]> =>
    apiClient.get('/auth/users').then(r => r.data);

export const updateUserRole = (userId: string, role: string): Promise<any> =>
    apiClient.patch(`/auth/users/${userId}/role`, { role }).then(r => r.data);

// ==================== H2.11 Contextual Page Assistant API ====================

export interface AssistantPageContext {
    path: string;
    module: string;
    page_title: string;
}

export interface AssistantChatResponse {
    response: string;
    navigate_to?: string;
    action?: string;
    suggested_prompts: string[];
}

export interface AssistantContextResponse {
    campaigns_count: number;
    recent_campaigns: string[];
    active_sequences: number;
    recent_content_items: string[];
    brand_guides_count: number;
    greeting: string;
}

export const assistantChat = (data: {
    message: string;
    conversation_history: { role: string; content: string }[];
    page_context: AssistantPageContext;
    project_id?: string;
}): Promise<AssistantChatResponse> =>
    apiClient.post('/assistant/chat', data, { timeout: 60000 }).then(r => r.data);

// SSE streaming endpoint for assistant chat (used with fetch + ReadableStream, not EventSource)
export const ASSISTANT_STREAM_URL = `${API_BASE_URL}/assistant/chat-stream`;

export const getAssistantContext = (): Promise<AssistantContextResponse> =>
    apiClient.get('/assistant/context').then(r => r.data);

export const getAssistantSuggestedPrompts = (pageContext: AssistantPageContext): Promise<string[]> =>
    apiClient.post('/assistant/suggested-prompts', { page_context: pageContext }).then(r => r.data.prompts || []);

export const getAssistantHistory = (): Promise<{ conversation_id: string; messages: { role: string; content: string; created_at: string }[] }> =>
    apiClient.get('/assistant/history').then(r => r.data);

export const clearAssistantHistory = (): Promise<any> =>
    apiClient.post('/assistant/clear').then(r => r.data);

// ── H3.1 AI Agent Mode ──────────────────────────────────────────────────────

export const getCompetitors = (projectId: string): Promise<any[]> =>
    apiClient.get(`/agent/competitors?project_id=${projectId}`).then(r => r.data);

export const addCompetitor = (data: any): Promise<any> =>
    apiClient.post('/agent/competitors', data).then(r => r.data);

export const updateCompetitor = (id: string, data: any): Promise<any> =>
    apiClient.patch(`/agent/competitors/${id}`, data).then(r => r.data);

export const deleteCompetitor = (id: string): Promise<void> =>
    apiClient.delete(`/agent/competitors/${id}`).then(() => {});

export const analyzeCompetitor = (id: string): Promise<any> =>
    apiClient.post(`/agent/competitors/${id}/analyze`, {}, { timeout: 90000 }).then(r => r.data);

export const getLatestCompetitorReport = (id: string): Promise<any> =>
    apiClient.get(`/agent/competitors/${id}/reports/latest`).then(r => r.data);

export const mineCompetitorReviews = (id: string, force = false): Promise<any> =>
    apiClient.post(`/agent/competitors/${id}/mine-reviews?force=${force}`, {}, { timeout: 120000 }).then(r => r.data);

export const getReviewInsights = (id: string): Promise<any[]> =>
    apiClient.get(`/agent/competitors/${id}/review-insights`).then(r => r.data);

export const getLatestReviewInsight = (id: string): Promise<any> =>
    apiClient.get(`/agent/competitors/${id}/review-insights/latest`).then(r => r.data);

export const promoteReviewTopicsToBriefs = (competitorId: string, insightId: string): Promise<any> =>
    apiClient.post(`/agent/competitors/${competitorId}/review-insights/${insightId}/to-briefs`, {}).then(r => r.data);

export const generateContentCalendar = (data: any): Promise<any> =>
    apiClient.post('/agent/content-calendar/generate', data, { timeout: 90000 }).then(r => r.data);

export const getContentCalendars = (projectId: string, month?: string): Promise<any[]> =>
    apiClient.get('/agent/content-calendar', { params: { project_id: projectId, month } }).then(r => r.data);

export const updateContentCalendar = (planId: string, data: any): Promise<any> =>
    apiClient.patch(`/agent/content-calendar/${planId}`, data).then(r => r.data);

export const generateMonthlyReport = (data: any): Promise<any> =>
    apiClient.post('/agent/monthly-report/generate', data, { timeout: 90000 }).then(r => r.data);

export const getMonthlyReports = (projectId: string): Promise<any[]> =>
    apiClient.get('/agent/monthly-reports', { params: { project_id: projectId } }).then(r => r.data);

export const getMonthlyReport = (reportId: string): Promise<any> =>
    apiClient.get(`/agent/monthly-reports/${reportId}`).then(r => r.data);

export const getContentSuggestions = (projectId: string): Promise<any> =>
    apiClient.post('/agent/content-suggestions', { project_id: projectId }, { timeout: 60000 }).then(r => r.data);

// ==================== H3.5 Proactive Coordinator API ====================

export interface Notification {
    notification_id: string;
    type: string;
    title: string;
    body: string;
    action_url?: string;
    action_label?: string;
    priority: 'high' | 'medium' | 'low';
    read: boolean;
    created_at: string;
}

export const getNotifications = (params?: { unread_only?: boolean; limit?: number }): Promise<Notification[]> =>
    apiClient.get('/coordinator/notifications', { params }).then(r => r.data);

export const getUnreadNotificationCount = (): Promise<{ unread_count: number }> =>
    apiClient.get('/coordinator/notifications/count').then(r => r.data);

export const markNotificationRead = (id: string): Promise<void> =>
    apiClient.post(`/coordinator/notifications/${id}/read`).then(() => {});

export const markAllNotificationsRead = (): Promise<void> =>
    apiClient.post('/coordinator/notifications/read-all').then(() => {});

export const dismissNotification = (id: string): Promise<void> =>
    apiClient.post(`/coordinator/notifications/${id}/dismiss`).then(() => {});

export const deleteNotification = (id: string): Promise<void> =>
    apiClient.delete(`/coordinator/notifications/${id}`).then(() => {});

export const runCoordinatorChecks = (): Promise<any> =>
    apiClient.post('/coordinator/run-checks').then(r => r.data);

export const getWeeklyCheckin = (): Promise<any> =>
    apiClient.get('/coordinator/weekly-checkin', { timeout: 60000 }).then(r => r.data);

// ── H3.3 Enterprise Features ─────────────────────────────────────────────────

export interface AuditLogEntry {
    log_id: string;
    user_id: string | null;
    user_email: string | null;
    action: string | null;
    resource_type: string | null;
    resource_id: string | null;
    resource_name: string | null;
    details: Record<string, any> | null;
    ip_address: string | null;
    success: boolean;
    created_at: string;
}

export interface AuditLogParams {
    action?: string;
    resource_type?: string;
    user_id?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
}

export const getAuditLogs = (params?: AuditLogParams): Promise<{ logs: AuditLogEntry[]; total: number }> =>
    apiClient.get('/enterprise/audit-logs', { params }).then(r => r.data);

export const getAuditLogActions = (): Promise<string[]> =>
    apiClient.get('/enterprise/audit-logs/actions').then(r => r.data);

export const getPlatformStats = (): Promise<any> =>
    apiClient.get('/enterprise/stats').then(r => r.data);

export const getPerformanceStats = (): Promise<any> =>
    apiClient.get('/enterprise/performance').then(r => r.data);

export const getOAuthAuthorizeUrl = (provider: string): Promise<{ authorization_url: string | null; configured: boolean }> =>
    apiClient.get(`/auth/oauth/${provider}/authorize`).then(r => r.data);

// ==================== H3.2 Marketplace API ====================

export interface MarketplaceTemplate {
    template_id: string;
    category: string;
    name: string;
    description: string | null;
    tags: string[];
    owner_name: string;
    use_count: number;
    featured: boolean;
    is_public: boolean;
    is_saved: boolean;
    thumbnail_url?: string | null;
    created_at: string;
    updated_at: string;
    template_data?: any;
}

export interface MarketplaceTemplateList {
    templates: MarketplaceTemplate[];
    total: number;
}

export const getMarketplaceTemplates = (params?: any): Promise<MarketplaceTemplateList> =>
    apiClient.get('/marketplace/templates', { params }).then(r => r.data);

export const getFeaturedTemplates = (): Promise<MarketplaceTemplate[]> =>
    apiClient.get('/marketplace/featured').then(r => r.data);

export const getMarketplaceTemplate = (id: string): Promise<MarketplaceTemplate> =>
    apiClient.get(`/marketplace/templates/${id}`).then(r => r.data);

export const getMyMarketplaceTemplates = (): Promise<MarketplaceTemplate[]> =>
    apiClient.get('/marketplace/my-templates').then(r => r.data);

export const getSavedTemplates = (): Promise<MarketplaceTemplate[]> =>
    apiClient.get('/marketplace/saved').then(r => r.data);

export const publishTemplate = (data: any): Promise<MarketplaceTemplate> =>
    apiClient.post('/marketplace/templates', data).then(r => r.data);

export const updateMarketplaceTemplate = (id: string, data: any): Promise<MarketplaceTemplate> =>
    apiClient.patch(`/marketplace/templates/${id}`, data).then(r => r.data);

export const deleteMarketplaceTemplate = (id: string): Promise<void> =>
    apiClient.delete(`/marketplace/templates/${id}`).then(() => {});

export const saveMarketplaceTemplate = (id: string): Promise<{ saved: boolean; template_id: string }> =>
    apiClient.post(`/marketplace/templates/${id}/save`).then(r => r.data);

export const useMarketplaceTemplate = (
    id: string,
    projectId: string,
    extra?: { title?: string; brandGuideId?: string },
): Promise<any> =>
    apiClient.post(`/marketplace/templates/${id}/use`, {
        project_id: projectId,
        title: extra?.title || undefined,
        brand_guide_id: extra?.brandGuideId || undefined,
    }).then(r => r.data);

export const publishCampaignAsTemplate = (campaignId: string): Promise<MarketplaceTemplate> =>
    apiClient.post(`/marketplace/publish-from/campaign/${campaignId}`).then(r => r.data);


// ── H4.1 Autonomy API ─────────────────────────────────────────────────────────

export const getAutonomySettings = (): Promise<any> =>
    apiClient.get('/autonomy/settings').then(r => r.data);

export const updateAutonomySettings = (data: any): Promise<any> =>
    apiClient.patch('/autonomy/settings', data).then(r => r.data);

export const pauseAgent = (until?: string): Promise<any> =>
    apiClient.post('/autonomy/settings/pause', { until }).then(r => r.data);

export const resumeAgent = (): Promise<any> =>
    apiClient.post('/autonomy/settings/resume').then(r => r.data);

export const getApprovalQueue = (status?: string): Promise<any[]> =>
    apiClient.get('/autonomy/queue', { params: status ? { status } : {} }).then(r => r.data);

export const getApprovalQueueItem = (id: string): Promise<any> =>
    apiClient.get(`/autonomy/queue/${id}`).then(r => r.data);

export const approveQueueItem = (id: string): Promise<any> =>
    apiClient.post(`/autonomy/queue/${id}/approve`).then(r => r.data);

export const rejectQueueItem = (id: string, note?: string): Promise<any> =>
    apiClient.post(`/autonomy/queue/${id}/reject`, { note }).then(r => r.data);

export const deleteQueueItem = (id: string): Promise<any> =>
    apiClient.delete(`/autonomy/queue/${id}`).then(r => r.data);

export const getAutonomyLog = (params?: any): Promise<any[]> =>
    apiClient.get('/autonomy/log', { params }).then(r => r.data);

export const rollbackAction = (logId: string): Promise<any> =>
    apiClient.post(`/autonomy/log/${logId}/rollback`).then(r => r.data);

export const enqueueAction = (data: any): Promise<any> =>
    apiClient.post('/autonomy/enqueue', data).then(r => r.data);

export const getSafetyStatus = (): Promise<any> =>
    apiClient.get('/autonomy/safety-status').then(r => r.data);

export const checkBlastRadius = (data: {
    action_category: string;
    contact_count?: number;
    post_count?: number;
}): Promise<any> =>
    apiClient.post('/autonomy/blast-radius-check', data).then(r => r.data);


// ── H4.2 Autonomous Content API ────────────────────────────────────────────────

export const executeCalendarPlan = (planId: string, data: {
    project_id: string;
    execute_all?: boolean;
    item_indices?: number[];
}): Promise<any> =>
    apiClient.post(`/autonomous/content/execute-calendar/${planId}`, data, {
        timeout: 300000, // 5 minutes — multiple Gemini calls
    }).then(r => r.data);

export const autonomousGenerateContent = (data: {
    project_id: string;
    content_type: string;
    topic: string;
    target_keyword?: string;
    platform?: string;
    brief?: string;
    campaign_id?: string;
}): Promise<any> =>
    apiClient.post('/autonomous/content/generate', data, {
        timeout: 120000, // 2 minutes
    }).then(r => r.data);

export const autonomousScheduleContent = (data: {
    task_id?: string;
    content: string;
    platform: string;
    project_id: string;
}): Promise<any> =>
    apiClient.post('/autonomous/content/schedule', data).then(r => r.data);

export const getAutonomousContentStatus = (projectId?: string): Promise<any> =>
    apiClient.get('/autonomous/content/status', {
        params: projectId ? { project_id: projectId } : {},
    }).then(r => r.data);


// ── H4.3 Autonomous Campaign API ───────────────────────────────────────────────

export const identifyCampaignOpportunities = (): Promise<any> =>
    apiClient.post('/autonomous/campaigns/identify-opportunities', {}, {
        timeout: 60000,
    }).then(r => r.data);

export const proposeCampaignBrief = (opportunity: any): Promise<any> =>
    apiClient.post('/autonomous/campaigns/propose-brief', { opportunity }, {
        timeout: 60000,
    }).then(r => r.data);

export const autoLaunchCampaign = (
    brief: any,
    assetTypes?: string[],
    brandGuideId?: string,
    projectId?: string,
): Promise<any> =>
    apiClient.post('/autonomous/campaigns/auto-launch', {
        brief,
        asset_types: assetTypes,
        brand_guide_id: brandGuideId,
        project_id: projectId,
    }, {
        timeout: 180000, // 3 minutes — creates campaign + generates all assets
    }).then(r => r.data);

export const getCampaignAdjustmentSuggestions = (campaignId: string): Promise<any> =>
    apiClient.get(`/autonomous/campaigns/adjustment-suggestions/${campaignId}`, {
        timeout: 60000,
    }).then(r => r.data);

export const generateCampaignRetrospective = (campaignId: string): Promise<any> =>
    apiClient.post(`/autonomous/campaigns/retrospective/${campaignId}`, {}, {
        timeout: 90000,
    }).then(r => r.data);


// ── H4.4 Autonomous Lead Nurturing API ─────────────────────────────────────────

export const scoreLeads = (projectId?: string): Promise<any> =>
    apiClient.post('/autonomous/leads/score-leads', {}, {
        params: projectId ? { project_id: projectId } : {},
        timeout: 120000,
    }).then(r => r.data);

export const autoEnrollLeads = (
    leadIds: string[],
    sequenceId: string,
    projectId?: string,
): Promise<any> =>
    apiClient.post('/autonomous/leads/auto-enroll', {
        lead_ids: leadIds,
        sequence_id: sequenceId,
        project_id: projectId,
    }, {
        timeout: 60000,
    }).then(r => r.data);

export const getWorkflowSuggestions = (leadId: string, projectId?: string): Promise<any> =>
    apiClient.get(`/autonomous/leads/workflow-suggestions/${leadId}`, {
        params: projectId ? { project_id: projectId } : {},
        timeout: 60000,
    }).then(r => r.data);

export const personaliseSequenceStep = (stepId: string, leadId: string): Promise<any> =>
    apiClient.post('/autonomous/leads/personalise-sequence-step', {
        step_id: stepId,
        lead_id: leadId,
    }, {
        timeout: 60000,
    }).then(r => r.data);

export const getNurtureHealth = (): Promise<any> =>
    apiClient.get('/autonomous/leads/nurture-health', {
        timeout: 30000,
    }).then(r => r.data);

export const reEngageLeads = (inactiveDays: number = 30, projectId?: string): Promise<any> =>
    apiClient.post('/autonomous/leads/re-engage', {
        inactive_days: inactiveDays,
        project_id: projectId,
    }, {
        timeout: 120000,
    }).then(r => r.data);


// ── H4.5 Autonomous Monitoring & Reporting API ──────────────────────────────

export const getPlatformHealth = (): Promise<any> =>
    apiClient.get('/autonomous/monitoring/platform-health', {
        timeout: 30000,
    }).then(r => r.data);

export const detectAnomalies = (): Promise<any> =>
    apiClient.post('/autonomous/monitoring/detect-anomalies', {}, {
        timeout: 60000,
    }).then(r => r.data);

export const generateExecutiveReport = (
    period: string,
    includeSections?: string[],
): Promise<any> =>
    apiClient.post('/autonomous/monitoring/generate-executive-report', {
        period,
        include_sections: includeSections ?? ['campaigns', 'leads', 'content', 'recommendations'],
    }, {
        timeout: 120000,
    }).then(r => r.data);

export const scheduleReport = (settings: object): Promise<any> =>
    apiClient.post('/autonomous/monitoring/schedule-report', settings, {
        timeout: 15000,
    }).then(r => r.data);

export const getReportSchedule = (): Promise<any> =>
    apiClient.get('/autonomous/monitoring/report-schedule', {
        timeout: 15000,
    }).then(r => r.data);

export const getRecentReports = (): Promise<any> =>
    apiClient.get('/autonomous/monitoring/recent-reports', {
        timeout: 15000,
    }).then(r => r.data);


// ── H5.1 Campaign Deployment API ─────────────────────────────────────────────

export interface ChannelStatus {
    name: string;
    status: string;   // queued|scheduled|active|deploying|live|not_connected|failed
    url?: string;
    error?: string;
    note?: string;
}

export interface DeploymentRecord {
    deployment_id: string;
    campaign_id: string;
    channels: ChannelStatus[];
    overall_status: string;  // deploying|live|partial|failed|rolled_back
    deployed_at: string;
}

export const deployCampaign = (campaignId: string): Promise<DeploymentRecord> =>
    apiClient.post(`/deployments/${campaignId}/deploy`).then(r => r.data);

export const getCampaignDeployments = (campaignId: string): Promise<DeploymentRecord[]> =>
    apiClient.get(`/deployments/${campaignId}/deployments`).then(r => r.data);

export const getDeploymentStatus = (deploymentId: string): Promise<DeploymentRecord> =>
    apiClient.get(`/deployments/status/${deploymentId}`).then(r => r.data);

export const rollbackDeployment = (deploymentId: string): Promise<any> =>
    apiClient.post(`/deployments/rollback/${deploymentId}`).then(r => r.data);


// ── H5.2 Domain Registration & DNS Management API ─────────────────────────────

export const searchDomains = (q: string): Promise<any> =>
    apiClient.get(`/domains/search?q=${encodeURIComponent(q)}`).then(r => r.data);

export const registerDomain = (data: object): Promise<any> =>
    apiClient.post('/domains/register', data).then(r => r.data);

export const getDomains = (): Promise<any> =>
    apiClient.get('/domains').then(r => r.data);

export const getDomain = (id: string): Promise<any> =>
    apiClient.get(`/domains/${id}`).then(r => r.data);

export const updateDomain = (id: string, data: object): Promise<any> =>
    apiClient.patch(`/domains/${id}`, data).then(r => r.data);

export const deleteDomain = (id: string): Promise<any> =>
    apiClient.delete(`/domains/${id}`).then(r => r.data);

export const addDnsRecord = (id: string, record: object): Promise<any> =>
    apiClient.post(`/domains/${id}/dns`, record).then(r => r.data);

export const deleteDnsRecord = (id: string, recordId: string): Promise<any> =>
    apiClient.delete(`/domains/${id}/dns/${recordId}`).then(r => r.data);

export const pointDomainToAime = (id: string): Promise<any> =>
    apiClient.post(`/domains/${id}/point-to-aime`).then(r => r.data);

export const pointDomainToWordPress = (id: string): Promise<any> =>
    apiClient.post(`/domains/${id}/point-to-wordpress`).then(r => r.data);

export const setupEmailRouting = (id: string, data: { address?: string; forward_to?: string }): Promise<any> =>
    apiClient.post(`/domains/${id}/email-routing`, data).then(r => r.data);

export const getEmailRoutingStatus = (id: string): Promise<any> =>
    apiClient.get(`/domains/${id}/email-routing`).then(r => r.data);

export const disableEmailRouting = (id: string): Promise<any> =>
    apiClient.delete(`/domains/${id}/email-routing`).then(r => r.data);


// ── Custom Domains (LP hosting on client FQDN) ─────────────────────────────

export const getCustomDomains = (projectId: string): Promise<any[]> =>
    apiClient.get(`/custom-domains?project_id=${projectId}`).then(r => r.data);

export const addCustomDomain = (data: { domain: string; project_id: string; method: string; landing_page_id?: string }): Promise<any> =>
    apiClient.post('/custom-domains', data).then(r => r.data);

export const updateCustomDomain = (id: string, data: Record<string, any>): Promise<any> =>
    apiClient.patch(`/custom-domains/${id}`, data).then(r => r.data);

export const deleteCustomDomain = (id: string): Promise<any> =>
    apiClient.delete(`/custom-domains/${id}`).then(r => r.data);

export const verifyCustomDomain = (id: string): Promise<any> =>
    apiClient.post(`/custom-domains/${id}/verify`).then(r => r.data);

export const getCustomDomainStatus = (id: string): Promise<any> =>
    apiClient.get(`/custom-domains/${id}/status`).then(r => r.data);

export const provisionCloudflare = (id: string): Promise<any> =>
    apiClient.post(`/custom-domains/${id}/provision-cloudflare`).then(r => r.data);

export const checkCustomDomainSSL = (id: string): Promise<any> =>
    apiClient.post(`/custom-domains/${id}/check-ssl`).then(r => r.data);


// ── H5.3 Cloudflare Pages — Landing Page Deploy API ──────────────────────────

export const deployLandingPage = (id: string): Promise<any> =>
    apiClient.post(`/landing-pages/${id}/deploy`).then(r => r.data);

export const getLandingPageDeployStatus = (id: string): Promise<any> =>
    apiClient.get(`/landing-pages/${id}/deploy-status`).then(r => r.data);

export const undeployLandingPage = (id: string): Promise<any> =>
    apiClient.delete(`/landing-pages/${id}/deploy`).then(r => r.data);


// ── H5.4 A/B Test Lifecycle API ───────────────────────────────────────────────

export const deployABVariants = (testId: string): Promise<any> =>
    apiClient.post(`/lead-capture/ab-tests/${testId}/deploy-variants`).then(r => r.data);

export const startABTest = (testId: string, trafficSplit?: number): Promise<any> =>
    apiClient.post(`/lead-capture/ab-tests/${testId}/start`, { traffic_split: trafficSplit ?? 50 }).then(r => r.data);

export const getABTestResults = (testId: string): Promise<any> =>
    apiClient.get(`/lead-capture/ab-tests/${testId}/results`).then(r => r.data);

export const promoteABWinner = (testId: string, variant: 'a' | 'b'): Promise<any> =>
    apiClient.post(`/lead-capture/ab-tests/${testId}/promote/${variant}`).then(r => r.data);

export const updateABTestAutoPromote = (testId: string, autoPromote: boolean): Promise<any> =>
    apiClient.patch(`/lead-capture/ab-tests/${testId}`, { auto_promote: autoPromote }).then(r => r.data);


// ── H5.5 WordPress Deployment & Plugin ───────────────────────────────────────

export const getWordPressPlans = (): Promise<any> =>
    apiClient.get('/wordpress/plans').then(r => r.data);

export const createWordPressSite = (data: object): Promise<any> =>
    apiClient.post('/wordpress/sites', data).then(r => r.data);

export const getWordPressSites = (): Promise<any> =>
    apiClient.get('/wordpress/sites').then(r => r.data);

export const getPluginWordPressSites = (ownerId?: string): Promise<any[]> =>
    apiClient.get('/wordpress/sites').then(r =>
        (r.data || []).filter((s: any) => s.connection_type === 'plugin' && s.status === 'active')
    );

export const getWordPressSite = (id: string): Promise<any> =>
    apiClient.get(`/wordpress/sites/${id}`).then(r => r.data);

export const deleteWordPressSite = (id: string): Promise<any> =>
    apiClient.delete(`/wordpress/sites/${id}`).then(r => r.data);

export const deployWordPressWebsite = (id: string, projectId?: string): Promise<any> =>
    apiClient.post(`/wordpress/sites/${id}/deploy-website`, { project_id: projectId }).then(r => r.data);

export const updateWordPressPage = (id: string, pageTitle: string, content: string): Promise<any> =>
    apiClient.post(`/wordpress/sites/${id}/update-page`, { page_title: pageTitle, content }).then(r => r.data);

export const bindWordPressDomain = (id: string, domain: string): Promise<any> =>
    apiClient.post(`/wordpress/sites/${id}/bind-domain`, { domain_name: domain }).then(r => r.data);

export const getWordPressPluginKey = (): Promise<any> =>
    apiClient.get('/wordpress/plugin/api-key').then(r => r.data);

export const generateWordPressPluginKey = (): Promise<any> =>
    apiClient.post('/wordpress/plugin/generate-key').then(r => r.data);

export const connectWordPressPlugin = (data: object): Promise<any> =>
    apiClient.post('/wordpress/plugin/connect', data).then(r => r.data);

// /wordpress/plugin/download requires the Authorization header (no session
// cookie — JWT lives in localStorage), so it can't be a plain <a href> or
// window.open() target: the browser sends no auth on a bare navigation and
// the endpoint 401s ("Not authenticated"). Fetch as a blob via apiClient
// (which attaches the header) and trigger the save client-side instead.
export const downloadWordPressPlugin = (): Promise<Blob> =>
    apiClient.get('/wordpress/plugin/download', { responseType: 'blob' }).then(r => r.data);

// Optional companion plugin — only needed on sites whose theme/page-builder
// (e.g. YOOtheme Pro) binds its content element to an ACF field instead of
// the native WordPress content field. Same auth-header requirement as above.
export const downloadAcfSyncPlugin = (): Promise<Blob> =>
    apiClient.get('/wordpress/plugin/acf-sync/download', { responseType: 'blob' }).then(r => r.data);


// ── H5.6 Social Accounts & Twilio 2FA ────────────────────────────────────────

// Twilio 2FA sessions
export const createTwoFASession = (data: object): Promise<any> =>
    apiClient.post('/twilio/sessions', data).then(r => r.data);

export const getTwoFASession = (sessionId: string): Promise<any> =>
    apiClient.get(`/twilio/sessions/${sessionId}`).then(r => r.data);

export const completeTwoFASession = (sessionId: string): Promise<any> =>
    apiClient.post(`/twilio/sessions/${sessionId}/complete`).then(r => r.data);

// Social accounts
export const getSocialAccounts = (projectId?: string): Promise<any> =>
    apiClient.get('/social/accounts', { params: projectId ? { project_id: projectId } : {} }).then(r => r.data);

export const createSocialAccount = (data: object): Promise<any> =>
    apiClient.post('/social/accounts', data).then(r => r.data);

export const updateSocialAccount = (id: string, data: object): Promise<any> =>
    apiClient.patch(`/social/accounts/${id}`, data).then(r => r.data);

export const deleteSocialAccount = (id: string): Promise<any> =>
    apiClient.delete(`/social/accounts/${id}`).then(r => r.data);

export const applySocialBranding = (id: string): Promise<any> =>
    apiClient.post(`/social/accounts/${id}/apply-branding`).then(r => r.data);

export interface ApplyBrandingAllResult {
    account_id: string;
    platform: string;
    applied?: boolean;
    reason?: string | null;
    error?: string;
    status?: string;
    field_results?: Record<string, any>;
}

export const applyAllSocialBranding = (projectId?: string): Promise<ApplyBrandingAllResult[]> =>
    apiClient.post('/social/accounts/apply-branding-all', null, { params: projectId ? { project_id: projectId } : {} }).then(r => r.data);

export const getSocialBrandingPreview = (id: string): Promise<any> =>
    apiClient.get(`/social/accounts/${id}/branding-preview`).then(r => r.data);

// ---------------------------------------------------------------------------
// Facebook Page Branding — draft model, resolution, capability check,
// and live push (specs/completed/facebook-page-branding-live-push.md).
// All 10 phases have shipped — pushBrandingDraft below IS wired to a
// button (FacebookBrandingModal's "Push to Facebook"); this comment
// previously described an earlier Phase-1B-only state. See I10 in
// specs/completed/facebook-page-branding-live-push-code-review.md.
// ---------------------------------------------------------------------------

export interface BrandingCapability {
    can_push: boolean;
    reason: string | null;
    missing_scopes: string[];
    granted_scopes: string[];
    checked_at?: string;
}

export interface BrandingFieldResult {
    ok: boolean;
    skipped: boolean;
    reason: string | null;
    detail: string | null;
    graph_id: string | null;
    at: string | null;
}

// ---------------------------------------------------------------------------
// Structured address (Phase 5A) — client_info.address_structured shape.
// `source` is the single enum that decides Graph-pushability downstream
// (address_structuring.is_pushable) — typed as a four-value union, not
// `string`, so a typo in a source value fails the build rather than
// silently failing is_pushable() in production.
// ---------------------------------------------------------------------------
export interface AddressStructured {
    formatted_address: string | null;
    street_address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    country_code: string | null;
    latitude: number | null;
    longitude: number | null;
    place_id: string | null;
    source: 'places_autocomplete' | 'backfill_places' | 'manual_entry' | 'needs_manual_review';
    confirmed_at: string | null;
    updated_at: string | null;
}

export interface BrandingDraft {
    draft_id: string;
    account_id: string;
    project_id: string | null;
    platform: string;
    bio_text: string | null;
    bio_source: 'existing_brand_data' | 'gemini' | 'fallback' | 'user_edited';
    bio_source_field: string | null;
    bio_text_generated: string | null;
    about_long_text: string | null;
    about_long_source: string | null;
    about_long_source_field: string | null;
    replace_page_description: boolean;
    contact_phone: string | null;
    contact_email: string | null;
    contact_website: string | null;
    contact_source: 'project_settings' | 'none';
    replace_page_emails: boolean;
    // Phase 5C — the contact_address snapshot (verbatim, including its own
    // `source`/`confirmed_at`) and the location replace-guard opt-in.
    contact_address: AddressStructured | null;
    // Computed server-side from the SNAPSHOT (address_structuring.is_pushable
    // on contact_address), never from live project_contact — see
    // specs/completed/fb-branding-address-hours-fix.md F2. The modal must
    // gate on these, not on project_contact.address_pushable.
    contact_address_pushable: boolean;
    contact_address_block_reason: string | null;
    replace_page_location: boolean;
    // Phase 5 — mirrors contact_address/contact_address_pushable/
    // replace_page_location above, for the structured hours snapshot.
    contact_hours: OfficeHoursStructured | null;
    contact_hours_pushable: boolean;
    contact_hours_block_reason: string | null;
    replace_page_hours: boolean;
    logo_mark_url: string | null;
    profile_image_source: 'draft_upload' | 'brand_logo_mark' | 'gemini' | 'none';
    profile_image_url: string | null;
    banner_image_url: string | null;
    // Cover-photo steering + logo overlay follow-up. banner_base_image_url
    // is ALWAYS the clean pre-overlay AI output — banner_image_url is the
    // field actually intended for push (base, or base+overlay).
    banner_base_image_url: string | null;
    profile_image_prompt: string | null;
    banner_image_prompt: string | null;
    banner_prompt_guidance: string | null;
    cover_logo_overlay: boolean;
    cover_logo_variant: 'primary' | 'dark_bg' | 'monochrome_white' | 'monochrome_black' | 'logo_mark' | null;
    status: 'draft' | 'pending_approval' | 'pushing' | 'applied' | 'partial' | 'failed' | 'discarded' | 'expired';
    field_results: Record<string, BrandingFieldResult> | null;
    last_error: string | null;
    created_at: string;
    expires_at: string | null;
}

// Structured business hours (Phase 3) — client_info.office_hours_structured
// shape. All seven day keys are always present; [] means closed that day.
// Mirrors business_hours.py's storage contract exactly — see that module
// for the validation/pushability rules this type does not itself enforce.
export interface OfficeHoursStructured {
    version: 1;
    days: Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', Array<{ open: string; close: string }>>;
    updated_at: string | null;
}

export interface BrandingProjectContact {
    project_id: string | null;
    project_name: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    address_structured?: AddressStructured | null;
    address_pushable?: boolean;
    address_block_reason?: string | null;
    office_hours: string | null;
    office_hours_structured?: OfficeHoursStructured | null;
    website: string | null;
    source: 'project_settings' | 'none';
}

export interface BrandingDraftResponse {
    draft: BrandingDraft;
    capability: BrandingCapability;
    quota_reason: string | null;
    brand_assets: {
        logo_url: string | null;
        favicon_url: string | null;
        logo_mark_url: string | null;
        logo_dark_bg_url: string | null;
        logo_monochrome_white_url: string | null;
        logo_monochrome_black_url: string | null;
        brand_guide_id: string | null;
        can_edit_brand_guide: boolean;
    };
    project_contact: BrandingProjectContact | null;
    page_current: {
        about: string | null;
        description: string | null;
        phone: string | null;
        emails: string[];
        location: Record<string, any> | null;
        hours: Record<string, any> | null;
        website: string | null;
    } | null;
    // Push-field keys ('phone'|'emails'|'website'|'location'|'hours') whose
    // value on the draft's snapshot differs from LIVE project_contact right
    // now — the F2 stale-draft banner's data source.
    contact_snapshot_changed?: Array<'phone' | 'emails' | 'website' | 'location' | 'hours'>;
}

export const getBrandingCapability = (accountId: string): Promise<BrandingCapability & { account_id: string }> =>
    apiClient.get(`/social/accounts/${accountId}/branding-capability`).then(r => r.data);

// createBrandingDraft can run up to two server-side image generations —
// override the 120s apiClient default (CRM /suggest precedent, CLAUDE.md).
export const createBrandingDraft = (accountId: string): Promise<BrandingDraftResponse> =>
    apiClient.post(`/social/accounts/${accountId}/branding-draft`, {}, { timeout: 180000 }).then(r => r.data);

// getBrandingDraft now returns the same real capability/brand_assets/
// project_contact/page_current shape createBrandingDraft does (all null
// when there is no draft to reopen) — see B2 in the 2026-09-17 code
// review; reopening an existing draft used to fabricate these client-side.
export const getBrandingDraft = (accountId: string): Promise<Partial<BrandingDraftResponse> & { draft: BrandingDraft | null }> =>
    apiClient.get(`/social/accounts/${accountId}/branding-draft`).then(r => r.data);

export const updateBrandingDraft = (
    draftId: string,
    patch: Partial<{
        bio_text: string;
        bio_source: 'gemini' | 'user_edited';
        about_long_text: string;
        replace_page_description: boolean;
        replace_page_emails: boolean;
        replace_page_location: boolean;
        replace_page_hours: boolean;
        logo_mark_url: string;
        profile_image_source: BrandingDraft['profile_image_source'];
        save_to_brand_guide: boolean;
        cover_logo_overlay: boolean;
        cover_logo_variant: BrandingDraft['cover_logo_variant'];
    }>,
): Promise<{ draft: BrandingDraft; saved_to_brand_guide: boolean; reason: string | null }> =>
    apiClient.patch(`/social/branding-drafts/${draftId}`, patch).then(r => r.data);

// Scoped cover-photo-only regenerate (steering guidance + optional logo
// overlay) — a real Gemini call, so it gets the same extended timeout as
// createBrandingDraft, but only ever spends ONE image generation (never
// also regenerates the profile picture).
export const regenerateBrandingCover = (
    draftId: string,
    patch: Partial<{
        banner_prompt_guidance: string;
        cover_logo_overlay: boolean;
        cover_logo_variant: BrandingDraft['cover_logo_variant'];
    }>,
): Promise<{ draft: BrandingDraft; quota_reason: string | null }> =>
    apiClient.post(`/social/branding-drafts/${draftId}/regenerate-cover`, patch, { timeout: 180000 }).then(r => r.data);

// Phase 3 (picture/cover/about/phone/emails) + Phase 5C (location) +
// website (facebook-page-branding-live-push follow-up) + Phase 5 (hours,
// fb-branding-address-hours-fix) — kept in lockstep with the backend
// PushBrandingRequest.fields Literal.
export const pushBrandingDraft = (
    draftId: string,
    fields: Array<'picture' | 'cover' | 'about' | 'phone' | 'emails' | 'location' | 'hours' | 'website'>,
): Promise<any> =>
    apiClient.post(`/social/branding-drafts/${draftId}/push`, { fields }).then(r => r.data);

export const discardBrandingDraft = (draftId: string): Promise<any> =>
    apiClient.delete(`/social/branding-drafts/${draftId}`).then(r => r.data);

// "Use latest details" — re-snapshots the draft's contact fields from LIVE
// Project Settings. No AI call, no image spend, no Facebook write (unlike
// createBrandingDraft's "Regenerate Images"). Fixes the F2 stale-snapshot
// bug: see specs/completed/fb-branding-address-hours-fix.md.
export const refreshBrandingDraftContact = (
    draftId: string,
): Promise<BrandingDraftResponse & { refreshed_fields: string[] }> =>
    apiClient.post(`/social/branding-drafts/${draftId}/refresh-contact`, {}).then(r => r.data);

// ---------------------------------------------------------------------------
// Google Places (New) — server-side proxy (Phase 5A). GOOGLE_PLACES_API_KEY
// never reaches the browser; these clients only ever see AIME's own
// already-mapped response. Both degrade to `degraded: true` at HTTP 200 on
// any Google-side/config failure — never an error, per the Migration Check /
// meta_leadgen_forms precedent. `placesAutocomplete` overrides the 120s
// apiClient default down to 8s: it's a keystroke-path request, and a stuck
// call would hold a connection slot and stack behind the debounce.
// ---------------------------------------------------------------------------

export const placesAutocomplete = (
    input: string,
    sessionToken: string,
    countryCode?: string,
    signal?: AbortSignal,
): Promise<{ suggestions: Array<{ place_id: string; primary_text: string; secondary_text: string }>; degraded: boolean; reason: string | null }> =>
    apiClient
        .get('/places/autocomplete', {
            params: { input, session_token: sessionToken, ...(countryCode ? { country: countryCode } : {}) },
            timeout: 8000,
            signal,
        })
        .then(r => r.data);

export const placeDetails = (
    placeId: string,
    sessionToken: string,
): Promise<{
    address: AddressStructured | null;
    degraded: boolean;
    reason: string | null;
    // Computed server-side from address_structuring.is_pushable (the same
    // chokepoint push_location re-checks) so a suburb-only pick (no street
    // line) can be flagged immediately, rather than only discovered later
    // in the branding modal. See
    // specs/completed/fb-branding-address-hours-fix.md F4.
    facebook_pushable: boolean;
    facebook_block_reason: string | null;
}> =>
    apiClient
        .get('/places/details', {
            params: { place_id: placeId, session_token: sessionToken },
            timeout: 8000,
        })
        .then(r => r.data);

// Facebook OAuth integration

/**
 * Build the OAuth authorize URL for the given platform, optionally appending
 * `use_flb=true` when the Facebook Login for Business flow is enabled.
 * The URL is opened in a popup by handleOAuthPopup in SocialAccountsModule.
 */
export const buildOAuthPopupUrl = (
    platform: string,
    projectId: string,
    token: string,
    useFlb = false,
): string => {
    const base = `/api/oauth/${platform}/authorize?project_id=${encodeURIComponent(projectId)}&token=${encodeURIComponent(token)}`;
    return useFlb ? `${base}&use_flb=true` : base;
};

export const checkFacebookOAuthConfig = (): Promise<{ configured: boolean }> =>
    apiClient.get('/social/facebook/check-config').then(r => r.data);

export const getFacebookPages = (projectId: string): Promise<{
    pages: Array<{
        page_id: string; name: string; category: string; picture_url: string | null; already_connected: boolean;
        instagram: { instagram_id: string; username: string } | null;
        instagram_already_connected: boolean;
    }>;
    credential_id: string;
}> =>
    apiClient.get('/social/facebook/pages', { params: { project_id: projectId } }).then(r => r.data);

export const connectFacebookPages = (data: { project_id: string; credential_id: string; page_ids: string[] }): Promise<any[]> =>
    apiClient.post('/social/facebook/connect-pages', data).then(r => r.data);

export const resetFacebookCredential = (projectId: string): Promise<{ deleted: number }> =>
    apiClient.delete('/social/facebook/credential', { params: { project_id: projectId } }).then(r => r.data);

// LinkedIn OAuth integration
export const checkLinkedInOAuthConfig = (): Promise<{ configured: boolean; company_pages_enabled: boolean }> =>
    apiClient.get('/social/linkedin/check-config').then(r => r.data);

export const connectLinkedInAccount = (data: { project_id: string; credential_id: string }): Promise<any> =>
    apiClient.post('/social/linkedin/connect', data).then(r => r.data);

export const getLinkedInPages = (projectId: string): Promise<{
    pages: Array<{ id: string; urn: string; name: string; vanity_name: string }>;
    credential_id: string;
}> =>
    apiClient.get('/social/linkedin/pages', { params: { project_id: projectId } }).then(r => r.data);

export const connectLinkedInPages = (data: { project_id: string; credential_id: string; page_ids: Array<{id: string; name: string; vanity_name: string}> }): Promise<any> =>
    apiClient.post('/social/linkedin/connect-pages', data).then(r => r.data);

// Instagram OAuth integration
export const checkInstagramOAuthConfig = (): Promise<{ configured: boolean }> =>
    apiClient.get('/social/instagram/check-config').then(r => r.data);

export const getInstagramAccounts = (projectId: string): Promise<{
    accounts: Array<{ instagram_id: string; username: string; name: string; profile_picture_url: string; followers_count: number; page_name: string; already_connected: boolean }>;
    credential_id: string;
}> =>
    apiClient.get('/social/instagram/accounts', { params: { project_id: projectId } }).then(r => r.data);

export const connectInstagramAccounts = (data: { project_id: string; credential_id: string; page_ids: string[] }): Promise<any[]> =>
    apiClient.post('/social/instagram/connect-accounts', data).then(r => r.data);

// Twitter/X OAuth integration
export const checkTwitterOAuthConfig = (): Promise<{ configured: boolean }> =>
    apiClient.get('/social/twitter/check-config').then(r => r.data);

export const connectTwitterAccount = (data: { project_id: string; credential_id: string }): Promise<any> =>
    apiClient.post('/social/twitter/connect', data).then(r => r.data);

// Social Publishing
export const publishToSocial = (data: {
    task_id: string; platform: string; account_id: string;
    mode: 'now' | 'schedule'; scheduled_for?: string; link?: string;
}): Promise<any> =>
    apiClient.post('/social/publish', data).then(r => r.data);


// ── H5.7 Organic Growth Engine API ───────────────────────────────────────────

export const getOrganicGrowthHealth = (projectId?: string) =>
    apiClient.get('/organic-growth/health', { params: { project_id: projectId } }).then(r => r.data);

export const getCompetitorInsights = (accountId: string) =>
    apiClient.get(`/organic-growth/competitor-insights/${accountId}`).then(r => r.data);

export const getEngagementQueue = (platform?: string, projectId?: string) =>
    apiClient.get('/organic-growth/engagement-queue', { params: { platform, project_id: projectId } }).then(r => r.data);

export const populateEngagementQueue = (accountId: string) =>
    apiClient.post(`/organic-growth/engagement-queue/populate/${accountId}`, {}, { timeout: 90000 }).then(r => r.data);

export const approveEngagementDraft = (draftId: string) =>
    apiClient.post(`/organic-growth/engagement-queue/${draftId}/approve`).then(r => r.data);

export const discardEngagementDraft = (draftId: string) =>
    apiClient.delete(`/organic-growth/engagement-queue/${draftId}`).then(r => r.data);

export const getDMFlows = (projectId?: string) =>
    apiClient.get('/organic-growth/dm-flows', { params: { project_id: projectId } }).then(r => r.data);

export const createDMFlow = (data: object) =>
    apiClient.post('/organic-growth/dm-flows', data).then(r => r.data);

export const updateDMFlow = (id: string, data: object) =>
    apiClient.patch(`/organic-growth/dm-flows/${id}`, data).then(r => r.data);

export const deleteDMFlow = (id: string) =>
    apiClient.delete(`/organic-growth/dm-flows/${id}`).then(r => r.data);

export const getFollowQueue = (projectId?: string) =>
    apiClient.get('/organic-growth/follow-queue', { params: { project_id: projectId } }).then(r => r.data);

export const populateFollowQueue = (accountId: string) =>
    apiClient.post(`/organic-growth/follow-queue/populate/${accountId}`, {}, { timeout: 90000 }).then(r => r.data);

export const executeFollow = (followId: string) =>
    apiClient.post(`/organic-growth/follow-queue/${followId}/execute`).then(r => r.data);

export const skipFollow = (followId: string) =>
    apiClient.post(`/organic-growth/follow-queue/${followId}/skip`).then(r => r.data);

export const getOrganicGrowthAnalytics = (projectId?: string) =>
    apiClient.get('/organic-growth/analytics', { params: { project_id: projectId } }).then(r => r.data);


// ── Dashboard ──────────────────────────────────────────────────────────────────

export interface DashboardSummary {
  kpis: {
    new_leads_this_week: number;
    new_leads_delta_pct: number;
    posts_published_this_week: number;
    active_campaigns: number;
    pending_approvals: number;
  };
  action_alerts: { id: string; type: string; title: string; body: string; link?: string }[];
  lead_trend: { week_label: string; count: number }[];
  top_campaign: { name: string; goal: string; asset_count: number; status: string } | null;
  email_health: { sequence_count: number; has_data: boolean };
  social_follower_growth: { platform: string; count: number }[];
  upcoming_content: { title: string; scheduled_at: string; platform: string; status: string }[];
  platform_status: { domains_active: number; wordpress_sites: number; social_accounts: number };
  ab_tests_active: number;
  agent_autonomy_mode: string;
  setup_progress: {
    brand_guide: boolean;
    website: boolean;
    content: boolean;
    landing_page: boolean;
    campaign: boolean;
    social_account: boolean;
    email_sequence: boolean;
  };
  hero_narrative: string | null;
}

export const getDashboardSummary = async (projectId?: string): Promise<DashboardSummary> => {
  const params = projectId ? { project_id: projectId } : {};
  const r = await apiClient.get('/dashboard/summary', { params });
  return r.data;
};

export const generateDashboardHeroNarrative = async (): Promise<{ narrative: string }> => {
  const r = await apiClient.post('/dashboard/hero-narrative');
  return r.data;
};

// ── Video Generation ────────────────────────────────────────────────────────

export interface VideoJob {
  id: number;
  provider: string;
  model_id: string;
  prompt: string;
  status: 'pending' | 'processing' | 'complete' | 'failed';
  result_url: string | null;
  duration_seconds: number | null;
  width: number | null;
  height: number | null;
  aspect_ratio: string | null;
  error: string | null;
  platform: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface VideoQuota {
  used_this_month: number;
  monthly_limit: number | null;
  tier: string;
  unlimited: boolean;
  productions_used_this_month?: number;
  productions_monthly_limit?: number | null;
  max_scenes_per_production?: number;
}

export const generateVideo = async (data: {
  prompt: string;
  duration?: number;
  aspect_ratio?: string;
  platform?: string;
}): Promise<{ job_id: number; provider: string; status: string }> => {
  const r = await apiClient.post('/video/generate', data);
  return r.data;
};

export const getVideoJob = async (jobId: number): Promise<VideoJob> => {
  const r = await apiClient.get(`/video/jobs/${jobId}`);
  return r.data;
};

export const getVideoJobs = async (limit = 10, offset = 0): Promise<VideoJob[]> => {
  const r = await apiClient.get(`/video/jobs?limit=${limit}&offset=${offset}`);
  return r.data;
};

export const getVideoLibrary = async (limit = 20, offset = 0): Promise<VideoJob[]> => {
  const r = await apiClient.get(`/video/library?limit=${limit}&offset=${offset}`);
  return r.data;
};

export const cancelVideoJob = async (jobId: number): Promise<void> => {
  await apiClient.delete(`/video/jobs/${jobId}`);
};

export const getVideoQuota = async (): Promise<VideoQuota> => {
  const r = await apiClient.get('/video/quota');
  return r.data;
};

// ── Video Productions ─────────────────────────────────────────────────────────

export interface ParseScriptRequest {
  script_id?: string;
  script_text?: string;
  target_scene_duration?: number;
  aspect_ratio?: string;
}

export interface ParsedScene {
  scene_index: number;
  scene_label: string;
  prompt: string;
  visual_hint: string;
  duration: number;
}

export interface ParseScriptResponse {
  scenes: ParsedScene[];
  estimated_duration_seconds: number;
  scene_count: number;
}

export interface CreateProductionRequest {
  title: string;
  script_id?: string;
  scenes: ParsedScene[];
  aspect_ratio: string;
  music_track_key: string;
  color_grade: string;
}

export interface VideoProductionScene {
  scene_index: number;
  scene_label?: string;
  prompt: string;
  visual_hint?: string;
  duration: number;
  status: string;
  clip_url?: string;
  error?: string;
}

export interface VideoProduction {
  id: number;
  title: string;
  status: string;
  total_scenes: number;
  scenes_complete: number;
  aspect_ratio: string;
  music_track_key?: string;
  color_grade?: string;
  final_url?: string;
  error?: string;
  created_at: string;
  completed_at?: string;
  scenes?: VideoProductionScene[];
}

export interface MusicPreset {
  key: string;
  label: string;
  mood: string;
  bpm: number | null;
}

export interface ColorGrade {
  key: string;
  label: string;
}

export const parseScript = (data: ParseScriptRequest): Promise<ParseScriptResponse> =>
  apiClient.post('/video/productions/parse-script', data).then(r => r.data);

export const createProduction = (data: CreateProductionRequest): Promise<{ production_id: number; status: string; scene_count: number }> =>
  apiClient.post('/video/productions', data).then(r => r.data);

export const listProductions = (params?: { limit?: number; offset?: number }): Promise<VideoProduction[]> =>
  apiClient.get('/video/productions', { params }).then(r => r.data);

export const getProduction = (id: number): Promise<VideoProduction> =>
  apiClient.get(`/video/productions/${id}`).then(r => r.data);

export const cancelProduction = (id: number): Promise<void> =>
  apiClient.delete(`/video/productions/${id}`).then(r => r.data);

export const retryFailedScenes = (id: number): Promise<{ retried: number }> =>
  apiClient.post(`/video/productions/${id}/retry-failed-scenes`).then(r => r.data);

export const getMusicPresets = (): Promise<MusicPreset[]> =>
  apiClient.get('/video/productions/music-presets').then(r => r.data);

export const getColorGrades = (): Promise<ColorGrade[]> =>
  apiClient.get('/video/productions/color-grades').then(r => r.data);

// ── Products (Marketplace / Seller) ──────────────────────────────────────────

export interface Product {
  product_id: string;
  name: string;
  description?: string;
  category?: string;
  price?: number;
  status?: string;
  is_lead_magnet?: boolean;
  created_at?: string;
  // Whitepaper fields (specs/completed/whitepaper-module.md) — present on
  // every list_products() row; used to filter the lp_whitepaper product
  // picker down to whitepaper-backed products (source_task_id != null).
  source_task_id?: string | null;
  whitepaper_gated?: boolean;
  cover_image_url?: string;
}

export const getProducts = (params?: { status?: string; is_lead_magnet?: boolean }): Promise<Product[]> =>
  apiClient.get('/products/', { params }).then(r => r.data);

// ── Revenue Analytics ─────────────────────────────────────────────────────────

export const getRevenueSummary = (): Promise<any> =>
  apiClient.get('/analytics/products/summary').then(r => r.data);

export const getRevenueByProduct = (): Promise<any[]> =>
  apiClient.get('/analytics/products/by-product').then(r => r.data);

export const getRevenueTimeline = (period: 'week' | 'month' | 'year'): Promise<any[]> =>
  apiClient.get('/analytics/products/timeline', { params: { period } }).then(r => r.data);

export const getTopBuyers = (): Promise<any[]> =>
  apiClient.get('/analytics/products/top-buyers').then(r => r.data);

// ── Campaign Products ─────────────────────────────────────────────────────────

export const getCampaignProducts = (campaignId: string): Promise<Product[]> =>
  apiClient.get(`/campaigns/${campaignId}/products`).then(r => r.data);

export const linkProductToCampaign = (campaignId: string, productId: string): Promise<void> =>
  apiClient.post(`/campaigns/${campaignId}/products`, { product_id: productId }).then(r => r.data);

export const unlinkProductFromCampaign = (campaignId: string, productId: string): Promise<void> =>
  apiClient.delete(`/campaigns/${campaignId}/products/${productId}`).then(r => r.data);

// ── Whitepaper generation (specs/completed/whitepaper-module.md, Phase 3) ────
// Shapes verified against the live backend (product_routes.py / models.py),
// not the spec's abbreviated JSON examples — see WhitepaperGenerateRequest /
// WhitepaperImageOptions / WhitepaperInfo in models.py.

export interface WhitepaperImageOptions {
  mode: 'none' | 'reuse' | 'generate';
  count: number; // 0-3
  include_cover: boolean;
}

export interface WhitepaperGenerateRequest {
  task_id: string;
  project_id?: string | null;
  brand_guide_id?: string | null;
  product_id?: string | null; // non-null => regenerate-into-existing
  title?: string | null;
  subtitle?: string | null;
  author?: string | null;
  branded: boolean;
  layout: 'report';
  cover_style?: string | null;
  include_toc: boolean;
  include_cta_page: boolean;
  cta_text?: string | null;
  cta_url?: string | null;
  include_checklist: boolean;
  checklist_position: 'after_body' | 'before_cta';
  force_checklist?: boolean; // request-only, never persisted
  images: WhitepaperImageOptions;
  gated: boolean;
}

export interface WhitepaperFileInfo {
  file_id: string;
  original_filename: string;
  file_size?: number | null;
  file_type?: string | null;
  mime_type?: string | null;
  page_count?: number | null;
}

/** POST /whitepapers/generate and POST /{id}/whitepaper/regenerate response —
 *  the flat "generate response" shape, NOT the full product detail blob. */
export interface WhitepaperGenerateResponse {
  product_id: string;
  name: string;
  slug: string;
  category: string;
  product_type: string;
  is_lead_magnet: boolean;
  whitepaper_gated: boolean;
  status: string;
  generation_status: string | null;
  generation_error?: string | null;
  generated_at: string | null;
  source_task_id: string | null;
  checklist_status: 'ready' | 'empty' | 'failed' | null;
  file: WhitepaperFileInfo | null;
}

export interface WhitepaperChecklistItem {
  text: string;
  detail?: string | null;
}

export interface WhitepaperChecklist {
  title?: string;
  intro?: string;
  items: WhitepaperChecklistItem[];
  _error?: string;
}

/** GET /{id}/whitepaper response. */
export interface WhitepaperInfo {
  source_task_id: string | null;
  generation_status: string | null;
  generation_error: string | null;
  whitepaper_options: (Record<string, any> & { checklist?: WhitepaperChecklist | null }) | null;
  checklist_status: 'ready' | 'empty' | 'failed' | null;
  file: WhitepaperFileInfo | null;
  generated_at: string | null;
}

export interface EligibleArticle {
  task_id: string;
  name: string;
  word_count: number;
  created_at: string | null;
  stream: string | null;
}

export const listEligibleArticles = (projectId: string): Promise<EligibleArticle[]> =>
  apiClient
    .get('/products/whitepapers/eligible-articles', { params: { project_id: projectId } })
    .then(r => r.data);

// 300s timeout — matches nginx /api proxy_read_timeout and the real
// Playwright+Gemini+image-generation latency (same override pattern as
// crmImportApi.suggest() / edm sync-to-crm / seo content-plan generate).
export const generateWhitepaper = (body: WhitepaperGenerateRequest): Promise<WhitepaperGenerateResponse> =>
  apiClient.post('/products/whitepapers/generate', body, { timeout: 300000 }).then(r => r.data);

export const regenerateWhitepaper = (
  productId: string,
  forceChecklist: boolean = false
): Promise<WhitepaperGenerateResponse> =>
  apiClient
    .post(`/products/${productId}/whitepaper/regenerate`, { force_checklist: forceChecklist }, { timeout: 300000 })
    .then(r => r.data);

export const getWhitepaper = (productId: string): Promise<WhitepaperInfo> =>
  apiClient.get(`/products/${productId}/whitepaper`).then(r => r.data);

/** Returns the raw HTML string the PDF would be rendered from — render it in
 *  an iframe via srcDoc. Nothing is persisted by this call. */
export const previewWhitepaper = (productId: string): Promise<string> =>
  apiClient.get(`/products/${productId}/whitepaper/preview`, { responseType: 'text' }).then(r => r.data);

// Seller-authenticated download, regardless of gating/publish state (the
// public /shop/whitepaper/{id}/download route only serves a published,
// ungated product). Requires the Authorization header, so — same reasoning
// as downloadWordPressPlugin above — this can't be a plain window.open()
// target; fetch as a blob and trigger the save client-side.
export const downloadWhitepaperAsSeller = (productId: string): Promise<Blob> =>
  apiClient.get(`/products/${productId}/whitepaper/download`, { responseType: 'blob' }).then(r => r.data);

// -- Campaign Variant Grouping & Promotion --

export const getGroupedAssets = (campaignId: string): Promise<{
    grouped: Record<string, { asset_type: string; variant_group: string; variants: CampaignAsset[] }>;
    ungrouped: CampaignAsset[];
}> => apiClient.get(`/campaigns/${campaignId}/assets/grouped`).then(r => r.data);

export const promoteVariant = (campaignId: string, assetId: string): Promise<{
    promoted: CampaignAsset;
    archived_count: number;
}> => apiClient.post(`/campaigns/${campaignId}/assets/${assetId}/promote`).then(r => r.data);

export const unlinkVariantGroup = (campaignId: string, variantGroup: string): Promise<{
    unlinked_count: number;
    revived_count: number;
}> => apiClient.post(`/campaigns/${campaignId}/assets/groups/${variantGroup}/unlink`).then(r => r.data);

// -- Email Sequence Execution --

export const enrollRecipient = (sequenceId: string, data: {
    recipient_email: string;
    recipient_name?: string;
    recipient_data?: any;
}): Promise<any> => apiClient.post(`/campaigns/sequences/${sequenceId}/enroll`, data).then(r => r.data);

export const enrollBatch = (sequenceId: string, recipients: Array<{
    recipient_email: string;
    recipient_name?: string;
}>): Promise<any> => apiClient.post(`/campaigns/sequences/${sequenceId}/enroll-batch`, { recipients }).then(r => r.data);

export const getEnrollments = (sequenceId: string, page?: number): Promise<any> =>
    apiClient.get(`/campaigns/sequences/${sequenceId}/enrollments`, { params: { page: page || 1 } }).then(r => r.data);

export const getSequenceStats = (sequenceId: string): Promise<any> =>
    apiClient.get(`/campaigns/sequences/${sequenceId}/stats`).then(r => r.data);

export const activateSequence = (sequenceId: string): Promise<{
    sequence_id: string;
    status: string;
    smtp_warning?: string;
}> => apiClient.post(`/campaigns/sequences/${sequenceId}/activate`).then(r => r.data);

// -- Sending Ramp Policy (Item 4) --

export const getSendingRampPolicy = (): Promise<SendingRampPolicy> =>
    apiClient.get('/sending/ramp-policy').then(r => r.data);

export const updateSendingRampPolicy = (data: Partial<SendingRampPolicy>): Promise<SendingRampPolicy> =>
    apiClient.patch('/sending/ramp-policy', data).then(r => r.data);

// -- Project SMTP Config (Item 1) --

export const getProjectSmtp = (projectId: string): Promise<ProjectEmailConfig> =>
    apiClient.get(`/sending/project-smtp/${projectId}`).then(r => r.data);

export const createProjectSmtp = (data: {
    project_id: string;
    smtp_server: string;
    smtp_port?: number;
    smtp_user: string;
    smtp_password: string;
    smtp_security?: string;
    from_name?: string;
    from_address?: string;
    reply_to?: string;
}): Promise<ProjectEmailConfig> =>
    apiClient.post('/sending/project-smtp', data).then(r => r.data);

export const updateProjectSmtp = (projectId: string, data: {
    smtp_server?: string;
    smtp_port?: number;
    smtp_user?: string;
    smtp_password?: string;
    smtp_security?: string;
    from_name?: string;
    from_address?: string;
    reply_to?: string;
    is_active?: boolean;
}): Promise<ProjectEmailConfig> =>
    apiClient.patch(`/sending/project-smtp/${projectId}`, data).then(r => r.data);

export const testProjectSmtp = (projectId: string): Promise<{ success: boolean; message: string }> =>
    apiClient.post(`/sending/project-smtp/${projectId}/test`).then(r => r.data);

export const deleteProjectSmtp = (projectId: string): Promise<void> =>
    apiClient.delete(`/sending/project-smtp/${projectId}`).then(() => undefined);

// -- Outbound SMS (specs/completed/outbound-sms-twilio-mobilemessage.md) --

export interface SmsProviderInfo {
    key: string;
    supports_alphanumeric_sender: boolean;
    supports_reply: boolean;
    supports_scheduling: boolean;
    max_recipients_per_request: number;
    is_resolved: boolean;
}

export interface SmsConfig {
    exists: boolean;
    provider: string;
    account_ref: string | null;
    has_credential: boolean;
    credential_hint: string | null;
    sender_id: string | null;
    from_number: string | null;
    verified: boolean;
    is_active: boolean;
    source: 'project' | 'user' | 'env' | 'stub';
}

export interface SmsConfigUpsert {
    provider: string;
    account_ref?: string;
    credential?: string;   // blank = leave unchanged
    sender_id?: string;
    from_number?: string;
    is_active?: boolean;
}

export interface SmsSendLogItem {
    send_log_id: string;
    to_masked: string;
    provider: string;
    status: string;
    provider_message_id: string | null;
    cost_cents: number | null;
    error: string | null;
    created_at: string | null;
}

export interface SmsOptOutItem {
    opt_out_id: string;
    phone_e164: string;
    source: string;
    keyword: string | null;
    provider: string | null;
    opted_out_at: string | null;
    notes: string | null;
    is_global: boolean;
}

export interface SmsThrottlePolicy {
    policy_id: string;
    user_id: string;
    initial_batch_size: number;
    batch_increment: number;
    batch_interval_minutes: number;
    max_batch_size: number;
    hourly_cap: number | null;
    daily_cap: number | null;
    current_batch_level: number;
    last_batch_at: string | null;
    cooldown_days: number;
}

export const smsApi = {
    listProviders: (): Promise<{ providers: SmsProviderInfo[]; resolved_source: string }> =>
        apiClient.get('/sms/providers').then(r => r.data),

    getConfig: (): Promise<SmsConfig> => apiClient.get('/sms/config').then(r => r.data),
    saveConfig: (data: SmsConfigUpsert): Promise<SmsConfig> =>
        apiClient.post('/sms/config', data).then(r => r.data),
    deleteConfig: (): Promise<void> => apiClient.delete('/sms/config').then(() => undefined),

    getProjectConfig: (projectId: string): Promise<SmsConfig> =>
        apiClient.get(`/sms/config/project/${projectId}`).then(r => r.data),
    saveProjectConfig: (projectId: string, data: SmsConfigUpsert): Promise<SmsConfig> =>
        apiClient.post(`/sms/config/project/${projectId}`, data).then(r => r.data),
    deleteProjectConfig: (projectId: string): Promise<void> =>
        apiClient.delete(`/sms/config/project/${projectId}`).then(() => undefined),

    testSend: (data: { to: string; body: string; project_id?: string | null }): Promise<{
        ok: boolean; send_log_id: string; provider: string;
        provider_message_id: string | null; status: string; stub: boolean;
    }> => apiClient.post('/sms/test-send', data, { timeout: 30000 }).then(r => r.data),

    listLogs: (limit = 50, offset = 0): Promise<{ items: SmsSendLogItem[]; total: number }> =>
        apiClient.get('/sms/logs', { params: { limit, offset } }).then(r => r.data),

    listOptOuts: (limit = 50, offset = 0): Promise<{ items: SmsOptOutItem[]; total: number }> =>
        apiClient.get('/sms/opt-outs', { params: { limit, offset } }).then(r => r.data),
    createOptOut: (data: { phone: string; reason?: string }): Promise<SmsOptOutItem> =>
        apiClient.post('/sms/opt-outs', data).then(r => r.data),
    deleteOptOut: (optOutId: string): Promise<void> =>
        apiClient.delete(`/sms/opt-outs/${optOutId}`).then(() => undefined),

    getThrottlePolicy: (): Promise<SmsThrottlePolicy> =>
        apiClient.get('/sms/throttle-policy').then(r => r.data),
    updateThrottlePolicy: (data: Partial<SmsThrottlePolicy>): Promise<SmsThrottlePolicy> =>
        apiClient.patch('/sms/throttle-policy', data).then(r => r.data),
};

// -- Email A/B Tests --

export const createEmailABTest = (sequenceId: string, data: {
    sequence_id: string;
    step_index: number;
    variant_b_subject?: string;
    variant_b_body_html?: string;
    variant_b_body_plain?: string;
    traffic_split?: number;
    auto_promote?: boolean;
}): Promise<any> => apiClient.post(`/campaigns/sequences/${sequenceId}/ab-tests`, data).then(r => r.data);

export const getEmailABTests = (sequenceId: string): Promise<any[]> =>
    apiClient.get(`/campaigns/sequences/${sequenceId}/ab-tests`).then(r => r.data);

export const updateEmailABTest = (testId: string, data: any): Promise<any> =>
    apiClient.patch(`/campaigns/ab-tests/${testId}`, data).then(r => r.data);

export const getEmailABResults = (testId: string): Promise<any> =>
    apiClient.get(`/campaigns/ab-tests/${testId}/results`).then(r => r.data);

export const promoteEmailABWinner = (testId: string, winner: string): Promise<any> =>
    apiClient.post(`/campaigns/ab-tests/${testId}/promote`, null, { params: { winner } }).then(r => r.data);

// -- Auto-Scale --

export const enableAutoScale = (campaignId: string, config?: any): Promise<any> =>
    apiClient.post(`/autonomous/campaigns/${campaignId}/auto-scale/enable`, config).then(r => r.data);

export const disableAutoScale = (campaignId: string): Promise<any> =>
    apiClient.post(`/autonomous/campaigns/${campaignId}/auto-scale/disable`).then(r => r.data);

export const getCampaignHealth = (campaignId: string): Promise<any> =>
    apiClient.get(`/autonomous/campaigns/${campaignId}/health`).then(r => r.data);

export const iterateCampaign = (campaignId: string, winningAssetId?: string): Promise<any> =>
    apiClient.post(`/autonomous/campaigns/${campaignId}/iterate`, null, { params: { winning_asset_id: winningAssetId } }).then(r => r.data);

// -- Ad Platform Integrations --

export const getAdConnections = (): Promise<any[]> =>
    apiClient.get('/ad-platforms/connections').then(r => r.data);

export const connectMetaAds = (data?: { account_id?: string; account_name?: string }): Promise<any> =>
    apiClient.post('/ad-platforms/connections/meta', data || {}).then(r => r.data);

export const connectGoogleAds = (data?: { account_id?: string; account_name?: string }): Promise<any> =>
    apiClient.post('/ad-platforms/connections/google', data || {}).then(r => r.data);

export const disconnectAdPlatform = (connectionId: string): Promise<void> =>
    apiClient.delete(`/ad-platforms/connections/${connectionId}`).then(r => r.data);

export const pushToMeta = (campaignId: string, data?: any): Promise<any> =>
    apiClient.post(`/ad-platforms/campaigns/${campaignId}/meta/push`, data || {}).then(r => r.data);

export const pushToGoogle = (campaignId: string, data?: any): Promise<any> =>
    apiClient.post(`/ad-platforms/campaigns/${campaignId}/google/push`, data || {}).then(r => r.data);

export const getMetaPerformance = (campaignId: string): Promise<any> =>
    apiClient.get(`/ad-platforms/campaigns/${campaignId}/meta/performance`).then(r => r.data);

export const getGooglePerformance = (campaignId: string): Promise<any> =>
    apiClient.get(`/ad-platforms/campaigns/${campaignId}/google/performance`).then(r => r.data);

export const getUnifiedPerformance = (campaignId: string): Promise<any> =>
    apiClient.get(`/ad-platforms/campaigns/${campaignId}/performance/unified`).then(r => r.data);

// ── EDM (Email Direct Marketing) ──────────────────────────────────────────────

export interface EDMTemplate {
    template_id: string;
    project_id: string;
    owner_id: string;
    brand_guide_id: string | null;
    name: string;
    description: string | null;
    category: string;
    subject_template: string | null;
    body_html_template: string | null;
    body_plain_template: string | null;
    preview_image_url: string | null;
    thumbnail_html: string | null;
    merge_fields: string[] | null;
    is_ai_generated: boolean;
    usage_count: number;
    status: string;
    created_at: string;
    updated_at: string;
}

// -- Template CRUD --

export const getEDMTemplates = async (projectId?: string, category?: string, status?: string): Promise<EDMTemplate[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (category) params.category = category;
    if (status) params.status = status;
    const response = await apiClient.get('/edm/templates', { params });
    return response.data;
};

export const getEDMTemplate = async (templateId: string): Promise<EDMTemplate> => {
    const response = await apiClient.get(`/edm/templates/${templateId}`);
    return response.data;
};

export const createEDMTemplate = async (data: {
    project_id: string;
    brand_guide_id?: string;
    name: string;
    description?: string;
    category?: string;
    subject_template?: string;
    body_html_template?: string;
    body_plain_template?: string;
    merge_fields?: string[];
}): Promise<EDMTemplate> => {
    const response = await apiClient.post('/edm/templates', data);
    return response.data;
};

export const updateEDMTemplate = async (templateId: string, data: Partial<EDMTemplate>): Promise<EDMTemplate> => {
    const response = await apiClient.patch(`/edm/templates/${templateId}`, data);
    return response.data;
};

export const deleteEDMTemplate = async (templateId: string): Promise<void> => {
    await apiClient.delete(`/edm/templates/${templateId}`);
};

export const duplicateEDMTemplate = async (templateId: string): Promise<EDMTemplate> => {
    const response = await apiClient.post(`/edm/templates/${templateId}/duplicate`);
    return response.data;
};

// -- AI Generation --

export const generateEDMTemplate = async (data: {
    project_id: string;
    brand_guide_id: string;
    category?: string;
    objective?: string;
    tone_override?: string;
    cta_text?: string;
}): Promise<EDMTemplate> => {
    const response = await apiClient.post('/edm/templates/generate', data);
    return response.data;
};

export const regenerateEDMBrand = async (templateId: string): Promise<EDMTemplate> => {
    const response = await apiClient.post(`/edm/templates/${templateId}/regenerate-brand`);
    return response.data;
};

export const previewEDMTemplate = async (templateId: string): Promise<{
    subject: string;
    body_html: string;
    body_plain: string;
    merge_fields: string[];
    sample_data: Record<string, string>;
}> => {
    const response = await apiClient.get(`/edm/templates/${templateId}/preview`);
    return response.data;
};

// -- Sequences --

export const getEDMSequences = async (projectId?: string): Promise<any[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    const response = await apiClient.get('/edm/sequences', { params });
    return response.data;
};

export const createEDMSequence = async (data: {
    project_id: string;
    name: string;
    description?: string;
    campaign_id?: string;
}): Promise<any> => {
    const response = await apiClient.post('/edm/sequences', data);
    return response.data;
};

export const createEDMSequenceFromTemplate = async (data: {
    project_id: string;
    template_id: string;
    sequence_name: string;
    step_count?: number;
    campaign_id?: string;
}): Promise<any> => {
    const response = await apiClient.post('/edm/sequences/from-template', data);
    return response.data;
};

// Publication-aligned scheduling (edm-stream-sequence-scheduling §1/§7).
// When omitted from a from-articles request the sequence behaves exactly as
// before: send_mode "relative", no absolute_send_at, no publication gates.
export interface EDMScheduleAlignment {
    enabled: boolean;
    offset_days: number;
    send_mode: 'broadcast' | 'relative';
    gate_unscheduled: boolean;
}

export interface EDMSchedulePreviewArticle {
    task_id: string;
    title: string | null;
    platform: string | null;
    publication_id: string | null;
    scheduled_for: string | null;
    send_at: string | null;
    url: string | null;
    url_source: 'published' | 'predicted' | 'unresolved' | string;
    gated: boolean;
}

export interface EDMSequenceScheduleStep {
    step_id: string;
    step_order: number;
    subject_line: string | null;
    approval_status: string | null;
    article_task_id: string | null;
    article_title: string | null;
    article_publication_id: string | null;
    publication_status: string | null;
    publication_scheduled_for: string | null;
    schedule_offset_days: number | null;
    absolute_send_at: string | null;
    publication_gate: boolean;
    url: string | null;
    url_source: string | null;
    held: boolean;
    held_reason: string | null;
}

export interface EDMSequenceSchedule {
    sequence_id: string;
    send_mode: string;
    send_time_of_day: string | null;
    owner_timezone: string | null;
    steps: EDMSequenceScheduleStep[];
    active_enrollments: number;
    held_step_count: number;
}

export const createEDMSequenceFromArticles = async (data: {
    project_id: string;
    template_id: string;
    sequence_name: string;
    mode: 'stream' | 'single' | 'manual';
    article_ids: string[];
    step_count?: number;
    delay_days?: number;
    campaign_id?: string;
    series_plan?: boolean;
    schedule_alignment?: EDMScheduleAlignment;
    /** Body-length preset. Omitted = no length instruction (pre-feature behaviour). */
    email_length?: 'brief' | 'standard' | 'detailed';
    /** Source-content intent; drives the per-step shape pool. Omitted = legacy fixed shape. */
    content_type?: 'general' | 'practical_guide' | 'regulatory_change' | 'comparison' | 'announcement';
    /** Free-text user guidance, <= 500 chars after trim. */
    additional_instructions?: string;
}): Promise<{
    sequence_id: string;
    name: string;
    step_count: number;
    mode: string;
    article_count: number;
    partial_generation: boolean;
    empty_step_orders: number[];
    ai_failed: boolean;
    send_mode?: string;
    scheduled_steps?: number;
    gated_steps?: number;
}> => {
    const response = await apiClient.post('/edm/sequences/from-articles', data);
    return response.data;
};

// Predicted publication + send timing for a set of articles, before any
// sequence exists (Contracts §8). Drives the wizard's Schedule step.
export const previewEDMSchedule = async (body: {
    project_id: string;
    article_ids: string[];
    offset_days?: number;
}): Promise<{ articles: EDMSchedulePreviewArticle[] }> => {
    const response = await apiClient.post('/edm/sequences/schedule-preview', body);
    return response.data;
};

export const getEDMSequenceSchedule = async (
    sequenceId: string,
): Promise<EDMSequenceSchedule> => {
    const response = await apiClient.get(`/edm/sequences/${sequenceId}/schedule`);
    return response.data;
};

export const realignEDMSequence = async (
    sequenceId: string,
    body: { offset_days?: number; dry_run?: boolean } = {},
): Promise<{
    sequence_id: string;
    dry_run: boolean;
    steps_updated: number;
    steps_unchanged: number;
    enrollments_updated: number;
    changes: Array<{
        step_id: string;
        step_order: number;
        old_absolute_send_at: string | null;
        new_absolute_send_at: string | null;
    }>;
}> => {
    const response = await apiClient.post(`/edm/sequences/${sequenceId}/realign`, body);
    return response.data;
};

/** Generation settings read back from GET /edm/sequences/{id} (Contract C4).
 *  Documentary only: getEDMSequence still returns `Promise<any>` — typing the
 *  full sequence-detail payload is a separate refactor, deliberately not done
 *  here (see spec Phase 6). */
export interface EDMSequenceGenerationSettings {
    email_length: string | null;
    content_type: string | null;
    additional_instructions: string | null;
}

export const getEDMSequence = async (sequenceId: string): Promise<any> => {
    const response = await apiClient.get(`/edm/sequences/${sequenceId}`);
    return response.data;
};

// Change the EDM template applied across an existing sequence. The template is
// applied at render/send time, so this re-skins every unsent email + preview
// instantly. Pass null to clear the template (raw inner HTML, no chrome).
export const updateEDMSequenceTemplate = async (
    sequenceId: string,
    templateId: string | null,
): Promise<{ sequence_id: string; edm_template_id: string | null; updated_at: string | null }> => {
    const response = await apiClient.patch(`/edm/sequences/${sequenceId}/template`, {
        template_id: templateId,
    });
    return response.data;
};

export const regenerateEDMStep = async (sequenceId: string, stepId: string): Promise<{
    step_id: string;
    subject_line: string;
    preview_text: string;
    body_html: string;
    regenerated: boolean;
}> => {
    const response = await apiClient.post(`/edm/sequences/${sequenceId}/steps/${stepId}/regenerate`);
    return response.data;
};

export const getEDMCTAOptions = async (sequenceId: string): Promise<{
    landing_pages: { id: string; title: string; slug: string; url: string; status: string }[];
    lead_forms: { id: string; name: string; embed_url: string; status: string }[];
    lists: { id: string; name: string; member_count: number; is_list: true }[];
    booking_pages: { id: string; title: string; url: string; status: string }[];
    primary_color?: string;
    secondary_color?: string;
    base_url?: string;
    verified_domain?: string | null;
}> => {
    const response = await apiClient.get(`/edm/sequences/${sequenceId}/cta-options`);
    return response.data;
};

export const createEDMList = async (
    project_id: string,
    name: string,
    description?: string,
): Promise<{
    sequence_id: string;
    name: string;
    description: string | null;
    is_list: true;
    status: string;
    member_count: number;
    created_at: string | null;
}> => {
    const response = await apiClient.post('/edm/lists', { project_id, name, description });
    return response.data;
};

// NOTE (IMPR-002, specs/completed/edm-cta-one-click-list-join-code-review.md):
// GET /api/edm/lists?project_id=... still exists on the backend (edm_routes.py)
// but has no frontend consumer — the CTA picker gets its lists array from
// GET /api/edm/sequences/{id}/cta-options instead, and "see who joined a list"
// is served by the existing per-sequence Recipients tab (works on a list
// sequence unmodified). A dead client wrapper here invited a future caller to
// assume a standalone "Lists panel" UI exists (it doesn't — see assistant_knowledge.py),
// so it was removed rather than left as dead code. Re-add it if a dedicated
// Lists surface is ever built.

// -- Journey --

export interface JourneyNode {
    id: string;
    edm_sequence_id: string;
    position?: { x: number; y: number };
    edm_sequence?: {
        sequence_id: string;
        name: string;
        status: string;
        step_count: number;
    };
}

export interface JourneyEdgeTrigger {
    type: 'campaign_start' | 'email_opened' | 'link_clicked' | 'no_open' | 'no_click' | 'sequence_completed' | 'lead_form' | 'crm_lead_stage';
    source_sequence_id?: string;
    step_id?: string;
    step_label?: string;
    after_days?: number;
    form_id?: string;
    form_name?: string;
    stage?: string;
}

export interface JourneyEdge {
    id: string;
    from_node: string;
    to_node: string;
    trigger: JourneyEdgeTrigger;
    halt_source: boolean;
    resume_on_complete: boolean;
}

export interface JourneyConfig {
    send_frequency_cap: number;
    nodes: JourneyNode[];
    edges: JourneyEdge[];
}

export const getJourney = async (campaignId: string): Promise<{ campaign_id: string; journey_config: JourneyConfig }> => {
    const response = await apiClient.get(`/campaigns/${campaignId}/journey`);
    return response.data;
};

export const saveJourney = async (campaignId: string, journeyConfig: JourneyConfig): Promise<{ campaign_id: string; journey_config: JourneyConfig }> => {
    const response = await apiClient.post(`/campaigns/${campaignId}/journey`, journeyConfig);
    return response.data;
};

export interface JourneyNodeAnalytics {
    enrolled: number;
    active: number;
    paused: number;
    completed: number;
    total_sent: number;
    open_rate: number;
    click_rate: number;
}

export const getJourneyAnalytics = async (campaignId: string): Promise<{
    nodes: Record<string, JourneyNodeAnalytics>;
    edges: Record<string, { traversal_count: number }>;
}> => {
    const response = await apiClient.get(`/campaigns/${campaignId}/journey/analytics`);
    return response.data;
};

// -- Standalone Journey Builder --

export interface ProjectJourney {
    journey_id: string;
    project_id: string;
    name: string;
    description?: string;
    journey_config?: JourneyConfig;
    status: string;
    node_count?: number;
    created_at?: string;
    updated_at?: string;
}

export const listProjectJourneys = async (projectId?: string): Promise<ProjectJourney[]> => {
    const params: Record<string, string> = {};
    if (projectId) params.project_id = projectId;
    const response = await apiClient.get('/edm/journeys', { params });
    return response.data;
};

export const createProjectJourney = async (projectId: string, name: string): Promise<ProjectJourney> => {
    const response = await apiClient.post('/edm/journeys', { project_id: projectId, name });
    return response.data;
};

export const getProjectJourney = async (journeyId: string): Promise<ProjectJourney> => {
    const response = await apiClient.get(`/edm/journeys/${journeyId}`);
    return response.data;
};

export const saveProjectJourney = async (journeyId: string, config: JourneyConfig): Promise<ProjectJourney> => {
    const response = await apiClient.post(`/edm/journeys/${journeyId}`, config);
    return response.data;
};

export const renameProjectJourney = async (journeyId: string, name: string): Promise<ProjectJourney> => {
    const response = await apiClient.patch(`/edm/journeys/${journeyId}`, { name });
    return response.data;
};

export const setProjectJourneyStatus = async (journeyId: string, status: 'draft' | 'active'): Promise<ProjectJourney> => {
    const response = await apiClient.patch(`/edm/journeys/${journeyId}`, { status });
    return response.data;
};

export const deleteProjectJourney = async (journeyId: string): Promise<void> => {
    await apiClient.delete(`/edm/journeys/${journeyId}`);
};

// -- Journey trigger event audit log (journey-builder-crm-trigger-wiring) --

export interface JourneyTriggerEvent {
    event_id: string;
    edge_id: string | null;
    trigger_type: string;
    match_value: string | null;
    recipient_email: string;
    target_sequence_id: string | null;
    outcome: string;
    detail: string | null;
    source: string;
    created_at: string;
}

export const getJourneyEvents = async (campaignId: string, limit = 100): Promise<{
    graph_kind: string; graph_id: string; events: JourneyTriggerEvent[];
}> => {
    const response = await apiClient.get(`/campaigns/${campaignId}/journey/events`, { params: { limit } });
    return response.data;
};

export const getProjectJourneyEvents = async (journeyId: string, limit = 100): Promise<{
    graph_kind: string; graph_id: string; events: JourneyTriggerEvent[];
}> => {
    const response = await apiClient.get(`/edm/journeys/${journeyId}/events`, { params: { limit } });
    return response.data;
};

// -- Recipients --

// crm-tenant-scoped-reads: which CRM view served the request. 'none' = the
// user's CRM workspace isn't ready (empty result + `notice`); 'superadmin' =
// platform-support cross-tenant read view.
export type CRMScopeMode = 'superadmin' | 'company' | 'tenant' | 'none';

export interface CRMRecipientsResponse {
    records: Array<{ id: string; module: string; email: string; first_name: string; last_name: string; company: string; phone: string }>;
    module: string;
    page: number;
    per_page: number;
    meta: Record<string, any>;
    crm_scope?: CRMScopeMode;
    notice?: string;
    /** crm-per-project-scoping: echoes the project the list was scoped to. */
    project_id?: string | null;
}

export interface CRMImportResult {
    enrolled: number;
    skipped: number;
    skipped_optout?: number;
    total_available: number;
    sequence_id?: string;
    crm_scope?: CRMScopeMode;
    notice?: string;
}

export const getCRMRecipients = async (crmModule: string = 'Leads', page: number = 1, perPage: number = 20, projectId?: string | null): Promise<CRMRecipientsResponse> => {
    const response = await apiClient.get('/edm/recipients/crm-lists', {
        params: { crm_module: crmModule, page, per_page: perPage, ...(projectId ? { project_id: projectId } : {}) },
    });
    return response.data;
};

export const importCRMRecipients = async (data: {
    sequence_id: string;
    crm_module?: string;
    filter?: Record<string, string>;
    limit?: number;
    scope?: 'own' | 'all';
}): Promise<CRMImportResult> => {
    const response = await apiClient.post('/edm/recipients/import-from-crm', data);
    return response.data;
};

export const importCSVRecipients = async (sequenceId: string, file: File): Promise<{ enrolled: number; skipped: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/edm/recipients/import-csv?sequence_id=${sequenceId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const deleteEnrollment = async (enrollmentId: string): Promise<void> => {
    await apiClient.delete(`/edm/enrollments/${enrollmentId}`);
};

// ── EDM Audiences: CRM custom group ↔ AIME label mapping (per project) ──────
export interface CRMGroup {
    key: string;        // SuiteCRM group value, e.g. "custom_1"
    crm_label: string;  // SuiteCRM display label, e.g. "Custom 1"
    alias: string;      // AIME-side friendly label, e.g. "Lawyers" ('' if unmapped)
}

export const getCRMGroups = async (projectId: string): Promise<{ groups: CRMGroup[]; options_available: boolean; crm_scope?: CRMScopeMode; notice?: string }> => {
    const response = await apiClient.get('/edm/crm-groups', { params: { project_id: projectId } });
    return response.data;
};

export const saveCRMGroups = async (projectId: string, labels: Record<string, string>): Promise<{ saved: boolean; labels: Record<string, string> }> => {
    const response = await apiClient.put('/edm/crm-groups', { labels }, { params: { project_id: projectId } });
    return response.data;
};

export const importGroupRecipients = async (data: {
    sequence_id: string;
    group_key: string;
    scope?: 'own' | 'all';
}): Promise<CRMImportResult> => {
    const response = await apiClient.post('/edm/recipients/import-from-group', data);
    return response.data;
};

export const clearAllEnrollments = async (sequenceId: string): Promise<{ deleted: number }> => {
    const response = await apiClient.delete(`/edm/sequences/${sequenceId}/enrollments`);
    return response.data;
};

export const getSequenceSendLog = async (sequenceId: string): Promise<{
    sequence_id: string;
    enrolled_total: number;
    steps: {
        step_id: string;
        step_order: number;
        subject_line: string | null;
        delay_days: number;
        sender_name: string | null;
        sent_count: number;
        opened_count: number;
        clicked_count: number;
        first_sent_at: string | null;
        last_sent_at: string | null;
    }[];
}> => {
    const response = await apiClient.get(`/edm/sequences/${sequenceId}/send-log`);
    return response.data;
};

export const getSequenceEnrollments = async (
    sequenceId: string,
    page: number = 1,
    perPage: number = 50,
): Promise<{ total: number; page: number; per_page: number; pages: number; enrollments: any[] }> => {
    const response = await apiClient.get(`/edm/sequences/${sequenceId}/enrollments`, {
        params: { page, per_page: perPage },
    });
    return response.data;
};

export const exportEnrollments = async (sequenceId: string): Promise<Blob> => {
    const response = await apiClient.get(`/edm/recipients/export/${sequenceId}`, {
        responseType: 'blob',
    });
    return response.data;
};

// -- CRM Sync --

export const syncEDMSequenceToCRM = async (sequenceId: string): Promise<any> => {
    // Links recipients to the CRM prospect list one at a time (sequential CRM API
    // calls, not batched) — for a sequence with many enrolled recipients this can
    // comfortably exceed apiClient's 120s default, aborting the request client-side
    // while the sync keeps running server-side to completion (nginx logs a 499, the
    // user sees a failure, but the DB ends up marked synced anyway once the backend
    // finishes — confirmed 2026-08-12). 300s matches nginx's proxy_read_timeout for
    // /api/, same override pattern as crmImportApi.suggest().
    const response = await apiClient.post(`/edm/sequences/${sequenceId}/sync-to-crm`, undefined, { timeout: 300000 });
    return response.data;
};

export const getEDMCRMStatus = async (sequenceId: string): Promise<any> => {
    const response = await apiClient.get(`/edm/sequences/${sequenceId}/crm-status`);
    return response.data;
};

export const triggerEDMCRMSend = async (sequenceId: string): Promise<any> => {
    const response = await apiClient.post(`/edm/sequences/${sequenceId}/crm-send`);
    return response.data;
};

// -- Starter Templates --

export interface EDMStarterTemplate {
    id: string;
    name: string;
    description: string;
    category: string;
    thumbnail_color: string;
}

export const getEDMStarterTemplates = async (): Promise<EDMStarterTemplate[]> => {
    const response = await apiClient.get('/edm/starter-templates');
    return response.data;
};

export const getEDMStarterTemplateHtml = async (templateId: string, brandGuideId?: string, projectId?: string): Promise<string> => {
    const params: any = {};
    if (brandGuideId) params.brand_guide_id = brandGuideId;
    if (projectId) params.project_id = projectId;
    const response = await apiClient.get(`/edm/starter-templates/${templateId}`, { params });
    return response.data.html;
};

// -- Image Library --

export interface EDMImage {
    filename: string;
    url: string;
    size: number;
    uploaded_at: string;
}

export const getEDMImages = async (): Promise<EDMImage[]> => {
    const response = await apiClient.get('/edm/images');
    return response.data;
};

export const deleteEDMImage = async (filename: string): Promise<void> => {
    await apiClient.delete(`/edm/images/${filename}`);
};

// -- Default Template --

export const getDefaultEDMTemplate = async (projectId: string): Promise<{ default_edm_template_id: string | null; template_name: string | null }> => {
    const response = await apiClient.get(`/edm/default-template/${projectId}`);
    return response.data;
};

export const setDefaultEDMTemplate = async (projectId: string, templateId: string | null): Promise<void> => {
    await apiClient.post(`/edm/default-template/${projectId}`, { template_id: templateId });
};

// ── Predictive Analytics ──────────────────────────────────────────────────────

export const getTrendPrediction = async (projectId: string, metricType: string, horizonDays: number = 14): Promise<any> => {
    const response = await apiClient.get('/predictions/trends', { params: { project_id: projectId, metric_type: metricType, horizon_days: horizonDays } });
    return response.data;
};

export const getLeadScores = async (projectId: string, limit: number = 50): Promise<any> => {
    const response = await apiClient.get('/predictions/lead-scores', { params: { project_id: projectId, limit } });
    return response.data;
};

export const refreshLeadScores = async (projectId: string): Promise<any> => {
    const response = await apiClient.post(`/predictions/lead-scores/refresh?project_id=${projectId}`);
    return response.data;
};

export const getInsightReport = async (projectId: string): Promise<any> => {
    const response = await apiClient.get('/predictions/insights', { params: { project_id: projectId } });
    return response.data;
};

export const getPredictionDashboard = async (projectId: string): Promise<any> => {
    const response = await apiClient.get('/predictions/dashboard', { params: { project_id: projectId } });
    return response.data;
};

// ── Adaptive Optimization ─────────────────────────────────────────────────────

export const getOptimizationExperiments = async (projectId: string): Promise<any[]> => {
    const response = await apiClient.get('/optimization/experiments', { params: { project_id: projectId } });
    return response.data;
};

export const createOptimizationExperiment = async (data: {
    project_id: string;
    name: string;
    experiment_type: string;
    target_page_id?: string;
    target_page_type?: string;
    target_selector?: string;
    conversion_goal: string;
    algorithm?: string;
    min_traffic?: number;
    auto_generate_new?: boolean;
}): Promise<any> => {
    const response = await apiClient.post('/optimization/experiments', data);
    return response.data;
};

export const generateExperimentVariants = async (experimentId: string): Promise<any> => {
    const response = await apiClient.post(`/optimization/experiments/${experimentId}/generate-variants`);
    return response.data;
};

export const startExperiment = async (experimentId: string): Promise<any> => {
    const response = await apiClient.post(`/optimization/experiments/${experimentId}/start`);
    return response.data;
};

export const pauseExperiment = async (experimentId: string): Promise<any> => {
    const response = await apiClient.post(`/optimization/experiments/${experimentId}/pause`);
    return response.data;
};

export const completeExperiment = async (experimentId: string): Promise<any> => {
    const response = await apiClient.post(`/optimization/experiments/${experimentId}/complete`);
    return response.data;
};

export const getExperimentResults = async (experimentId: string): Promise<any> => {
    const response = await apiClient.get(`/optimization/experiments/${experimentId}/results`);
    return response.data;
};

export const getOptimizationDashboard = async (projectId: string): Promise<any> => {
    const response = await apiClient.get('/optimization/dashboard', { params: { project_id: projectId } });
    return response.data;
};

// ── Genesis Node ──────────────────────────────────────────────────────────────

export const getGenesisStats = async (): Promise<{ total_seats: number; claimed_seats: number; is_sold_out: boolean }> => {
    const response = await apiClient.get('/billing/genesis-stats');
    return response.data;
};

// ── Public Support ────────────────────────────────────────────────────────────

export const submitSupportRequest = async (data: {
    email: string;
    name?: string;
    category: string;
    subject?: string;
    message: string;
    website?: string;
    form_loaded_at: number;
}): Promise<{ detail: string }> => {
    const response = await apiClient.post('/support/submit', data);
    return response.data;
};

// ── Knowledge Base ────────────────────────────────────────────────────────────

export interface KnowledgeModule {
    key: string;
    name: string;
    route: string;
    description: string;
    key_features: string[];
    user_actions: Array<{ action: string; how: string; tab?: string }>;
    tier: string;
    accessible: boolean;
    required_tier: string;
}

export interface KnowledgeWorkflow {
    key: string;
    name: string;
    steps: string[];
}

export interface KnowledgeBaseResponse {
    modules: KnowledgeModule[];
    workflows: KnowledgeWorkflow[];
    user_tier: string;
    is_admin: boolean;
}

export const getKnowledgeBase = async (): Promise<KnowledgeBaseResponse> => {
    const response = await apiClient.get('/knowledge-base');
    return response.data;
};

// ==================== Support Tickets ====================

export interface SupportTicket {
    ticket_id: string;
    short_id?: string;
    user_id: string;
    user_email?: string;
    user_name?: string;
    subject: string;
    category: string;
    priority: string;
    status: string; // 'open' | 'closed'
    created_at: string;
    updated_at: string;
    closed_at?: string;
    message_count?: number;
}

export interface SupportTicketMessage {
    message_id: string;
    ticket_id: string;
    author_id: string;
    author_name?: string;
    author_email?: string;
    body: string;
    is_staff_reply: boolean;
    created_at: string;
}

export interface SupportTicketDetail extends SupportTicket {
    messages: SupportTicketMessage[];
}

export const createSupportTicket = (data: {
    subject: string;
    category: string;
    message: string;
    priority?: string;
}): Promise<SupportTicket> =>
    apiClient.post('/support/tickets', data).then(r => r.data);

export const getSupportTickets = (params?: {
    status?: string;
    q?: string;
    limit?: number;
    offset?: number;
}): Promise<SupportTicket[]> =>
    apiClient.get('/support/tickets', { params }).then(r => r.data.tickets ?? r.data);

export const getSupportTicket = (ticketId: string): Promise<SupportTicketDetail> =>
    apiClient.get(`/support/tickets/${ticketId}`).then(r => r.data);

export const replySupportTicket = (ticketId: string, body: string): Promise<SupportTicketMessage> =>
    apiClient.post(`/support/tickets/${ticketId}/messages`, { body }).then(r => r.data);

export const updateSupportTicket = (ticketId: string, data: { status: string }): Promise<SupportTicket> =>
    apiClient.patch(`/support/tickets/${ticketId}`, data).then(r => r.data);


// ==================== API Call Quotas (Admin) ====================

export interface ApiCallQuotaRow {
    id: number;
    surface: string;
    surface_label: string;
    tier: string;
    daily_limit: number;
    unlimited: boolean;
    updated_at: string | null;
    updated_by: string | null;
    total_calls_today: number;
}

export const getAdminQuotas = (): Promise<ApiCallQuotaRow[]> =>
    apiClient.get('/admin/quotas').then(r => r.data);

export const setAdminQuota = (surface: string, tier: string, daily_limit: number): Promise<ApiCallQuotaRow> =>
    apiClient.put(`/admin/quotas/${surface}/${tier}`, { daily_limit }).then(r => r.data);

// ==================== Google Search Console ====================

export interface GscStatus {
    connected: boolean;
    status: 'connected' | 'needs_reauth' | 'disconnected' | 'revoked';
    site_url?: string;
    last_synced?: string;
    last_error?: string;
}

export interface GscSite { url: string; permission: string; }

export interface GscSnapshots {
    days: number;
    site_url: string;
    daily_totals: Array<{ date: string; clicks: number; impressions: number; avg_position: number }>;
    top_queries: Array<{ query: string; clicks: number; impressions: number; avg_position: number }>;
    top_pages: Array<{ page: string; clicks: number; impressions: number }>;
}

export const gscGetAuthUrl = (project_id: string): Promise<{ auth_url: string }> =>
    apiClient.get('/gsc/auth', { params: { project_id } }).then(r => r.data);

export const gscGetStatus = (project_id: string): Promise<GscStatus> =>
    apiClient.get('/gsc/status', { params: { project_id } }).then(r => r.data);

export const gscListSites = (project_id: string): Promise<{ sites: GscSite[] }> =>
    apiClient.get('/gsc/sites', { params: { project_id } }).then(r => r.data);

export const gscConnect = (project_id: string, site_url: string): Promise<any> =>
    apiClient.post('/gsc/connect', null, { params: { project_id, site_url } }).then(r => r.data);

export const gscSync = (project_id: string): Promise<{ rows_ingested: number }> =>
    apiClient.post('/gsc/sync', null, { params: { project_id } }).then(r => r.data);

export const gscGetSnapshots = (project_id: string, days: number = 30): Promise<GscSnapshots> =>
    apiClient.get('/gsc/snapshots', { params: { project_id, days } }).then(r => r.data);

export const gscDisconnect = (project_id: string): Promise<any> =>
    apiClient.delete('/gsc/disconnect', { params: { project_id } }).then(r => r.data);

// ==================== Ad Copy Image Generation ====================

// Art-direction style presets for ad-image generation. Duplicated from the
// backend's ad_copy_generator.AD_IMAGE_TYPES (single source of truth for
// behaviour — an id this list doesn't know falls back server-side to
// "photo" via resolve_image_type()), matching the existing AD_ANGLES /
// AD_FRAMEWORKS duplication pattern already used elsewhere in this module.
export const AD_IMAGE_TYPES: { id: string; label: string; desc: string }[] = [
    { id: 'photo', label: 'Photo', desc: 'Realistic photography' },
    { id: 'illustration', label: 'Illustration', desc: 'Hand-drawn / vector illustration style' },
    { id: 'flat_graphic', label: 'Flat Graphic', desc: 'Bold flat-design graphic composition' },
    { id: '3d_render', label: '3D Render', desc: 'Stylised 3D render' },
    { id: 'lifestyle', label: 'Lifestyle', desc: 'Candid lifestyle photography' },
    { id: 'minimal', label: 'Minimal', desc: 'Minimalist composition with generous negative space' },
    { id: 'abstract', label: 'Abstract', desc: 'Abstract shapes and colour rather than a literal scene' },
];

export const generateAdImage = (data: {
    visual_concept: string;
    /** @deprecated ignored by the backend — overlay text is applied post-hoc via compositeText() */
    image_overlay_text?: string;
    /** Selects the "Digital advertising creative" prompt framing (clean, text-free base art). */
    ad_creative?: boolean;
    platform: string;
    brand_guide_id?: string;
    /** Key from AD_IMAGE_TYPES; unknown/omitted falls back to "photo" server-side. */
    image_type?: string;
}): Promise<{ image_url: string; image_prompt: string; width: number; height: number; platform: string; platform_label: string; provider: string; thumbnail_url?: string | null; image_type?: string }> =>
    apiClient.post('/multichannel/ad-copy/generate-image', data, { timeout: 120000 }).then(r => r.data);

// ==================== Saved Ad Creatives ====================

export interface SavedAdCreative {
    id: number;
    platform: string;
    angle?: string;
    framework?: string;
    copy_data: Record<string, any>;
    image_url?: string;
    thumbnail_url?: string | null;
    image_prompt?: string;
    overlay_state?: OverlayState | null;
    label?: string;
    campaign_id?: string | null;
    created_at: string;
    target_audience?: string | null;
    offer?: string | null;
    audience_segments?: string[] | null;
    product_service?: string | null;
    goal?: string | null;
    image_type?: string | null;
    // Optional so the interface stays satisfied by the PATCH response, which
    // does not include it — only the list endpoint computes the grouped count.
    lead_forms_count?: number;
    // Same posture as lead_forms_count — grouped count from list_saved_ad_creatives
    // only (specs/completed/ad-image-aspect-variants.md).
    image_variants_count?: number;
}

// ── Ad image aspect variants (specs/completed/ad-image-aspect-variants.md) ──
// Meta ad shapes derived from one base image via user-driven pan + crop, then
// the existing text-overlay compositor. Entirely mechanical — no AI, no
// image-generation quota. Duplicated deliberately from
// ad_image_variants.ASPECT_RATIOS on the backend, per the existing
// AD_ANGLES / AD_FRAMEWORKS / AD_IMAGE_TYPES precedent in this same module.
export type AdImageRatioKey = 'square' | 'vertical' | 'horizontal';

export const AD_ASPECT_RATIOS: Record<AdImageRatioKey, {
    label: string; ratioLabel: string; note: string; derived: boolean;
    width: number; height: number;
}> = {
    square: { label: 'Square', ratioLabel: '1:1', note: 'Feed, Explore, in-stream.', derived: false, width: 1080, height: 1080 },
    vertical: { label: 'Vertical', ratioLabel: '9:16', note: 'Stories, Reels.', derived: true, width: 1080, height: 1920 },
    // 1200x628 — matches Meta's recommended link-ad size and the value
    // PLATFORM_IMAGE_SPECS already uses for linkedin/google. Intentionally
    // 1200 wide where the other two are 1080 (Known Risk 5 in the spec).
    horizontal: { label: 'Horizontal', ratioLabel: '1.91:1', note: 'Right column, link ads, Audience Network.', derived: true, width: 1200, height: 628 },
};

export const DERIVED_AD_RATIOS: ('vertical' | 'horizontal')[] = ['vertical', 'horizontal'];

export interface RatioGeometry {
    ratio: AdImageRatioKey;
    label: string;
    ratio_label: string;
    note: string;
    derived: boolean;
    target_w: number;
    target_h: number;
    crop_w: number;
    crop_h: number;
    pan_axis: 'x' | 'y' | 'none';
    upscale_factor: number | null;
}

export interface AdImageVariant {
    ratio: AdImageRatioKey;
    pan_x: number;
    pan_y: number;
    overlay_state: OverlayState | null;
    crop_url: string | null;
    variant_image_url: string | null;
    thumbnail_url: string | null;
    width: number | null;
    height: number | null;
    needs_regenerate: boolean;
}

export interface AdImageVariantsResponse {
    available: boolean;
    reason: string;
    source: { url: string; width: number; height: number } | null;
    ratios: RatioGeometry[];
    variants: AdImageVariant[];
}

export const getAdImageVariants = (creativeId: number): Promise<AdImageVariantsResponse> =>
    apiClient.get(`/multichannel/ad-copy/saved/${creativeId}/image-variants`).then(r => r.data);

export const putAdImageVariant = (
    creativeId: number,
    ratio: 'vertical' | 'horizontal',
    data: Partial<{
        pan_x: number; pan_y: number; overlay_state: OverlayState | null;
        variant_image_url: string | null; thumbnail_url: string | null;
        width: number | null; height: number | null;
    }>,
): Promise<AdImageVariant> =>
    apiClient.put(`/multichannel/ad-copy/saved/${creativeId}/image-variants/${ratio}`, data).then(r => r.data);

export interface CropAdImageVariantResult {
    available: boolean;
    reason?: string;
    crop_url: string | null;
    width?: number;
    height?: number;
    crop_w?: number;
    crop_h?: number;
    pan_axis?: 'x' | 'y' | 'none';
    upscale_factor?: number | null;
    cached?: boolean;
}

export const cropAdImageVariant = (
    creativeId: number,
    ratio: 'vertical' | 'horizontal',
): Promise<CropAdImageVariantResult> =>
    apiClient.post(`/multichannel/ad-copy/saved/${creativeId}/image-variants/${ratio}/crop`).then(r => r.data);

// Constraint, not a preference: apiClient's request interceptor is what
// attaches Authorization — a bare <a href> would 401 on this authed
// endpoint. Caller must build an object URL from the returned blob.
export const downloadAdImageVariantsZip = (creativeId: number): Promise<Blob> =>
    apiClient.get(`/multichannel/ad-copy/saved/${creativeId}/image-variants/download`, { responseType: 'blob' })
        .then(r => r.data);

export const saveAdCreative = (data: {
    platform: string;
    copy_data: Record<string, any>;
    image_url?: string;
    thumbnail_url?: string | null;
    image_prompt?: string;
    overlay_state?: OverlayState | null;
    angle?: string;
    framework?: string;
    project_id?: string;
    label?: string;
    target_audience?: string;
    offer?: string;
    audience_segments?: string[];
    product_service?: string;
    goal?: string;
    image_type?: string;
}): Promise<{ id: number; created_at: string }> =>
    apiClient.post('/multichannel/ad-copy/saved', data).then(r => r.data);

export const listSavedAdCreatives = (project_id?: string): Promise<SavedAdCreative[]> =>
    apiClient.get('/multichannel/ad-copy/saved', { params: project_id ? { project_id } : {} }).then(r => r.data);

export const deleteSavedAdCreative = (id: number): Promise<{ deleted: number }> =>
    apiClient.delete(`/multichannel/ad-copy/saved/${id}`).then(r => r.data);

export const updateSavedAdCreative = (id: number, data: {
    image_url?: string;
    thumbnail_url?: string | null;
    image_prompt?: string;
    overlay_state?: OverlayState | null;
    label?: string;
    copy_data?: Record<string, any>;
    target_audience?: string | null;
    offer?: string | null;
    audience_segments?: string[] | null;
    product_service?: string | null;
    goal?: string | null;
    image_type?: string | null;
}): Promise<{ id: number; image_url?: string; thumbnail_url?: string | null; image_prompt?: string; overlay_state?: OverlayState | null; label?: string; copy_data?: Record<string, any>; target_audience?: string | null; offer?: string | null; audience_segments?: string[] | null; product_service?: string | null; goal?: string | null; image_type?: string | null }> =>
    apiClient.patch(`/multichannel/ad-copy/saved/${id}`, data).then(r => r.data);

export const regenerateSavedAdCopy = (id: number, data: {
    product_service?: string;
    goal?: string;
    target_audience?: string;
    offer?: string;
    brand_guide_id?: string;
}): Promise<{
    id: number;
    copy_data: Record<string, any>;
    product_service?: string | null;
    goal?: string | null;
    target_audience?: string | null;
    offer?: string | null;
    audience_segments?: string[] | null;
    angle?: string | null;
    framework?: string | null;
    platform: string;
}> =>
    apiClient.post(`/multichannel/ad-copy/saved/${id}/regenerate-copy`, data, { timeout: 120000 })
        .then(r => r.data);

// ==================== Lead Forms (Meta Instant Forms) ====================

export interface LeadFormFlag {
    kind: 'over_limit' | 'restricted_risk';
    path: string;
    limit?: number;
    actual?: number;
    source?: 'meta_documented' | 'aime_house';
    rule?: string;
    detail?: string;
}

export interface LeadFormData {
    name: string;
    locale: string;
    is_optimized_for_quality: boolean;
    context_card: { title: string; style: string; content: string[]; button_text: string };
    questions: { type: string; key?: string; label?: string; options?: { value: string; key?: string }[] }[];
    privacy_policy?: { url: string; link_text: string };
    thank_you_page: { title: string; body: string; button_type: string; button_text: string; website_url: string };
    tracking_parameters?: { aime_saved_ad_id: string; form_type: string; utm_campaign: string };
    _form_type: string;
    _setup_notes: string[];
    _grounding_partial?: boolean;
    _privacy_url_missing?: boolean;
    _flags: LeadFormFlag[];
    [key: string]: any;
}

export interface SavedLeadForm {
    id: number;
    saved_ad_id: number;
    project_id?: string | null;
    form_type: string;
    form_data: LeadFormData;
    privacy_policy_url?: string | null;
    label?: string | null;
    created_at: string;
    meta_page_id?: string | null;
    meta_form_id?: string | null;
    meta_form_name?: string | null;
    meta_linked_at?: string | null;
}

// 300s timeout: up to 3 generation + 3 repair Gemini calls run sequentially —
// the 120s apiClient default would abort mid-run (matches nginx proxy_read_timeout
// and the crmImportApi.suggest() precedent).
export const generateLeadForms = (creativeId: number, data: {
    form_types?: string[];   // omit for all three; NEVER send [] (server 400s it)
    privacy_policy_url?: string | null;
    custom_hint?: string;
    label?: string;
    brand_guide_id?: string;
    locale?: string;
    // Rail A — UTM-tagged thank-you destination (mutually exclusive; server 400s both)
    thank_you_url?: string;
    thank_you_landing_page_id?: string;
    utm_campaign?: string;
}): Promise<{ forms: SavedLeadForm[]; errors: { form_type: string; error: string }[]; privacy_url_suggestion: string | null }> =>
    apiClient.post(`/multichannel/ad-copy/saved/${creativeId}/lead-forms`, data, { timeout: 300000 }).then(r => r.data);

export const listLeadForms = (params: { saved_ad_id?: number; project_id?: string }): Promise<SavedLeadForm[]> =>
    apiClient.get('/multichannel/lead-forms', { params }).then(r => r.data);

// "Saved" prefix distinguishes these from the lead-capture module's
// updateLeadForm/deleteLeadForm (embedded lead-capture forms, string ids).
export const updateSavedLeadForm = (id: number, data: {
    form_data?: LeadFormData;
    label?: string | null;
    privacy_policy_url?: string | null;
}): Promise<SavedLeadForm> =>
    apiClient.patch(`/multichannel/lead-forms/${id}`, data).then(r => r.data);

export const deleteSavedLeadForm = (id: number): Promise<{ deleted: number }> =>
    apiClient.delete(`/multichannel/lead-forms/${id}`).then(r => r.data);

// --- Meta Instant Form linking (specs/completed/meta-leadform-linking.md) ---

export interface MetaLinkablePage {
    page_id: string;
    name: string;
    account_id: string;
    has_token: boolean;
}

export const listMetaLinkablePages = (): Promise<{ pages: MetaLinkablePage[] }> =>
    apiClient.get('/multichannel/lead-forms/meta-pages').then(r => r.data);

export interface MetaLeadgenForm {
    id: string;
    name: string;
    status: string | null;
    created_time: string | null;
}

export const listMetaLeadgenForms = (pageId: string): Promise<{
    available: boolean;
    reason?: 'no_page_token' | 'token_expired' | 'permission_denied' | 'unsupported' | 'graph_error';
    forms: MetaLeadgenForm[];
}> =>
    apiClient.get(`/multichannel/lead-forms/meta-pages/${encodeURIComponent(pageId)}/leadgen-forms`).then(r => r.data);

export const setLeadFormMetaLink = (id: number, data: {
    page_id: string;
    form_id: string;
    form_name?: string;
}): Promise<SavedLeadForm> =>
    apiClient.put(`/multichannel/lead-forms/${id}/meta-link`, data).then(r => r.data);

export const clearLeadFormMetaLink = (id: number): Promise<SavedLeadForm> =>
    apiClient.delete(`/multichannel/lead-forms/${id}/meta-link`).then(r => r.data);

export interface MetaCoverImageResult {
    available: boolean;
    url: string | null;
    reason?: 'no_base_image' | 'remote_base' | 'unsupported_path' | 'missing_file' | 'encode_failed' | 'overlay_baked_base';
    width?: number;
    height?: number;
    source_url?: string;
    source_width?: number;
    source_height?: number;
    cached?: boolean;
}

export const getMetaCoverImage = (creativeId: number): Promise<MetaCoverImageResult> =>
    apiClient.post(`/multichannel/ad-copy/saved/${creativeId}/meta-cover-image`).then(r => r.data);

export interface InterestSuggestionFlag {
    kind: 'restricted_risk';
    rule: string;
    detail: string;
}
export interface InterestSuggestion {
    term: string;
    bucket: 'interest' | 'behaviour' | 'employer_or_industry' | 'life_event' | 'adjacent_brand';
    rationale: string;
    flags: InterestSuggestionFlag[];
}
export interface InterestSuggestionsResult {
    suggestions: InterestSuggestion[];
    disclaimer: string;
    grounding: {
        target_audience: string;
        offer: string | null;
        audience_segments: string[] | null;
        used_copy_fields: string[];
    };
}
// Default 120s apiClient timeout is deliberate and sufficient: ONE Gemini call,
// no repair pass — unlike generateLeadForms (300s: up to 3 generate + 3 repair calls).
export const suggestAdInterests = (creativeId: number, data: {
    target_audience?: string;
    offer?: string;
    audience_segments?: string[];
    brand_guide_id?: string;
} = {}): Promise<InterestSuggestionsResult> =>
    apiClient.post(`/multichannel/ad-copy/saved/${creativeId}/suggest-interests`, data)
        .then(r => r.data);

// ==================== UTM Attribution Breakdown ====================

export interface UtmBreakdownRow {
    key: string;             // never null — untagged traffic lives in `untagged`
    sessions: number;
    page_views: number;
    leads: number;
    conversion_rate: number; // percent, 1dp; 0.0 when sessions === 0
}

export interface UtmBreakdownResponse {
    date_range: { from: string; to: string; days: number };
    group_by: 'source' | 'medium' | 'campaign';
    rows: UtmBreakdownRow[];
    untagged: { sessions: number; page_views: number; leads: number };
}

export const getUtmBreakdown = (
    projectId: string,
    days: number,
    groupBy: 'source' | 'medium' | 'campaign' = 'source',
): Promise<UtmBreakdownResponse> =>
    apiClient.get('/analytics/utm-breakdown', {
        params: { project_id: projectId, days, group_by: groupBy },
    }).then(r => r.data);

// ==================== Site Builder Blog Posts ====================

export interface SiteBlogPost {
    id: number;
    site_project_id: string;
    task_id: string;
    slug: string;
    status: 'draft' | 'published';
    is_live: boolean;
    published_at?: string;
    display_order?: number;
    title: string;
    excerpt?: string;
    featured_image_url?: string;
    content_html?: string;
    last_published_via_publication_id?: string;
    created_at: string;
    updated_at: string;
}

export const listSiteBlogPosts = (siteId: string, status?: string): Promise<SiteBlogPost[]> =>
    apiClient.get(`/site-builder/sites/${siteId}/blog`, { params: status ? { status } : {} }).then(r => r.data);

export const getSiteBlogPost = (siteId: string, slug: string): Promise<SiteBlogPost> =>
    apiClient.get(`/site-builder/sites/${siteId}/blog/${slug}`).then(r => r.data);

export const addSiteBlogPost = (siteId: string, data: {
    task_id: string;
    slug?: string;
    status?: 'draft' | 'published';
    title_override?: string;
    excerpt_override?: string;
    featured_image_url_override?: string;
    display_order?: number;
}): Promise<{ id: number; slug: string; status: string; published_at?: string; publication_id?: string }> =>
    apiClient.post(`/site-builder/sites/${siteId}/blog`, data).then(r => r.data);

export const updateSiteBlogPost = (siteId: string, joinId: number, data: {
    slug?: string;
    status?: 'draft' | 'published';
    title_override?: string;
    excerpt_override?: string;
    featured_image_url_override?: string;
    display_order?: number;
}): Promise<{ id: number; slug: string; status: string; published_at?: string }> =>
    apiClient.patch(`/site-builder/sites/${siteId}/blog/${joinId}`, data).then(r => r.data);

export const removeSiteBlogPost = (siteId: string, joinId: number): Promise<{ deleted: number }> =>
    apiClient.delete(`/site-builder/sites/${siteId}/blog/${joinId}`).then(r => r.data);

// ==================== Netlify Deploy ====================

export interface DeployStatus {
    deploy_target: string | null;
    deploy_target_id: string | null;
    deploy_status: 'ok' | 'failed' | 'retrying' | null;
    deploy_failure_count: number;
    deploy_url: string | null;
    last_redeploy_attempt_at: string | null;
    last_redeploy_error: string | null;
    pending_redeploy_at: string | null;
}

export const connectNetlify = (siteId: string, data: {
    netlify_api_token: string;
    netlify_site_id: string;
}): Promise<{ connected: boolean; netlify_site_name: string; netlify_site_id: string }> =>
    apiClient.post(`/site-builder/sites/${siteId}/connect-netlify`, data).then(r => r.data);

export const disconnectNetlify = (siteId: string): Promise<{ disconnected: boolean }> =>
    apiClient.delete(`/site-builder/sites/${siteId}/connect-netlify`).then(r => r.data);

export const getDeployStatus = (siteId: string): Promise<DeployStatus> =>
    apiClient.get(`/site-builder/sites/${siteId}/deploy-status`).then(r => r.data);

export const triggerDeploy = (siteId: string): Promise<{ queued: boolean; pending_redeploy_at: string }> =>
    apiClient.patch(`/site-builder/sites/${siteId}/deploy`).then(r => r.data);

// ==================== Imported static sites (site-import feature) ====================

export interface ImportedEditPatch {
    selector: string;
    // 'textnode' = plain text for ONE text node of a mixed-content element (text beside an icon);
    // node_index is required for it and absent on every other type.
    type: 'text' | 'src' | 'html' | 'textnode';
    value: string;
    page: string;
    node_index?: number;
    // textnode only: the node's PRISTINE text; the server applies the patch only if it still matches.
    expect?: string;
}

// Import a hand-authored static-site bundle (.zip). Returns the created SiteProject.
// onProgress: 0–100 upload percentage (fires while the browser streams the body to the server).
// Timeout is generous: nginx buffers the WHOLE bundle before the backend runs, so a bundle near
// the 60 MB cap on a slow uplink can take many minutes. 300s was too tight and aborted mid-upload.
export const importStaticSite = async (
    projectId: string,
    name: string,
    file: File,
    onProgress?: (percent: number) => void,
): Promise<any> => {
    const formData = new FormData();
    formData.append('project_id', projectId);
    formData.append('name', name);
    formData.append('file', file);
    const response = await apiClient.post('/site-builder/sites/import', formData, {
        // NB: do NOT rely on this header carrying the multipart boundary — the browser
        // overrides it with a boundary-bearing one when sending FormData.
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 1800000, // 30 min — covers a 60 MB bundle on a slow (~0.3 Mbit/s) uplink
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
    });
    return response.data;
};

export const getSitePatches = (siteId: string): Promise<{ patches: ImportedEditPatch[] }> =>
    apiClient.get(`/site-builder/sites/${siteId}/patches`).then(r => r.data);

export const saveSitePatches = (siteId: string, patches: ImportedEditPatch[]): Promise<{ status: string; count: number }> =>
    apiClient.put(`/site-builder/sites/${siteId}/patches`, { patches }).then(r => r.data);

// --- AIME component sections inserted into an imported page ---

/**
 * position is partitioned by page kind, and the backend rejects a crossing with a 400:
 *   host pages      -> before | after | append_body   (sibling-only; anchor_selector required)
 *   generated pages -> page_content                   (appended into <main> in `order`; no anchor)
 * See specs/completed/imported-site-new-page.md D-2.
 */
export type ImportedSectionPosition = 'before' | 'after' | 'append_body' | 'page_content';

/**
 * `?for=` filter on GET /site-builder/components (specs/completed/imported-site-deep-insert-zones.md):
 *   imported      -> palette for imported HOST pages (includes the host-styled content_host_text)
 *   imported_page -> palette for GENERATED pages (adds blog_*, excludes content_host_text)
 *   imported_all  -> union of both; schema lookup only (PropertiesPanel), never a palette
 * Omitted -> the component-site catalogue (imported_only components excluded).
 */
export type SiteComponentFilter = 'imported' | 'imported_page' | 'imported_all';

/** Controls of a content_host_text section as the server stores/returns them (write-time cleaned:
 *  heading <= 200 chars, heading_level in h2/h3/h4, body allowlist-sanitised, <= 50,000 chars). */
export interface HostTextControls {
    heading: string;
    heading_level: 'h2' | 'h3' | 'h4';
    body: string;
}

export interface ImportedSection {
    section_id: string;
    page: string;
    anchor_selector: string;
    position: ImportedSectionPosition;
    order: number;
    component_id: string;
    variant: string;
    controls: Record<string, any>;
    /** In-page anchor target — link to this section with #anchor_id. */
    anchor_id?: string;
}

export const listSiteSections = (siteId: string): Promise<{ sections: ImportedSection[] }> =>
    apiClient.get(`/site-builder/sites/${siteId}/sections`).then(r => r.data);

export const insertSiteSection = (siteId: string, data: {
    page: string;
    anchor_selector: string;
    position: ImportedSectionPosition;
    component_id: string;
    variant?: string;
    controls?: Record<string, any>;
}): Promise<ImportedSection> =>
    apiClient.post(`/site-builder/sites/${siteId}/sections`, data).then(r => r.data);

export const updateSiteSection = (siteId: string, sectionId: string, data: {
    controls?: Record<string, any>;
    variant?: string;
    order?: number;
    anchor_selector?: string;
    position?: string;
    anchor_id?: string;
}): Promise<ImportedSection> =>
    apiClient.put(`/site-builder/sites/${siteId}/sections/${sectionId}`, data).then(r => r.data);

export const deleteSiteSection = (siteId: string, sectionId: string): Promise<{ status: string }> =>
    apiClient.delete(`/site-builder/sites/${siteId}/sections/${sectionId}`).then(r => r.data);

// --- Copy / duplicate a block on an imported page (specs/completed/imported-site-clone-block.md) ---
// Rows with component_id "host_clone": a sanitised server-side snapshot of a host block. Created only
// by POST /clones, text edited only by PATCH /clones/{id}/text. Both are plain JSON bodies (apiClient's
// default content type) matching CloneCreate / CloneTextUpdate on the backend.

/** controls of a host_clone row: sanitised outer HTML snapshot (+ informational source selector). */
export interface CloneControls { html: string; source_selector: string; element_count?: number; }
export interface CloneTextEdit { node_index: number; expect: string; value: string; }
/** Not stored — what the sanitiser removed from the source (POST /clones response only). */
export interface CloneDropReport { ids: number; style_rules: number; attributes: number; }

export const cloneSiteBlock = (siteId: string, data: {
    page: string; source_selector: string; source_text: string;
    anchor_selector: string; position: 'before' | 'after';
}): Promise<ImportedSection & { dropped?: CloneDropReport }> =>
    apiClient.post(`/site-builder/sites/${siteId}/clones`, data).then(r => r.data);

export const updateSiteCloneText = (siteId: string, sectionId: string, edits: CloneTextEdit[]): Promise<ImportedSection> =>
    apiClient.patch(`/site-builder/sites/${siteId}/clones/${sectionId}/text`, { edits }).then(r => r.data);

// Rendered HTML for ONE inserted section — the editor swaps this into the iframe in place rather
// than reloading the whole bundle (which re-fetches every asset, e.g. a 121-frame hero sequence).
export const renderSiteSection = (siteId: string, sectionId: string): Promise<string> =>
    apiClient.get(`/site-builder/sites/${siteId}/sections/${sectionId}/render`,
        { responseType: 'text', transformResponse: [(d) => d] }).then(r => r.data as string);

// --- Generated pages on an imported site (specs/completed/imported-site-new-page.md) ---
// Pages that do NOT exist in the immutable bundle, synthesised at serve time from an existing
// bundle page's chrome. NB the path is /generated-pages, not /pages — /pages is already the
// component-site page CRUD, and FastAPI matches the first registered route.

export interface ImportedPage {
    page_id: string;
    page_rel: string;            // e.g. "blog.html" — also the value to send as section.page
    title: string;
    kind: 'blog_index' | 'landing';
    chrome_donor: string;        // bundle page whose <head>/nav/footer this clones
    meta_title: string;
    meta_description: string;
    og_image?: string;
    canonical_url?: string;
    noindex?: boolean;
    nav_label: string;
    include_donor_scripts: boolean;
    created_at: string;
}

export interface NavInjection {
    nav_link_id: string;
    page_id: string;
    label: string;
    href: string;
    nav_selector: string;
    enabled: boolean;
}

export interface NavCandidate {
    selector: string;
    preview_html: string;        // display only — render as TEXT, never dangerouslySetInnerHTML
    link_count: number;
}

export const listSitePages = (siteId: string): Promise<{ pages: ImportedPage[] }> =>
    apiClient.get(`/site-builder/sites/${siteId}/generated-pages`).then(r => r.data);

export const createSitePage = (siteId: string, data: {
    page_rel: string;
    title: string;
    kind?: 'blog_index' | 'landing';
    chrome_donor?: string;
    meta_title?: string;
    meta_description?: string;
    nav_label?: string;
    add_nav_link?: boolean;
    nav_selector?: string;
    include_donor_scripts?: boolean;
}): Promise<ImportedPage> =>
    apiClient.post(`/site-builder/sites/${siteId}/generated-pages`, data).then(r => r.data);

export const updateSitePage = (siteId: string, pageId: string, data: {
    title?: string;
    meta_title?: string;
    meta_description?: string;
    og_image?: string;
    canonical_url?: string;
    noindex?: boolean;
    chrome_donor?: string;
    nav_label?: string;
    include_donor_scripts?: boolean;
}): Promise<ImportedPage> =>
    apiClient.put(`/site-builder/sites/${siteId}/generated-pages/${pageId}`, data).then(r => r.data);

// Per-page head-metadata overrides for imported-static BUNDLE HOST pages (real .html files on
// disk — generated pages carry their own meta_title/meta_description/og_image/canonical_url/
// noindex on the ImportedPage entry above instead). Whole-map replace on save.
// specs/completed/sitemap-llms-seo-metadata.md Phase 4.
export interface HeadMetaEntry {
    meta_title: string;
    meta_description: string;
    og_image: string;
    canonical_url: string;
    noindex: boolean;
}

export const getSiteHeadMeta = (siteId: string): Promise<{ head_meta: Record<string, HeadMetaEntry>; pages: string[] }> =>
    apiClient.get(`/site-builder/sites/${siteId}/head-meta`).then(r => r.data);

export const saveSiteHeadMeta = (siteId: string, headMeta: Record<string, HeadMetaEntry>): Promise<{ head_meta: Record<string, HeadMetaEntry> }> =>
    apiClient.put(`/site-builder/sites/${siteId}/head-meta`, { head_meta: headMeta }).then(r => r.data);

export interface SiteFavicon {
    favicon_url: string | null;
    icon_32_url: string | null;
    icon_48_url: string | null;
    apple_touch_url: string | null;
    ico_url: string | null;
    warnings: string[];
}

export const getSiteFavicon = (siteId: string): Promise<SiteFavicon> =>
    apiClient.get(`/site-builder/sites/${siteId}/favicon`).then(r => r.data);

export const uploadSiteFavicon = (siteId: string, file: File): Promise<SiteFavicon> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(`/site-builder/sites/${siteId}/favicon`, form, { timeout: 60000, headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

export const setSiteFaviconFromLibrary = (siteId: string, assetUrl: string): Promise<SiteFavicon> =>
    apiClient.post(`/site-builder/sites/${siteId}/favicon/from-library`, { asset_url: assetUrl }, { timeout: 60000 }).then(r => r.data);

export const removeSiteFavicon = (siteId: string): Promise<SiteFavicon> =>
    apiClient.delete(`/site-builder/sites/${siteId}/favicon`).then(r => r.data);

// Site custom code (specs/completed/site-builder-custom-code.md) — site-level head/body tracking
// snippets, injected only on the custom domain / export / Netlify (never on AiMe preview links).
export interface SiteCustomCode {
    head_code: string | null; body_code: string | null; updated_at: string | null;
    can_edit: boolean; max_chars: number; detected: string[]; warnings: string[];
    live_url: string | null; netlify_connected: boolean;
    runs_on_aime_origin?: boolean;
}
export interface SiteCustomCodeCheck {
    checked_url: string | null; status_code: number | null;
    head_found: boolean; body_found: boolean; head_expected: boolean; body_expected: boolean;
}

export const getSiteCustomCode = (siteId: string): Promise<SiteCustomCode> =>
    apiClient.get(`/site-builder/sites/${siteId}/custom-code`).then(r => r.data);

// Whole-object replace: BOTH keys always sent; null / blank clears that field.
export const saveSiteCustomCode = (siteId: string, head_code: string | null, body_code: string | null): Promise<SiteCustomCode> =>
    apiClient.put(`/site-builder/sites/${siteId}/custom-code`, { head_code, body_code }).then(r => r.data);

export const checkSiteCustomCode = (siteId: string): Promise<SiteCustomCodeCheck> =>
    apiClient.get(`/site-builder/sites/${siteId}/custom-code/check`).then(r => r.data);

// Cascades: the page's sections and its nav link go with it.
export const deleteSitePage = (siteId: string, pageId: string): Promise<{ status: string; page_rel: string }> =>
    apiClient.delete(`/site-builder/sites/${siteId}/generated-pages/${pageId}`).then(r => r.data);

export const listSiteBundlePages = (siteId: string): Promise<{ pages: string[]; entrypoint: string }> =>
    apiClient.get(`/site-builder/sites/${siteId}/bundle-pages`).then(r => r.data);

export const getNavCandidates = (siteId: string, page?: string): Promise<{ candidates: NavCandidate[] }> =>
    apiClient.get(`/site-builder/sites/${siteId}/nav-candidates`, { params: page ? { page } : {} })
        .then(r => r.data);

export const getNavLinks = (siteId: string): Promise<{ nav_links: NavInjection[] }> =>
    apiClient.get(`/site-builder/sites/${siteId}/nav-links`).then(r => r.data);

export const saveNavLinks = (siteId: string, navLinks: NavInjection[]): Promise<{ nav_links: NavInjection[] }> =>
    apiClient.put(`/site-builder/sites/${siteId}/nav-links`, { nav_links: navLinks }).then(r => r.data);

// ==================== AIME-hosted custom domain (Site Builder) ====================

export interface SiteCustomDomain {
    connected: boolean;
    domain_id?: string;
    domain?: string;
    status?: string;
    ssl_status?: string;
    dns_target_ip?: string | null;
    dns_resolves?: boolean;
    dns_resolved_to?: string | null;
    dns_points_to_aime?: boolean;
    ssl_live?: boolean;
}

export const connectSiteCustomDomain = (siteId: string, domain: string): Promise<SiteCustomDomain> =>
    apiClient.post(`/site-builder/sites/${siteId}/custom-domain`, { domain }).then(r => r.data);

export const getSiteCustomDomain = (siteId: string): Promise<SiteCustomDomain> =>
    apiClient.get(`/site-builder/sites/${siteId}/custom-domain`).then(r => r.data);

export const disconnectSiteCustomDomain = (siteId: string): Promise<{ disconnected: boolean; domain: string }> =>
    apiClient.delete(`/site-builder/sites/${siteId}/custom-domain`).then(r => r.data);

export type SeoCrawlerFileKey = 'robots' | 'sitemap' | 'llms' | 'llms-full';

export interface SeoFilePreview {
    file: SeoCrawlerFileKey;
    content_type: string;
    content: string;
    byte_size: number;
    url_count: number;
}

export const getCustomDomainSeoFilePreview = (domainId: string, file: SeoCrawlerFileKey): Promise<SeoFilePreview> =>
    apiClient.get(`/custom-domains/${domainId}/seo-files/preview`, { params: { file } }).then(r => r.data);

// ==================== Traffic Analytics ====================

export interface TrafficSummary {
    page_views: number;
    unique_visitors: number;
    avg_time_seconds: number;
    engaged_session_pct: number;
}

export interface TrafficDaily {
    date: string;
    page_views: number;
    unique_visitors: number;
}

export interface TrafficPage {
    page_slug: string;
    page_views: number;
}

export interface TrafficResponse {
    source: 'aime' | 'ga';
    date_range: { from: string; to: string; days: number };
    summary: TrafficSummary;
    daily: TrafficDaily[];
    top_pages: TrafficPage[];
}

export const getTraffic = (projectId: string, days: number, source: 'aime' | 'ga' = 'aime'): Promise<TrafficResponse> => {
    const url = source === 'ga' ? '/google-analytics/traffic' : '/analytics/traffic';
    return apiClient.get(url, { params: { project_id: projectId, days } }).then(r => r.data);
};

// ==================== Google Analytics OAuth ====================

export interface GAProperty {
    property_id: string;
    display_name: string;
    account_name: string;
}

export const startGAConnect = (projectId: string): Promise<{ auth_url: string }> =>
    apiClient.get('/google-analytics/connect', { params: { project_id: projectId } }).then(r => r.data);

export const listGAProperties = (projectId: string): Promise<{ properties: GAProperty[] }> =>
    apiClient.get('/google-analytics/properties', { params: { project_id: projectId } }).then(r => r.data);

export const selectGAProperty = (projectId: string, propertyId: string, propertyName: string): Promise<void> =>
    apiClient.post('/google-analytics/select-property', { property_id: propertyId, property_name: propertyName }, { params: { project_id: projectId } }).then(r => r.data);

export const disconnectGA = (projectId: string): Promise<void> =>
    apiClient.delete('/google-analytics/disconnect', { params: { project_id: projectId } }).then(r => r.data);

// ── Webflow CMS ───────────────────────────────────────────────────────────────

export interface WebflowSite {
    webflow_site_id: string;
    site_display_name: string | null;
    blog_collection_name: string | null;
    connected_email: string | null;
    connected_at: string | null;
    last_published_at: string | null;
    last_publish_error: string | null;
    field_map: Record<string, string>;
    is_fully_configured: boolean;
    /** True after the OAuth grant was revoked and credentials wiped: the row
     *  still has connected_email and site metadata, but the access token is gone.
     *  Frontend uses this to show "Reconnect Webflow" instead of "Disconnect". */
    is_credentials_cleared?: boolean;
}

export interface WebflowAuthorisedSite { id: string; name: string; }
export interface WebflowCollection { id: string; displayName: string; }
export interface WebflowField { id: string; displayName: string; type: string; }

export const startWebflowOAuth = (projectId: string): Promise<{ auth_url: string }> =>
    apiClient.post('/webflow/oauth/start', { project_id: projectId }).then(r => r.data);

export const listWebflowAuthorisedSites = (webflowSiteId: string): Promise<{ sites: WebflowAuthorisedSite[] }> =>
    apiClient.get(`/webflow/sites/${webflowSiteId}/authorised-sites`).then(r => r.data);

export const selectWebflowSite = (body: { webflow_site_id: string; webflow_site_external_id: string; site_display_name: string }): Promise<{ collections: WebflowCollection[] }> =>
    apiClient.post('/webflow/select-site', body).then(r => r.data);

export const selectWebflowCollection = (body: { webflow_site_id: string; blog_collection_id: string }): Promise<{ fields: WebflowField[]; auto_detected_field_map: Record<string, string | null>; collection_name: string }> =>
    apiClient.post('/webflow/select-collection', body).then(r => r.data);

export const saveWebflowFieldMap = (body: { webflow_site_id: string; field_map: Record<string, string> }): Promise<{ ok: boolean }> =>
    apiClient.post('/webflow/save-field-map', body).then(r => r.data);

export const listWebflowSites = (projectId: string): Promise<{ sites: WebflowSite[] }> =>
    apiClient.get('/webflow/sites', { params: { project_id: projectId } }).then(r => r.data);

export const disconnectWebflowSite = (webflowSiteId: string): Promise<{ ok: boolean }> =>
    apiClient.delete(`/webflow/sites/${webflowSiteId}`).then(r => r.data);

// ---------------------------------------------------------------------------
// Internal Linking Engine
// ---------------------------------------------------------------------------

export interface InternalLinkSuggestion {
    target_task_id: string;
    target_url: string;
    target_title: string;
    anchor_text: string;
    sentence_context: string;
    confidence: number;
    insertion_html: string;
}

export const suggestInternalLinks = async (
    taskId: string,
    opts?: { maxLinks?: number; minConfidence?: number },
): Promise<{ suggestions: InternalLinkSuggestion[]; total: number }> => {
    const response = await apiClient.post('/seo/internal-links/suggest', {
        task_id: taskId,
        max_links: opts?.maxLinks ?? 5,
        min_confidence: opts?.minConfidence ?? 0.5,
    });
    return response.data;
};

export const applyInternalLinks = async (
    taskId: string,
    suggestions: InternalLinkSuggestion[],
    mode: 'manual' | 'suggested_accepted' | 'auto' = 'suggested_accepted',
): Promise<{ applied: number; skipped: number; dropped: number; edges: string[] }> => {
    const response = await apiClient.post('/seo/internal-links/apply', {
        task_id: taskId,
        suggestions,
        mode,
    });
    return response.data;
};

export const reindexProjectLinks = async (projectId: string): Promise<{ indexed: number; skipped: number; total_articles: number }> => {
    const response = await apiClient.post(`/seo/internal-links/reindex/${projectId}`);
    return response.data;
};

export const getLinkGraph = async (projectId: string): Promise<{ nodes: any[]; edges: any[] }> => {
    const response = await apiClient.get(`/seo/internal-links/graph/${projectId}`);
    return response.data;
};

export interface ReverseLinkOpportunity {
    source_task_id: string;
    source_url: string;
    source_title: string;
    anchor_text: string;
    sentence_context: string;
    confidence: number;
    insertion_html: string;
}

export const getReverseLinkOpportunities = async (
    taskId: string,
): Promise<{ opportunities: ReverseLinkOpportunity[] }> => {
    const response = await apiClient.post(`/seo/internal-links/reverse/${taskId}`);
    return response.data;
};

export const updateProjectSettings = async (
    projectId: string,
    settings: { auto_internal_links?: boolean; auto_internal_links_reverse?: boolean; allow_emojis?: boolean; content_locale?: string },
): Promise<any> => {
    const response = await apiClient.patch(`/projects/${projectId}/settings`, settings);
    return response.data;
};

// ── SERP-driven Content Plan (serp-content-plan) ──────────────────────────

export interface ContentPlanPillar {
    name: string;
    intent: string;
}

export interface ContentPlanItem {
    id: string;
    title: string;
    target_keyword: string;
    pillar: string;
    suggested_publish_at: string; // UTC ISO with Z suffix
    target_word_count: number;
    competitors: string[];
    difficulty: string;
    // ── social channel extension (all optional; absent ⇒ article item) ──
    item_type?: 'article' | 'social_post';
    task_id?: string;
    publication_id?: string;
    platform?: string;           // linkedin|twitter|facebook|instagram
    social_account_id?: string | null;
    source_task_id?: string;
    source_article?: string;
    content_preview?: string;
    // ── aime_site channel extension ──
    site_project_id?: string;
}

export interface ContentPlanCost {
    serp_calls: number;
    gemini_tokens_input: number;
    gemini_tokens_output: number;
    usd_estimate: number;
}

export interface ContentPlanDraft {
    generated_at: string;
    locale: string;
    cadence: string;
    channel: string;
    project_tz: string;
    low_confidence: boolean;
    pillars: ContentPlanPillar[];
    items: ContentPlanItem[];
    cost: ContentPlanCost;
    approved_at: string | null;
    approved_task_ids: string[];
    approved_publication_ids: string[];
    updated_at?: string;
    truncated_candidate_count?: number;
    site_project_id?: string;  // aime_site channel: target SiteProject
    site_name?: string;
}

export interface ContentPlanGenerateRequest {
    project_id: string;
    seeds: string[];
    locale: string;
    cadence: 'daily' | '3x_week' | 'weekly';
    channel: string;
    force_gemini_only?: boolean;  // NEW — bypass SERP; defaults false
    site_project_id?: string;  // aime_site channel: target SiteProject, chosen in step 1
}

export interface ContentPlanPatchRequest {
    items: ContentPlanItem[];
}

export interface ContentPlanRegenerateRowRequest {
    row_id: string;
    seed_override?: string;
}

export interface ContentPlanApproveRequest {
    project_id: string;
    items: ContentPlanItem[];
}

export interface ContentPlanApproveResponse {
    tasks_created: number;
    publications_created: number;
    task_ids: string[];
    publication_ids: string[];
    skipped?: { row_id: string; reason: string }[];
    warnings?: string[];
}

export interface SocialPlanCandidate {
    task_id: string;
    platform: string;
    source_task_id: string | null;
    source_article: string;
    title: string;
    content_preview: string;
    content_length: number;
    hero_image_url: string | null;
    eligible: boolean;
    ineligible_reason: string | null;
}

export interface SocialPlanCandidatesResponse {
    project_id: string;
    candidates: SocialPlanCandidate[];
    total_eligible: number;
    total_ineligible: number;
    connected_platforms: string[];
}

export interface ContentPlanApproveConflictResponse {
    already_approved: boolean;
    approved_at: string;
    task_ids: string[];
    publication_ids: string[];
}

export const generateContentPlan = async (
    payload: ContentPlanGenerateRequest,
): Promise<ContentPlanDraft> => {
    const response = await apiClient.post('/seo/content-plan/generate', payload);
    return response.data;
};

export const getContentPlanDraft = async (
    projectId: string,
): Promise<ContentPlanDraft | null> => {
    try {
        const response = await apiClient.get(`/seo/content-plan/${projectId}`);
        return response.data;
    } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
    }
};

export const patchContentPlanDraft = async (
    projectId: string,
    payload: ContentPlanPatchRequest,
): Promise<ContentPlanDraft> => {
    const response = await apiClient.patch(`/seo/content-plan/${projectId}`, payload);
    return response.data;
};

export const regenerateContentPlanRow = async (
    projectId: string,
    payload: ContentPlanRegenerateRowRequest,
): Promise<ContentPlanItem> => {
    const response = await apiClient.post(
        `/seo/content-plan/${projectId}/regenerate-row`,
        payload,
    );
    return response.data;
};

export const approveContentPlan = async (
    payload: ContentPlanApproveRequest,
): Promise<ContentPlanApproveResponse> => {
    const response = await apiClient.post('/seo/content-plan/approve', payload);
    return response.data;
};

export const discardContentPlanDraft = async (projectId: string): Promise<void> => {
    await apiClient.delete(`/seo/content-plan/${projectId}`);
};

export const getSocialPlanCandidates = async (
    projectId: string,
): Promise<SocialPlanCandidatesResponse> => {
    const response = await apiClient.get(`/seo/content-plan/social-candidates/${projectId}`);
    return response.data;
};

// ── Content Quickstart service functions ─────────────────────────────────────

export interface QuickstartState {
    has_masterplan: boolean;
    masterplan_streams: string[];
    has_approved_plan: boolean;
    next_publications: {
        task_id: string;
        title: string;
        scheduled_for: string;
        platform: string;
    }[];
    masterplan_skipped: boolean;
    plan_skipped: boolean;
}

export interface QuickstartGeneratePlanPayload {
    project_id: string;
    seeds: string[];
    locale: string;
    cadence: 'daily' | '3x_week' | 'weekly';
    channel: string;
}

export interface RefreshSerpResult {
    refreshed: number;
    draft: ContentPlanDraft;
    warnings: string[];
}

export const getQuickstartState = async (
    projectId: string,
): Promise<QuickstartState> => {
    const response = await apiClient.get(`/onboarding/quickstart-state/${projectId}`);
    return response.data;
};

export const skipQuickstartStep = async (
    step: 'masterplan' | 'plan',
): Promise<void> => {
    await apiClient.post('/onboarding/quickstart-skip', { step });
};

export const quickstartGeneratePlan = async (
    payload: QuickstartGeneratePlanPayload,
): Promise<ContentPlanDraft> => {
    const response = await apiClient.post('/onboarding/quickstart-generate-plan', payload);
    return response.data;
};

export const quickstartApprovePlan = async (payload: {
    project_id: string;
    items: ContentPlanItem[];
}): Promise<ContentPlanApproveResponse> => {
    const response = await apiClient.post('/onboarding/quickstart-approve-plan', payload);
    return response.data;
};

export const refreshContentPlanSerp = async (
    projectId: string,
): Promise<RefreshSerpResult> => {
    const response = await apiClient.post(`/seo/content-plan/${projectId}/refresh-serp`);
    return response.data;
};

export const quickstartPatchPlan = async (
    projectId: string,
    payload: ContentPlanPatchRequest,
): Promise<ContentPlanDraft> => {
    const response = await apiClient.patch(`/onboarding/quickstart-patch-plan/${projectId}`, payload);
    return response.data;
};

export interface QuickstartPlanStatus {
    has_draft: boolean;
    approved_at: string | null;
    low_confidence: boolean;
}

export const getQuickstartPlanStatus = async (
    projectId: string,
): Promise<QuickstartPlanStatus> => {
    const response = await apiClient.get(`/onboarding/quickstart-plan-status/${projectId}`);
    return response.data;
};

// ==================== Low-CTR Opportunities (SEO) ====================

export interface LowCtrOpportunity {
    page: string;
    impressions: number;
    clicks: number;
    ctr: number;               // 0-1 float
    position: number | null;
    top_query: string | null;
    task_id: string | null;
    task_name: string | null;
    suggested_action: string;
}

export interface LowCtrOpportunitiesResponse {
    project_id: string;
    opportunities: LowCtrOpportunity[];
    summary: {
        opportunities_found: number;
        linked_to_tasks: number;
        gsc_connected: boolean;
        params: Record<string, any>;
    };
}

export const getLowCtrOpportunities = async (
    projectId: string,
    params: { days?: number; min_impressions?: number; max_ctr?: number; limit?: number } = {},
): Promise<LowCtrOpportunitiesResponse> => {
    const response = await apiClient.get(`/seo/projects/${projectId}/low-ctr-opportunities`, { params });
    return response.data;
};

// ---------------------------------------------------------------------------
// Index Coverage Rescue (T4) — GSC URL Inspection index coverage
// ---------------------------------------------------------------------------

export interface IndexCoverageRow {
    id: number;
    page_url: string;
    coverage_state: string | null;
    coverage_state_key: string | null;
    verdict: string | null;
    last_crawl_time: string | null;
    google_canonical: string | null;
    user_canonical: string | null;
    task_id: string | null;
    task_name: string | null;
    has_rewrite: boolean;
    last_inspected_at: string;
    /** Populated locally after calling getIndexCoverageRewrite — not from the list endpoint. */
    rewrite?: any;
}

export interface IndexCoverageResponse {
    project_id: string;
    rows: IndexCoverageRow[];
    summary: {
        total: number;
        crawled_not_indexed: number;
        gsc_connected: boolean;
        last_inspected_at: string | null;
        /** GSC property siteUrl — included so the frontend can build deep-links client-side. */
        site_url: string | null;
    };
}

export interface IndexCoverageRefreshResult {
    project_id: string;
    inspected: number;
    skipped_cached: number;
    skipped_quota: number;
    rows_updated: number;
    daily_budget_remaining: number;
    gsc_connected: boolean;
}

export interface IndexCoverageRewriteResult {
    row_id: number;
    suggestion: {
        summary: string;
        missing_originality_signals: string[];
        recommended_sections: string[];
        data_to_add: string[];
        rewrite_brief: string;
    } | null;
    error?: string;
}

export const getIndexCoverage = async (
    projectId: string,
    params: { state?: string; limit?: number } = {},
): Promise<IndexCoverageResponse> => {
    const response = await apiClient.get(
        `/seo/projects/${projectId}/index-coverage`,
        { params },
    );
    return response.data;
};

export const refreshIndexCoverage = async (
    projectId: string,
): Promise<IndexCoverageRefreshResult> => {
    // On-demand sweep can take up to ~300s on large sites — override the
    // default 120s apiClient timeout (same pattern as crmImportApi.suggest()).
    const response = await apiClient.post(
        `/seo/projects/${projectId}/index-coverage/refresh`,
        {},
        { timeout: 300000 },
    );
    return response.data;
};

export const getIndexCoverageRewrite = async (
    rowId: number,
): Promise<IndexCoverageRewriteResult> => {
    const response = await apiClient.post(`/seo/index-coverage/${rowId}/rewrite-suggestion`);
    return response.data;
};

export const getIndexCoverageRecheckLink = async (
    rowId: number,
): Promise<{ row_id: number; gsc_inspect_url: string; note: string }> => {
    const response = await apiClient.get(`/seo/index-coverage/${rowId}/recheck-link`);
    return response.data;
};

// ---------------------------------------------------------------------------
// Keyword Cannibalization Analysis
// ---------------------------------------------------------------------------

export interface CannibalizationQueryRow {
    query: string;
    impressions: number;
    clicks: number;
    position: number | null;
}

export interface CannibalizationCluster {
    label: string;
    intent: 'informational' | 'commercial' | 'transactional';
    queries: CannibalizationQueryRow[];
    impressions: number;
}

export interface CannibalizationSplitCandidate {
    page: string;
    total_impressions: number;
    clusters: CannibalizationCluster[];
    suggested_action: string;
    suggested_seeds: string[];
    task_id: string | null;
    task_name: string | null;
}

export interface CannibalizationPageEntry {
    page: string;
    impressions: number;
    clicks: number;
    position: number | null;
    task_id: string | null;
    task_name: string | null;
}

export interface CannibalizationConsolidateCandidate {
    query: string;
    total_impressions: number;
    pages: CannibalizationPageEntry[];
    suggested_action: string;
}

export interface CannibalizationAnalysis {
    status: 'ok' | 'insufficient_data' | 'not_run';
    computed_at: string | null;
    gsc_latest_date: string | null;
    params: Record<string, any>;
    data_floor: Record<string, any>;
    split_candidates: CannibalizationSplitCandidate[];
    consolidate_candidates: CannibalizationConsolidateCandidate[];
    cost: {
        gemini_calls: number;
        gemini_tokens_input: number;
        gemini_tokens_output: number;
        usd_estimate: number;
    };
    truncated?: boolean;
    truncated_message?: string;
}

export const getCannibalizationAnalysis = async (
    projectId: string,
): Promise<CannibalizationAnalysis> => {
    const response = await apiClient.get(`/seo/projects/${projectId}/cannibalization`);
    return response.data;
};

export const analyzeCannibalization = async (
    projectId: string,
    body: { force: boolean } = { force: false },
): Promise<CannibalizationAnalysis> => {
    const response = await apiClient.post(
        `/seo/projects/${projectId}/cannibalization/analyze`,
        body,
        { timeout: 300000 }, // 5 min — Gemini clustering can be slow
    );
    return response.data;
};

// ---------------------------------------------------------------------------
// AI Referrer Tracking — "Pages AI Recommends"
// ---------------------------------------------------------------------------

export interface AIReferrerPlatformCount {
    platform: string;
    label: string;
    visits: number;
}

export interface AIReferrerPage {
    page: string;
    platforms: AIReferrerPlatformCount[];
    total_visits: number;
    prev_total_visits: number;
    trend: 'up' | 'down' | 'flat';
}

export interface AIReferrerSummaryResponse {
    project_id: string;
    pages: AIReferrerPage[];
    platform_totals: AIReferrerPlatformCount[];
    summary: {
        total_ai_visits: number;
        prev_total_ai_visits: number;
        platforms_seen: number;
        pages_cited: number;
        params: Record<string, any>;
    };
}

export const getAIReferrers = async (
    projectId: string,
    params: { days?: number; limit?: number } = {},
): Promise<AIReferrerSummaryResponse> => {
    const response = await apiClient.get(`/seo/projects/${projectId}/ai-referrers`, { params });
    return response.data;
};

// ---------------------------------------------------------------------------
// GSC Opportunities — Striking-distance keywords + Low-CTR title rewrites
// ---------------------------------------------------------------------------

export interface StrikingDistanceOpportunity {
    page: string;
    keyword: string;
    impressions: number;
    clicks: number;
    ctr: number;               // 0-1 float
    position: number | null;
}

export interface StrikingDistanceResponse {
    project_id: string;
    opportunities: StrikingDistanceOpportunity[];
    summary: {
        opportunities_found: number;
        gsc_connected: boolean;
        params: Record<string, any>;
    };
}

/**
 * LinkOpportunity — shape returned by the enrich endpoint (and the backend
 * internal_linker.find_link_opportunities). Uses source_* fields (the article
 * that could link TO the striking-distance page). This is DISTINCT from the
 * existing InternalLinkSuggestion interface which uses target_* fields.
 *
 * When applying a link suggestion from the enrich panel, the frontend must
 * role-swap: the article being edited is source_task_id, and the link target
 * is the striking-distance page's task_id. See StrikingDistancePanel.tsx for
 * the apply adapter.
 */
export interface LinkOpportunity {
    source_task_id: string;    // existing article that could link TO us
    source_url: string;
    source_title: string;
    anchor_text: string;
    sentence_context: string;
    confidence: number;        // 0..1 cosine similarity
    insertion_html: string;    // ready-to-splice <a> tag
}

export interface StrikingDistanceEnrichResponse {
    page: string;
    keyword: string;
    task_id: string | null;
    missing_sections: string[];
    internal_link_suggestions: LinkOpportunity[];
    low_confidence: boolean;
}

export const getStrikingDistanceOpportunities = async (
    projectId: string,
    params: {
        days?: number;
        min_impressions?: number;
        pos_min?: number;
        pos_max?: number;
        limit?: number;
    } = {},
): Promise<StrikingDistanceResponse> => {
    const response = await apiClient.get(`/seo/projects/${projectId}/striking-distance`, { params });
    return response.data;
};

export const enrichStrikingDistance = async (
    projectId: string,
    page: string,
    keyword: string,
): Promise<StrikingDistanceEnrichResponse> => {
    const response = await apiClient.post(
        `/seo/projects/${projectId}/striking-distance/enrich`,
        { page, keyword },
    );
    return response.data;
};

export interface ApplyTarget {
    type: 'task' | 'landing_page' | 'none';
    id: string | null;
}

export interface TitleRewriteResponse {
    page: string;
    current_title: string;
    rewritten_title: string;
    competitor_titles: string[];
    rationale: string;
    apply_target: ApplyTarget;
    low_confidence: boolean;
}

export const rewriteTitle = async (
    projectId: string,
    payload: { page: string; top_query: string | null; task_id: string | null },
): Promise<TitleRewriteResponse> => {
    const response = await apiClient.post(`/seo/projects/${projectId}/title-rewrite`, payload);
    return response.data;
};

export const applyTitleRewrite = async (
    projectId: string,
    apply_target: ApplyTarget,
    rewritten_title: string,
): Promise<{ applied: boolean; target: ApplyTarget; message: string }> => {
    const response = await apiClient.post(`/seo/projects/${projectId}/title-rewrite/apply`, {
        apply_target,
        rewritten_title,
    });
    return response.data;
};

// ==================== Site Builder — Pricing Calculator Generator ====================

export interface PricingInputPayload {
    label: string;
    unit?: string;
    low?: string | number;
    high?: string | number;
    notes?: string;
}

export interface GeneratePricingPagePayload {
    project_id: string;
    brand_guide_id?: string | null;
    service: string;
    area: string;
    currency?: string;
    form_id?: string | null;
    pricing_inputs: PricingInputPayload[];
}

/**
 * POST /api/site-builder/generate-pricing-page
 *
 * Returns { sections: SectionInstance[] } — the wizard splices these into the
 * current page document and triggers autosave via updateDoc.
 */
export const generatePricingPage = (payload: GeneratePricingPagePayload): Promise<{ sections: any[] }> =>
    apiClient.post('/site-builder/generate-pricing-page', payload, { timeout: 120000 }).then(r => r.data);

// ---------------------------------------------------------------------------
// GBP Category Gap (T9) — Local Categories tab
// ---------------------------------------------------------------------------

export interface GbpOwnCategories {
    project_id: string;
    own_categories: string[];
    service: string | null;
    city: string | null;
    locale: string;
}

export interface GbpCategoryGapCompetitor {
    name: string;
    rank: number;
    categories: string[];
    url: string;
    rating: number | null;
    reviews_count: number | null;
}

export interface GbpMissingCategory {
    category: string;
    used_by_count: number;
    used_by: string[];
}

export interface GbpCategoryGapReport {
    service: string | null;
    city: string | null;
    locale: string;
    query: string;
    competitors: GbpCategoryGapCompetitor[];
    own_categories: string[];
    missing_categories: GbpMissingCategory[];
    shared_categories: string[];
    low_confidence: boolean;
    data_source: string;
    provider: string;
    refreshed_at: string;
}

export interface GbpCategoryGapResponse {
    project_id: string;
    report: GbpCategoryGapReport | null;
}

/** GET /api/seo/projects/{id}/gbp-categories — never spends SERP credits */
export const getGbpCategories = (projectId: string): Promise<GbpOwnCategories> =>
    apiClient.get(`/seo/projects/${projectId}/gbp-categories`).then(r => r.data);

/** PUT /api/seo/projects/{id}/gbp-categories — persist own categories + service/city */
export const saveGbpCategories = (
    projectId: string,
    body: { own_categories: string[]; service?: string; city?: string },
): Promise<GbpOwnCategories> =>
    apiClient.put(`/seo/projects/${projectId}/gbp-categories`, body).then(r => r.data);

/** GET /api/seo/projects/{id}/gbp-category-gap — cached report, never spends SERP */
export const getGbpCategoryGap = (projectId: string): Promise<GbpCategoryGapResponse> =>
    apiClient.get(`/seo/projects/${projectId}/gbp-category-gap`).then(r => r.data);

/**
 * POST /api/seo/projects/{id}/gbp-category-gap/refresh
 *
 * Fetches live Google map-pack data — spends SERP quota.
 * 90 s timeout (SERP local fetch can be slow; safely under nginx 300 s limit).
 */
export const refreshGbpCategoryGap = (
    projectId: string,
    body: { service?: string; city?: string },
): Promise<GbpCategoryGapResponse> =>
    apiClient
        .post(`/seo/projects/${projectId}/gbp-category-gap/refresh`, body, { timeout: 90000 })
        .then(r => r.data);

// ---------------------------------------------------------------------------
// Location Page Generator ("Areas We Serve") — T13
// ---------------------------------------------------------------------------

export interface AreaInput {
    area_name: string;
    region?: string;
}

export interface GenerateLocationPagesPayload {
    areas: AreaInput[];
    services_text?: string;
    include_testimonials?: boolean;
    similarity_threshold?: number;
}

export interface SimilarityReportEntry {
    a: string;
    b: string;
    similarity: number;
    regenerated: boolean;
}

export interface GenerateLocationPagesResponse {
    pages: any[];
    similarity_report: SimilarityReportEntry[];
    warnings: string[];
}

export interface CommitLocationPagesPayload {
    pages: any[];
    index_title?: string;
    index_slug?: string;
    add_nav_link?: boolean;
}

export interface CommitLocationPagesResponse {
    pages: any[];
    index_page_id: string;
}

export interface LocationPagesListResponse {
    pages: any[];
    index_page: any | null;
}

/**
 * POST /api/site-builder/sites/{siteId}/locations/generate
 *
 * Generates area pages (preview only — nothing persisted).  Long-running
 * (multiple Gemini calls + possible regeneration): timeout set to 300 000 ms
 * to match the nginx proxy_read_timeout and the existing /site-builder/generate
 * timeout used by GenerationWizard.tsx:458.
 */
export const generateLocationPages = (
    siteId: string,
    payload: GenerateLocationPagesPayload,
): Promise<GenerateLocationPagesResponse> =>
    apiClient
        .post(`/site-builder/sites/${siteId}/locations/generate`, payload, { timeout: 300000 })
        .then(r => r.data);

/**
 * POST /api/site-builder/sites/{siteId}/locations/commit
 *
 * Appends the previewed pages + auto-built index page into the site document.
 * This is the only DB-write in the generate → preview → commit flow.
 */
export const commitLocationPages = (
    siteId: string,
    payload: CommitLocationPagesPayload,
): Promise<CommitLocationPagesResponse> =>
    // 180s — commit calls build_index_page (one Gemini call) which can take
    // up to ~30s; the default 120s is tight when the model is slow.
    apiClient.post(`/site-builder/sites/${siteId}/locations/commit`, payload, { timeout: 180000 }).then(r => r.data);

/**
 * GET /api/site-builder/sites/{siteId}/locations
 *
 * Lists area and index pages currently present in the site document.
 */
export const listLocationPages = (siteId: string): Promise<LocationPagesListResponse> =>
    apiClient.get(`/site-builder/sites/${siteId}/locations`).then(r => r.data);

/**
 * DELETE /api/site-builder/sites/{siteId}/locations/{pageId}
 *
 * Removes an area page from the site document, prunes the index link, and
 * removes its sitemap indexability row (safe if site was never published).
 */
export const deleteLocationPage = (siteId: string, pageId: string): Promise<{ status: string }> =>
    apiClient.delete(`/site-builder/sites/${siteId}/locations/${pageId}`).then(r => r.data);

// ── Citation Audit API ────────────────────────────────────────────────────────

export interface BusinessNap {
    nap_id: string;
    project_id: string;
    business_name: string | null;
    address_line: string | null;
    locality: string | null;
    region: string | null;
    postal_code: string | null;
    country: string | null;
    phone: string | null;
    website_url: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface BusinessNapInput {
    business_name?: string;
    address_line?: string;
    locality?: string;
    region?: string;
    postal_code?: string;
    country?: string;
    phone?: string;
    website_url?: string;
}

export interface NapDiff {
    field: string;
    canonical: string;
    found: string;
    match: string;
}

export interface DirectoryResult {
    directory_key: string;
    directory_name: string;
    status: 'listed_consistent' | 'listed_inconsistent' | 'not_found' | 'check_failed';
    listing_url: string | null;
    found_nap: { business_name?: string | null; phone?: string | null; address?: string | null } | null;
    diffs: NapDiff[];
    reason: string | null;
    search_url: string | null;
}

export interface CitationAuditRecord {
    citation_audit_id: string;
    project_id: string;
    nap_snapshot: BusinessNapInput | null;
    locale: string;
    status: 'running' | 'complete' | 'failed';
    directories_total: number | null;
    consistent_count: number | null;
    inconsistent_count: number | null;
    not_found_count: number | null;
    check_failed_count: number | null;
    results_json?: DirectoryResult[] | null;
    error_message: string | null;
    created_at: string | null;
    completed_at: string | null;
}

/** GET /api/seo/nap/{project_id} */
export const getBusinessNap = (projectId: string): Promise<{ exists: boolean; nap: BusinessNap | null }> =>
    apiClient.get(`/seo/nap/${projectId}`).then(r => r.data);

/** PUT /api/seo/nap/{project_id} */
export const saveBusinessNap = (projectId: string, data: BusinessNapInput): Promise<{ nap: BusinessNap }> =>
    apiClient.put(`/seo/nap/${projectId}`, data).then(r => r.data);

/** POST /api/seo/citations/run */
export const runCitationAudit = (projectId: string, locale?: string): Promise<{ citation_audit_id: string; status: string; directories_count: number }> =>
    apiClient.post('/seo/citations/run', { project_id: projectId, locale }).then(r => r.data);

/** GET /api/seo/citations/{citation_audit_id} */
export const getCitationAudit = (citationAuditId: string): Promise<CitationAuditRecord> =>
    apiClient.get(`/seo/citations/${citationAuditId}`).then(r => r.data);

/** GET /api/seo/citations/history/{project_id} */
export const getCitationHistory = (projectId: string): Promise<CitationAuditRecord[]> =>
    apiClient.get(`/seo/citations/history/${projectId}`).then(r => r.data);

// ============ Form Intake ============

export interface FormIntakeForm {
  form_id: string;
  merge_repeat_submissions?: boolean;
  name: string;
  schema: FormIntakeSchemaDef;
  status: string; // draft | provisioning | published | failed
  crm_target?: {
    parent_module: string;
    groups: Record<string, { module: string; parent_link_field: string }>;
  };
  target_crm?: string; // 'local' | 'company'
  created_at?: string;
  updated_at?: string;
  submission_count?: number;
  deletable?: boolean;
  public_enabled?: boolean;
  public_token?: string | null;
  public_path?: string | null;
  lead_source?: string | null;
  booking_type_id?: string | null;
  project_id?: string | null;
  style?: FormIntakeStyleDef | null;
}

export interface FormIntakeInvite {
  invite_id?: string;
  token: string;
  fill_path: string;
  client_email?: string;
  client_label?: string;
  expires_at?: string;
  requires_pin?: boolean;
}

export interface FormIntakeSubmission {
  submission_id: string;
  source?: string;
  submitted_at?: string;
  crm_synced_at?: string;
  crm_sync_error?: string;
  crm_parent_record_id?: string;
  crm_child_record_ids?: any;
  data?: any;
  invite_client_label?: string | null;
  invite_client_email?: string | null;
  updated_at?: string | null;
  revision_count?: number;
}

// Schema used in both FormIntakeModule.tsx and PublicFormFill.tsx
export interface FormIntakeFieldDef {
  id: string;
  type: string;
  label: string;
  required?: boolean;
  options?: string[];
  help?: string;
}

export interface FormIntakeSectionDef {
  key: string;
  title: string;
  repeating: boolean;
  item_label?: string;
  description?: string;
  fields: FormIntakeFieldDef[];
  page_break_before?: boolean;
}

export interface FormIntakeSchemaDef {
  name: string;
  sections: FormIntakeSectionDef[];
}

// Constrained typed style fields only — never raw CSS. All optional; colours are '#rrggbb'.
export interface FormIntakeStyleDef {
  header_color?: string;
  text_color?: string;
  background_color?: string;
  page_background?: string;
  input_background?: string;
  input_text_color?: string;
  border_color?: string;
  button_color?: string;
  button_text_color?: string;
  font_family?: string;
}

// Per-module `_c` field diff returned by GET /form-intake/forms/{id}/field-diff
export interface FormIntakeFieldDiffModule {
  existing: string[];
  to_create: string[];
  already_present: string[];
}

export type FormIntakeFieldDiff = Record<string, FormIntakeFieldDiffModule>;

// GET /form-intake/forms/{id}/provisioning-status
export interface FormIntakeProvisioningStatus {
  target_crm: string;
  remote_ready: boolean;
  reason?: string | null;
  configured: boolean;
  enabled: boolean;
  installed_at?: string | null;
}

export const formIntakeApi = {
  upload: async (file: File, projectId?: string): Promise<FormIntakeForm> => {
    const fd = new FormData();
    fd.append('file', file);
    if (projectId) fd.append('project_id', projectId);
    const res = await apiClient.post('/form-intake/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  createForm: async (name?: string, projectId?: string): Promise<FormIntakeForm> => {
    const res = await apiClient.post('/form-intake/forms', { name, project_id: projectId });
    return res.data;
  },
  listForms: async (): Promise<FormIntakeForm[]> => {
    const res = await apiClient.get('/form-intake/forms');
    return res.data;
  },
  getForm: async (id: string): Promise<FormIntakeForm> => {
    const res = await apiClient.get(`/form-intake/forms/${id}`);
    return res.data;
  },
  updateForm: async (id: string, body: { name?: string; schema?: FormIntakeSchemaDef; crm_target?: any; target_crm?: string; booking_type_id?: string | null; project_id?: string | null; style?: FormIntakeStyleDef | null }): Promise<FormIntakeForm> => {
    const res = await apiClient.put(`/form-intake/forms/${id}`, body);
    return res.data;
  },
  // Presentation-only update (style + per-section page breaks) — allowed even while the form
  // is published, unlike updateForm above which 409s on a published form.
  updatePresentation: async (id: string, body: { style?: FormIntakeStyleDef | null; page_breaks?: Record<string, boolean> }): Promise<FormIntakeForm> => {
    const res = await apiClient.put(`/form-intake/forms/${id}/presentation`, body);
    return res.data;
  },
  // AI-assisted page-break suggestion over the existing schema's section titles. Returns
  // suggestions only — never persists; the caller confirms then calls updatePresentation.
  suggestPageBreaks: async (id: string): Promise<{ suggested_keys: string[]; source: 'gemini' | 'heuristic' }> => {
    const res = await apiClient.post(`/form-intake/forms/${id}/suggest-page-breaks`);
    return res.data;
  },
  // The two bootstrap sources for the Style tab's reset buttons, plus the resolved value a NEW
  // form would be seeded with (computed by the same precedence the backend uses at create time).
  getStyleDefaults: async (formId: string): Promise<{
    brand: FormIntakeStyleDef | null;
    saved_default: FormIntakeStyleDef | null;
    resolved: FormIntakeStyleDef | null;
  }> => {
    const res = await apiClient.get(`/form-intake/forms/${formId}/style-defaults`);
    return res.data;
  },
  getProjectStyleDefault: async (projectId: string): Promise<{ style: FormIntakeStyleDef | null }> => {
    const res = await apiClient.get(`/form-intake/projects/${projectId}/style-default`);
    return res.data;
  },
  saveProjectStyleDefault: async (projectId: string, style: FormIntakeStyleDef | null): Promise<{ style: FormIntakeStyleDef }> => {
    const res = await apiClient.put(`/form-intake/projects/${projectId}/style-default`, { style });
    return res.data;
  },
  publish: async (id: string): Promise<{ status: string }> => {
    const res = await apiClient.post(`/form-intake/forms/${id}/publish`);
    return res.data;
  },
  unpublish: async (id: string): Promise<{ status: string }> => {
    const res = await apiClient.post(`/form-intake/forms/${id}/unpublish`);
    return res.data;
  },
  generateAndPublish: async (formId: string): Promise<{ status: string; form_id: string; auto_generate?: boolean }> => {
    const res = await apiClient.post(`/form-intake/forms/${formId}/generate-and-publish`);
    return res.data;
  },
  // Read-only per-module `_c` field diff that Publish would create — the pre-publish preview.
  // 400 if no CRM parent module mapped; 409 if remote provisioning not configured/enabled;
  // 502 if the customer's companion endpoint is unreachable.
  getFieldDiff: async (formId: string): Promise<FormIntakeFieldDiff> => {
    const res = await apiClient.get(`/form-intake/forms/${formId}/field-diff`);
    return res.data;
  },
  // Whether the owning company's CRM has the provisioning companion installed + enabled.
  getProvisioningStatus: async (formId: string): Promise<FormIntakeProvisioningStatus> => {
    const res = await apiClient.get(`/form-intake/forms/${formId}/provisioning-status`);
    return res.data;
  },
  listInvites: async (formId: string): Promise<FormIntakeInvite[]> => {
    const res = await apiClient.get(`/form-intake/forms/${formId}/invites`);
    return res.data;
  },
  createInvite: async (formId: string, body: { client_email?: string; client_label?: string; expires_in_days?: number; access_pin?: string }): Promise<FormIntakeInvite> => {
    const res = await apiClient.post(`/form-intake/forms/${formId}/invites`, body);
    return res.data;
  },
  listSubmissions: async (formId: string): Promise<FormIntakeSubmission[]> => {
    const res = await apiClient.get(`/form-intake/forms/${formId}/submissions`);
    return res.data;
  },
  resyncSubmission: async (formId: string, sid: string): Promise<any> => {
    const res = await apiClient.post(`/form-intake/forms/${formId}/submissions/${sid}/resync`);
    return res.data;
  },
  updateSettings: async (formId: string, settings: { merge_repeat_submissions?: boolean }): Promise<FormIntakeForm> => {
    const res = await apiClient.put(`/form-intake/forms/${formId}/settings`, settings);
    return res.data;
  },
  enablePublicLink: async (formId: string, leadSource?: string): Promise<FormIntakeForm> => {
    const res = await apiClient.post(`/form-intake/forms/${formId}/public-link`, { lead_source: leadSource });
    return res.data;
  },
  disablePublicLink: async (formId: string): Promise<FormIntakeForm> => {
    const res = await apiClient.delete(`/form-intake/forms/${formId}/public-link`);
    return res.data;
  },
  deleteForm: async (id: string): Promise<{ deleted: boolean; form_id: string; invites_invalidated: number }> => {
    const res = await apiClient.delete(`/form-intake/forms/${id}`);
    return res.data;
  },
  staffFill: async (formId: string, data: Record<string, any>): Promise<any> => {
    const res = await apiClient.post(`/form-intake/forms/${formId}/fill`, { data });
    return res.data;
  },
  // Public endpoints (no auth)
  publicGetForm: async (token: string): Promise<{
    name: string;
    description?: string;
    schema: FormIntakeSchemaDef;
    requires_pin: boolean;
    already_submitted: boolean;
    open: boolean;
    style?: FormIntakeStyleDef | null;
    has_draft?: boolean;
    draft_updated_at?: string | null;
  }> => {
    const res = await apiClient.get(`/form-intake/public/fill/${token}`);
    return res.data;
  },
  publicSubmit: async (token: string, body: { data: Record<string, any>; access_pin?: string }): Promise<{ message: string }> => {
    const res = await apiClient.post(`/form-intake/public/fill/${token}/submit`, body);
    return res.data;
  },
  // Server-side PIN-gated read of a saved draft. Phase 0A: PIN gate is live; draft_data/
  // draft_updated_at/page_index are always null until Phase 4 adds the backing columns.
  publicResume: async (token: string, body: { access_pin?: string }): Promise<{
    draft_data: Record<string, any> | null;
    draft_updated_at: string | null;
    page_index: number | null;
  }> => {
    const res = await apiClient.post(`/form-intake/public/fill/${token}/resume`, body);
    return res.data;
  },
  // Save partial progress. Invite-based links persist server-side; anonymous/open tokens without
  // an email return {local_only: true} (200, not an error) — the client's own localStorage copy
  // is the only persistence in that case.
  publicSaveDraft: async (token: string, body: {
    data: Record<string, any>;
    access_pin?: string;
    email?: string;
    page_index?: number;
  }): Promise<{
    saved: boolean;
    local_only: boolean;
    promoted: boolean;
    draft_updated_at: string | null;
    resume_path: string | null;
    email_sent: boolean | null;
  }> => {
    const res = await apiClient.put(`/form-intake/public/fill/${token}/draft`, body);
    return res.data;
  },
};

// ─── Booking Public API ──────────────────────────────────────────────────────

export interface BookingType {
  booking_type_id: string;
  name: string;
  description?: string;
  duration_minutes: number;
  location?: string;
  timezone: string;
  resource_name?: string;
  require_phone?: boolean;
  branding?: BookingBranding | null;
}

export interface BookingSlot {
  start_utc: string;
  start_local: string;
}

export interface BookingSlotsResponse {
  timezone: string;
  slots: BookingSlot[];
}

export interface BookPayload {
  start_utc: string;
  attendee_name?: string;
  attendee_email?: string;
  attendee_phone?: string;
  form_submission_id?: string;
  form_id?: string;
}

export interface BookResponse {
  ok: boolean;
  appointment_id: string;
  manage_token: string;
  start_utc: string;
  start_local: string;
  timezone: string;
  location?: string;
  message?: string;
}

export interface ManageAppointment {
  appointment_id: string;
  status: string;
  name?: string;
  location?: string;
  start_utc: string;
  start_local: string;
  timezone: string;
  branding?: BookingBranding | null;
  resource_name?: string | null;
}

export interface DomainBookingType {
  booking_type_id: string;
  name: string;
  description?: string | null;
  duration_minutes: number;
  location?: string | null;
  resource_name?: string | null;
  timezone: string;
}

export interface DomainBookingTypesResponse {
  domain: string;
  branding: BookingBranding | null;
  types: DomainBookingType[];
}

export const bookingPublicApi = {
  getType: async (typeId: string): Promise<BookingType> => {
    const res = await apiClient.get(`/booking/public/types/${typeId}`);
    return res.data;
  },
  getDomainTypes: async (): Promise<DomainBookingTypesResponse> => {
    const res = await apiClient.get(`/booking/public/domain-types`);
    return res.data;
  },
  getSlots: async (typeId: string): Promise<BookingSlotsResponse> => {
    const res = await apiClient.get(`/booking/public/types/${typeId}/slots`);
    return res.data;
  },
  book: async (typeId: string, payload: BookPayload): Promise<BookResponse> => {
    const res = await apiClient.post(`/booking/public/types/${typeId}/book`, payload);
    return res.data;
  },
  getManage: async (token: string): Promise<ManageAppointment> => {
    const res = await apiClient.get(`/booking/public/manage/${token}`);
    return res.data;
  },
  cancel: async (token: string): Promise<{ ok: boolean; status: string }> => {
    const res = await apiClient.post(`/booking/public/manage/${token}/cancel`);
    return res.data;
  },
};

// ─── Booking Management API (authed, enterprise-gated form_intake) ────────────

export interface BookingAvailabilityRow {
  day_of_week: number; // 0=Mon … 6=Sun
  start_time: string;  // "HH:MM"
  end_time: string;    // "HH:MM"
}

export interface AssignableUser {
  user_id: string;
  full_name: string;
  email: string;
}

export interface BookingResourceItem {
  resource_id: string;
  name: string;
  email?: string | null;
  timezone: string;
  crm_user_id?: string | null;
  blackout_dates?: string[];
  active?: boolean;
  availability?: BookingAvailabilityRow[];
  // Calendar sync fields (CS-1 two-way calendar sync)
  calendar_provider?: 'ms' | 'google' | null;
  calendar_id?: string | null;
  user_id?: string | null;
  assigned_user?: { user_id: string; full_name: string; email: string } | null;
  calendar_sync_status?: 'none' | 'not_connected' | 'needs_write_scope' | 'connected';
  calendar_self_connected?: boolean;
  has_availability_link?: boolean;
  calendar_connection_source?: 'resource' | 'user' | null;
  connect_link_pending?: boolean;
  project_id?: string | null;
  project_name?: string | null;
}

export interface BookingTypeItem {
  booking_type_id: string;
  resource_id: string;
  name: string;
  description?: string | null;
  duration_minutes: number;
  buffer_minutes: number;
  min_notice_hours: number;
  max_advance_days: number;
  location?: string | null;
  active?: boolean;
  require_phone?: boolean;
}

export interface Appointment {
  appointment_id: string;
  status: string;
  start_utc: string;
  end_utc: string;
  attendee_name?: string | null;
  attendee_email?: string | null;
  attendee_phone?: string | null;
  booking_type_name?: string | null;
  resource_name?: string | null;
  timezone?: string | null;
  location?: string | null;
  crm_meeting_id?: string | null;
  crm_sync_error?: string | null;
  form_id?: string | null;
}

export const bookingApi = {
  // Resources
  listResources: async (projectId?: string | null): Promise<BookingResourceItem[]> => {
    const res = await apiClient.get('/booking/resources', {
      params: projectId ? { project_id: projectId } : undefined,
    });
    return res.data;
  },
  listAssignableUsers: async (): Promise<AssignableUser[]> => {
    const res = await apiClient.get('/booking/assignable-users');
    return res.data;
  },
  createResource: async (body: {
    name: string;
    email?: string;
    timezone?: string;
    crm_user_id?: string;
    blackout_dates?: string[];
    active?: boolean;
    calendar_provider?: 'ms' | 'google' | null;
    calendar_id?: string | null;
    user_id?: string;
    project_id?: string | null;
  }): Promise<BookingResourceItem> => {
    const res = await apiClient.post('/booking/resources', body);
    return res.data;
  },
  updateResource: async (id: string, body: {
    name?: string;
    email?: string;
    timezone?: string;
    crm_user_id?: string;
    blackout_dates?: string[];
    active?: boolean;
    calendar_provider?: 'ms' | 'google' | null;
    calendar_id?: string | null;
    user_id?: string;
    project_id?: string | null;
  }): Promise<BookingResourceItem> => {
    const res = await apiClient.put(`/booking/resources/${id}`, body);
    return res.data;
  },
  deleteResource: async (id: string): Promise<void> => {
    await apiClient.delete(`/booking/resources/${id}`);
  },
  setAvailability: async (id: string, rows: BookingAvailabilityRow[]): Promise<BookingResourceItem> => {
    const res = await apiClient.put(`/booking/resources/${id}/availability`, rows);
    return res.data;
  },

  // Seatless calendar connect (admin)
  sendCalendarInvite: async (id: string): Promise<{ connect_path: string; expires_at: string; email_sent: boolean }> => {
    const res = await apiClient.post(`/booking/resources/${id}/calendar-invite`);
    return res.data;
  },
  revokeCalendar: async (id: string): Promise<{ revoked: boolean; resource_id: string; calendar_sync_status: string }> => {
    const res = await apiClient.post(`/booking/resources/${id}/calendar-revoke`);
    return res.data;
  },
  regenerateAvailabilityLink: async (id: string): Promise<{ availability_path: string; email_sent: boolean }> => {
    const res = await apiClient.post(`/booking/resources/${id}/availability-link`);
    return res.data;
  },

  // Seatless calendar connect (public, no auth)
  publicConnectInfo: async (token: string): Promise<{ resource_name: string; providers: string[]; connected: boolean }> => {
    const res = await apiClient.get(`/booking/public/connect/${token}`);
    return res.data;
  },
  publicConnectStart: async (token: string, provider: 'ms' | 'google'): Promise<{ auth_url: string }> => {
    const res = await apiClient.post(`/booking/public/connect/${token}/start`, { provider });
    return res.data;
  },
  publicAvailabilityGet: async (token: string): Promise<{
    resource_name: string;
    timezone: string;
    availability: { day_of_week: number; start_time: string; end_time: string }[];
    blackout_dates: string[];
  }> => {
    const res = await apiClient.get(`/booking/public/availability/${token}`);
    return res.data;
  },
  publicAvailabilitySet: async (
    token: string,
    body: {
      availability: { day_of_week: number; start_time: string; end_time: string }[];
      blackout_dates: string[];
    },
  ): Promise<{
    resource_name: string;
    timezone: string;
    availability: { day_of_week: number; start_time: string; end_time: string }[];
    blackout_dates: string[];
  }> => {
    const res = await apiClient.put(`/booking/public/availability/${token}`, body);
    return res.data;
  },

  // Booking Types
  listTypes: async (): Promise<BookingTypeItem[]> => {
    const res = await apiClient.get('/booking/types');
    return res.data;
  },
  createType: async (body: {
    resource_id: string;
    name: string;
    description?: string;
    duration_minutes: number;
    buffer_minutes: number;
    min_notice_hours: number;
    max_advance_days: number;
    location?: string;
    active?: boolean;
  }): Promise<BookingTypeItem> => {
    const res = await apiClient.post('/booking/types', body);
    return res.data;
  },
  updateType: async (id: string, body: {
    resource_id?: string;
    name?: string;
    description?: string;
    duration_minutes?: number;
    buffer_minutes?: number;
    min_notice_hours?: number;
    max_advance_days?: number;
    location?: string;
    active?: boolean;
  }): Promise<BookingTypeItem> => {
    const res = await apiClient.put(`/booking/types/${id}`, body);
    return res.data;
  },
  deleteType: async (id: string): Promise<void> => {
    await apiClient.delete(`/booking/types/${id}`);
  },

  // Appointments
  listAppointments: async (): Promise<Appointment[]> => {
    const res = await apiClient.get('/booking/appointments');
    return res.data;
  },
  updateAppointmentStatus: async (id: string, status: 'cancelled' | 'completed' | 'no_show' | 'booked'): Promise<void> => {
    await apiClient.post(`/booking/appointments/${id}/status`, { status });
  },

  // Project-level booking-page branding preset (mirrors formIntakeApi.getStyleDefaults /
  // saveProjectStyleDefault). `logo_options` — extra vs. FormIntake's shape — lists the
  // project's default BrandGuide's available logo variants for the modal's logo picker.
  getBrandingDefaults: async (projectId: string): Promise<{
    brand: BookingBranding | null;
    saved_default: BookingBranding | null;
    resolved: BookingBranding | null;
    logo_options: { key: string; label: string; url: string }[];
  }> => {
    const res = await apiClient.get(`/booking/projects/${projectId}/branding-defaults`);
    return res.data;
  },
  saveBrandingDefault: async (projectId: string, style: BookingBranding | null): Promise<{ style: BookingBranding }> => {
    const res = await apiClient.put(`/booking/projects/${projectId}/branding-default`, { style });
    return res.data;
  },
};

// ==================== Agent API Tokens (PATs) ====================
// Inbound Personal Access Tokens for external agentic harnesses
// (specs/completed/agent-api-access.md). Feature ships dark: endpoints 404
// until AGENT_API_ENABLED is set server-side.

export interface AgentTokenMetadata {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string | null;
}

export interface AgentTokenCreateResponse extends AgentTokenMetadata {
  token: string; // plaintext — returned ONLY on create; never retrievable again
}

export const agentTokensApi = {
  list: async (): Promise<AgentTokenMetadata[]> => {
    const res = await apiClient.get('/agent-tokens');
    return res.data.tokens;
  },
  create: async (body: {
    name: string;
    scopes: string[];
    expires_at?: string;
  }): Promise<AgentTokenCreateResponse> => {
    const res = await apiClient.post('/agent-tokens', body);
    return res.data;
  },
  revoke: async (id: string): Promise<void> => {
    await apiClient.delete(`/agent-tokens/${id}`);
  },
};

// ── Project Voice & Tone Rules (project-voice-tone-rules, Phase 9) ────────
// Shapes mirror the ACTUAL backend responses in voice_rule_routes.py /
// models.py (VoiceRuleOut / VoiceRuleCreate / VoiceRuleUpdate / VoiceRuleOrder),
// not the spec doc's earlier envelope draft — list/reorder return bare
// arrays, delete returns {success, rule_id}, suggest returns
// {candidates: [{rule_text}]} only (no rationale/grounded_on/disclaimer —
// voice_rule_suggester._assemble_candidates only ever emits rule_text).

export interface VoiceRule {
    rule_id: string;
    project_id: string;
    rule_text: string;
    scope: string[];
    active: boolean;
    sort_order: number;
    source: 'manual' | 'ai_suggested';
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

export interface VoiceModuleOption {
    key: string;
    label: string;
}

export interface VoiceRuleCandidate {
    rule_text: string;
}

export const listVoiceRuleModules = async (): Promise<{ modules: VoiceModuleOption[] }> => {
    const response = await apiClient.get('/voice-rules/modules');
    return response.data;
};

export const listVoiceRules = async (projectId: string): Promise<VoiceRule[]> => {
    const response = await apiClient.get(`/projects/${projectId}/voice-rules`);
    return response.data;
};

export const createVoiceRule = async (
    projectId: string,
    body: { rule_text: string; scope: string[]; active?: boolean; source?: 'manual' | 'ai_suggested' },
): Promise<VoiceRule> => {
    const response = await apiClient.post(`/projects/${projectId}/voice-rules`, body);
    return response.data;
};

export const updateVoiceRule = async (
    projectId: string,
    ruleId: string,
    body: Partial<{ rule_text: string; scope: string[]; active: boolean; sort_order: number }>,
): Promise<VoiceRule> => {
    const response = await apiClient.patch(`/projects/${projectId}/voice-rules/${ruleId}`, body);
    return response.data;
};

export const deleteVoiceRule = async (
    projectId: string,
    ruleId: string,
): Promise<{ success: boolean; rule_id: string }> => {
    const response = await apiClient.delete(`/projects/${projectId}/voice-rules/${ruleId}`);
    return response.data;
};

export const reorderVoiceRules = async (
    projectId: string,
    ruleIds: string[],
): Promise<VoiceRule[]> => {
    const response = await apiClient.put(`/projects/${projectId}/voice-rules/order`, { rule_ids: ruleIds });
    return response.data;
};

export const suggestVoiceRules = async (
    projectId: string,
    body: { brand_guide_id?: string; focus?: string },
): Promise<{ candidates: VoiceRuleCandidate[] }> => {
    const response = await apiClient.post(`/projects/${projectId}/voice-rules/suggest`, body);
    return response.data;
};


// ── crm-per-project-scoping (specs/completed/crm-per-project-scoping.md) ─────

/** Values of the X-AIME-CRM-Status header returned by GET /crm/_sso on refusal. */
export type CrmSsoStatus =
    | 'no-credentials'
    | 'no-module'
    | 'project-required'
    | 'no-project-access'
    | 'project-not-ready'
    | 'project-company-crm';

export type CrmAccessKind = 'rw' | 'ro';

export interface CrmProjectEntry {
    project_id: string;
    name: string;
    crm_access: CrmAccessKind | null;
    crm_ready: boolean;
    reason: string | null;
    is_owner: boolean;
    is_archived?: boolean;
}

export interface CrmProjectsResponse {
    scoping: boolean;
    support_view: boolean;
    projects: CrmProjectEntry[];
    unassigned: { available: boolean; count_hint: number | null };
}

export interface CrmSessionContext {
    project_id: string | null;
    bucket: 'project' | 'unassigned' | null;
    identity_kind: CrmAccessKind | null;
}

export type CrmUnassignedModule = 'Leads' | 'Contacts' | 'Accounts';

export interface CrmUnassignedRecord {
    id: string;
    name: string;
    email1: string;
    account_name: string;
    date_entered: string;
    module: CrmUnassignedModule;
}

export interface CrmUnassignedResponse {
    records: CrmUnassignedRecord[];
    module: CrmUnassignedModule;
    page: number;
    per_page: number;
    crm_scope: string;
    notice?: string;
}

export interface CrmAssignResult {
    moved: number;
    rejected: number;
    failed: number;
    batch_id: string | null;
    /** `all:true` moves at most 200 records per request; true when more remain. */
    truncated?: boolean;
}

export type CrmAssignRequest =
    | { project_id: string; records: Array<{ module: CrmUnassignedModule; id: string }> }
    | { project_id: string; all: true; module: CrmUnassignedModule };

export interface ProjectCrmIdentity {
    user_id: string;
    email: string | null;
    full_name: string | null;
    access_kind: CrmAccessKind | null;
    granted_via: 'owner' | 'project_member' | 'team';
    team_name: string | null;
    status: string;
    status_reason: string | null;
    granted_at: string | null;
    last_error_short: string | null;
}

export interface ProjectCrmAccess {
    project_group: { status: string; origin: string } | null;
    identities: ProjectCrmIdentity[];
    scoping?: boolean;
}

export const getCrmProjects = async (): Promise<CrmProjectsResponse> => {
    const response = await apiClient.get('/crm/projects');
    return response.data;
};

/**
 * Projects the caller can WRITE CRM records into, for import / sheet-source pickers.
 * With project scoping on, a read-only (`ro`) or unready project 403s at confirm, so it
 * is not offered. With scoping off (or if the lookup fails) the list is returned
 * unfiltered, exactly as before.
 */
export const filterCrmWritableProjects = (list: Project[], info: CrmProjectsResponse | null): Project[] => {
    if (!info || !info.scoping) return list;
    const writable = new Set(info.projects.filter(p => p.crm_access === 'rw').map(p => p.project_id));
    return list.filter(p => writable.has(p.project_id));
};

export const getCrmWritableProjects = async (): Promise<Project[]> => {
    const [list, info] = await Promise.all([
        getProjects(false),
        getCrmProjects().catch(() => null),
    ]);
    return filterCrmWritableProjects(list, info);
};

export const getCrmSessionContext = async (): Promise<CrmSessionContext> => {
    const response = await apiClient.get('/crm/session-context');
    return response.data;
};

export const getCrmUnassigned = async (
    module: CrmUnassignedModule, page: number = 1, perPage: number = 50,
): Promise<CrmUnassignedResponse> => {
    const response = await apiClient.get('/crm/unassigned', { params: { module, page, per_page: perPage } });
    return response.data;
};

export const assignCrmUnassigned = async (body: CrmAssignRequest): Promise<CrmAssignResult> => {
    const response = await apiClient.post('/crm/unassigned/assign', body);
    return response.data;
};

export const undoCrmUnassigned = async (batchId: string): Promise<{ reverted: number; failed: number }> => {
    const response = await apiClient.post('/crm/unassigned/undo', { batch_id: batchId });
    return response.data;
};

export const getProjectCrmAccess = async (projectId: string): Promise<ProjectCrmAccess> => {
    const response = await apiClient.get(`/projects/${projectId}/crm-access`);
    return response.data;
};

export const resyncProjectCrmAccess = async (projectId: string): Promise<{ queued: boolean }> => {
    const response = await apiClient.post(`/projects/${projectId}/crm-access/resync`);
    return response.data;
};
